import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import url from '../../config.js';
import whiteLogo from '../../assets/icons/white_logo_png.png';
import SharedLogsPanel from './SharedLogsPanel';
import StaleBanner from '../../components/stale-session-banner';

const domain     = url.domain;
const backendUrl = url.backendUrl; // port 8083 — where brownfield analyze/answer routes live

const SEV_COLOR = { error: '#ff4f4f', warning: '#f5a623', pass: '#00c98d' };
const SEV_ICON  = { error: '❌', warning: '⚠️', pass: '✅' };
const scoreColor = s => s >= 80 ? '#00c98d' : s >= 50 ? '#f5a623' : '#ff4f4f';

// Render bot message text with **bold** and split option lines (A)/(B)/(C) onto new rows
function formatBotMessage(content) {
  if (!content || typeof content !== 'string') return content;
  const toBold = (text) => {
    const segs = text.split(/\*\*([^*]+)\*\*/g);
    return segs.map((s, i) => i % 2 === 1 ? <strong key={i} style={{ color: '#dce8ff' }}>{s}</strong> : s);
  };
  // Split before each option marker so each gets its own line
  const parts = content.split(/(?=\*\*\([A-Z]\))/);
  if (parts.length === 1) return <span>{toBold(content)}</span>;
  return (
    <>
      {parts.map((part, i) => (
        <div key={i} style={i > 0 ? { marginTop: 8, paddingLeft: 4, borderLeft: '2px solid rgba(59,127,255,0.3)' } : { marginBottom: 10 }}>
          {toBold(part.trim())}
        </div>
      ))}
    </>
  );
}

function getUserRole() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.platformRole || u.role || 'viewer';
  } catch { return 'viewer'; }
}

const USE_MOCK_BF = false; // set true to load dummy data for UI testing

const MOCK_BF_RESULTS = {
  scanned: 7, no_code: 0, total: 7, avg_score: 31, detected_language: 'mulesoft',
  results: [
    {
      flow_name: 'account-enriches',
      file_path: 'src/main/mule/account-enriches.xml',
      status: 'scanned',
      score: 23,
      sha: '264c21a2cc216678c7464e655c33a35ca6317a58',
      summary: '17 errors and 5 warnings found. Critical violations include: missing HTTP listeners on health check flows, main flow too long.',
      violations: [
        { severity: 'error',   rule: 'File naming convention',                        detail: "File is named 'account-enriches' but should follow camelCase naming." },
        { severity: 'error',   rule: 'Separation of concerns - global config in wrong file', detail: "The Salesforce config 'fetchA' should live in a global config file." },
        { severity: 'error',   rule: 'Global element naming convention (camelCase)',  detail: "Salesforce config name does not start with a lowercase letter." },
        { severity: 'error',   rule: 'Property file environment variable naming',     detail: "Configuration properties uses '${env}' but should use '${mule.env}'." },
        { severity: 'error',   rule: 'Main flow should not be lengthy; use flow references', detail: "The POST /account-enrich flow has too many steps inline." },
        { severity: 'error',   rule: 'Maximum 8 distinct connectors in main flow',   detail: "Counting distinct connector types/instances exceeds the limit." },
        { severity: 'error',   rule: 'Use application/json instead of application/java for internal payloads', detail: "Multiple ee:transform components output application/java." },
        { severity: 'error',   rule: 'Do not hard-code values in flows',              detail: "HTTP status codes (200, 404, 500) are hard-coded in set-payload." },
        { severity: 'error',   rule: 'Liveliness and readiness flows missing HTTP listener', detail: "The 'liveliness-check' and 'readiness-check' flows have no HTTP listener." },
        { severity: 'error',   rule: 'Flow listener rename convention: <METHOD> <endpoint>', detail: "The health check flows 'livel…' don't follow the naming convention." },
        { severity: 'error',   rule: 'Error handler must distinguish error types (4xx vs 5xx)', detail: "The try error-handler uses on-error-propagate without type filter." },
        { severity: 'error',   rule: 'Flow being referred by api.xml must be appended with \' - main flow\'', detail: "The flow 'POST /account-enrich' needs ' - main flow' suffix." },
        { severity: 'error',   rule: 'Reuse components — success payload hardcoded inline', detail: "The success response payload is duplicated in multiple set-payload steps." },
        { severity: 'error',   rule: 'Minimize variables — group flow variables inside a single variable', detail: "Multiple individual flow variables should be combined." },
        { severity: 'error',   rule: 'Common logging from exchange not added',        detail: "The standard services adding common logging module is absent." },
        { severity: 'warning', rule: 'Correlation ID propagation',                   detail: "Correlation ID not explicitly set at entry point." },
        { severity: 'warning', rule: 'Response timeout not set',                     detail: "HTTP request timeout not configured — recommend 30000ms." },
        { severity: 'warning', rule: 'Logger component verbosity',                   detail: "Some logger components log the full payload; consider logging summaries." },
        { severity: 'warning', rule: 'Secure properties placeholder',                detail: "One credential value may not be using the secure properties placeholder." },
        { severity: 'warning', rule: 'API kit router present',                       detail: "Missing explicit API kit router — verify api.xml is correctly linked." },
        { severity: 'pass',    rule: 'HTTPS listener',                               detail: "All HTTP listeners reference a shared config." },
        { severity: 'pass',    rule: 'Secure config file present',                   detail: "A .yaml secure properties file is present in resources." },
      ],
      fixed_code: `<?xml version="1.0" encoding="UTF-8"?>\n<mule xmlns="http://www.mulesoft.org/schema/mule/core">\n  <!-- fixed -->\n</mule>`,
      diff: [
        '--- a/account-enriches.xml',
        '+++ b/accountEnriches.xml',
        '@@ -1,4 +1,4 @@',
        '-<flow name="account-enriches">',
        '+<flow name="accountEnriches - main flow">',
        '   <http:listener config-ref="HTTP_Listener_Config" path="/api/account-enrich"/>',
        '+  <set-variable variableName="correlationId" value="#[correlationId]"/>',
        '   <flow-ref name="accountEnrich-subflow"/>',
      ],
    },
    {
      flow_name: 'salesforce-upsert',
      file_path: 'src/main/mule/salesforce-upsert.xml',
      status: 'scanned',
      score: 45,
      sha: 'b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0',
      summary: '9 errors found. Missing error handler, DLQ not configured, hard-coded credentials.',
      violations: [
        { severity: 'error',   rule: 'Global Error Handler',           detail: 'Flow must reference a global error handler.' },
        { severity: 'error',   rule: 'Dead Letter Queue',              detail: 'Async Salesforce flows must configure a DLQ for unprocessable messages.' },
        { severity: 'error',   rule: 'Do not hard-code values in flows', detail: "Salesforce instance URL is hard-coded — move to secure properties." },
        { severity: 'error',   rule: 'Separation of concerns',         detail: 'Salesforce connector config should live in a global config file.' },
        { severity: 'error',   rule: 'Use application/json payloads',  detail: 'Transform outputs application/java — use application/json.' },
        { severity: 'warning', rule: 'Correlation ID',                 detail: 'Correlation ID not set at entry point.' },
        { severity: 'warning', rule: 'Logger Component',               detail: 'Use the common logging framework, not bare logger.' },
        { severity: 'pass',    rule: 'Secure Properties',              detail: 'Secure properties placeholder present.' },
        { severity: 'pass',    rule: 'HTTP Listener Config',           detail: 'HTTP listener references a shared config.' },
      ],
      fixed_code: `<?xml version="1.0" encoding="UTF-8"?>\n<mule xmlns="http://www.mulesoft.org/schema/mule/core">\n  <flow name="salesforceUpsert - main flow">\n    <http:listener config-ref="HTTP_Listener_Config" path="/api/sf-upsert"/>\n    <set-variable variableName="correlationId" value="#[correlationId]"/>\n    <flow-ref name="salesforceUpsert-subflow"/>\n    <error-handler ref="Global_Error_Handler"/>\n  </flow>\n</mule>`,
      diff: [
        '--- a/salesforce-upsert.xml',
        '+++ b/salesforceUpsert.xml',
        '@@ -1,4 +1,5 @@',
        '-<flow name="salesforce-upsert">',
        '+<flow name="salesforceUpsert - main flow">',
        '+  <set-variable variableName="correlationId" value="#[correlationId]"/>',
        '   <flow-ref name="salesforceUpsert-subflow"/>',
        '+  <error-handler ref="Global_Error_Handler"/>',
      ],
    },
    {
      flow_name: 'order-api-main',
      file_path: 'src/main/mule/order-api.xml',
      status: 'scanned',
      score: 78,
      sha: 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0',
      summary: 'Minor warnings only — core patterns compliant.',
      violations: [
        { severity: 'pass',    rule: 'Global Error Handler', detail: 'Global error handler correctly configured.' },
        { severity: 'pass',    rule: 'HTTP Listener Config', detail: 'HTTP listener references a shared config.' },
        { severity: 'pass',    rule: 'Secure Properties',    detail: 'Secure properties placeholder in use.' },
        { severity: 'warning', rule: 'Response Timeout',     detail: 'HTTP request timeout not explicitly set — recommend 30000ms.' },
        { severity: 'pass',    rule: 'Correlation ID',       detail: 'Correlation ID set at entry point.' },
        { severity: 'pass',    rule: 'API Kit Router',       detail: 'APIKit router present.' },
      ],
      fixed_code: `<?xml version="1.0" encoding="UTF-8"?>\n<mule xmlns="http://www.mulesoft.org/schema/mule/core">\n  <flow name="order-api-main">\n    <http:listener config-ref="HTTP_Listener_Config" path="/api/orders/*" responseTimeout="30000"/>\n    <apikit:router config-ref="order-api-config"/>\n    <error-handler ref="Global_Error_Handler"/>\n  </flow>\n</mule>`,
      diff: [
        '--- a/order-api.xml',
        '+++ b/order-api.xml',
        '@@ -2,4 +2,4 @@',
        '   <http:listener config-ref="HTTP_Listener_Config" path="/api/orders/*"',
        '-              />',
        '+              responseTimeout="30000"/>',
      ],
    },
    {
      flow_name: 'inventory-sync-scheduler',
      file_path: 'src/main/mule/inventory-sync-scheduler.xml',
      status: 'scanned',
      score: 100,
      sha: 'c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1',
      summary: 'Fully compliant — no violations found.',
      violations: [
        { severity: 'pass', rule: 'Global Error Handler', detail: 'Global error handler correctly configured.' },
        { severity: 'pass', rule: 'Scheduler Config',     detail: 'Scheduler uses externalised cron expression.' },
        { severity: 'pass', rule: 'Secure Properties',    detail: 'Secure properties placeholder in use.' },
        { severity: 'pass', rule: 'Logger Component',     detail: 'Common logging framework in use.' },
        { severity: 'pass', rule: 'Correlation ID',       detail: 'Correlation ID generated at scheduler entry.' },
      ],
      fixed_code: null,
      diff: [],
    },
    {
      flow_name: 'customer-notification',
      file_path: 'src/main/mule/customer-notification.xml',
      status: 'scanned',
      score: 52,
      sha: 'd3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2',
      summary: 'Missing DLQ and logger standards need addressing.',
      violations: [
        { severity: 'error',   rule: 'Dead Letter Queue',    detail: 'Async messaging flows must configure a DLQ.' },
        { severity: 'warning', rule: 'Logger Component',     detail: 'Use the common logging framework instead of bare logger.' },
        { severity: 'pass',    rule: 'Global Error Handler', detail: 'Global error handler correctly configured.' },
        { severity: 'pass',    rule: 'Secure Properties',    detail: 'Secure properties placeholder in use.' },
      ],
      fixed_code: `<?xml version="1.0" encoding="UTF-8"?>\n<mule xmlns="http://www.mulesoft.org/schema/mule/core">\n  <flow name="customerNotification - main flow">\n    <anypoint-mq:subscriber config-ref="MQ_Config" destination="customer-notifications"/>\n    <flow-ref name="sendNotification-subflow"/>\n    <anypoint-mq:ack/>\n    <error-handler ref="Global_Error_Handler"><on-error-propagate><anypoint-mq:nack/></on-error-propagate></error-handler>\n  </flow>\n</mule>`,
      diff: [
        '--- a/customer-notification.xml',
        '+++ b/customerNotification.xml',
        '@@ -4,4 +4,8 @@',
        '   <flow-ref name="sendNotification-subflow"/>',
        '+  <anypoint-mq:ack/>',
        '   <error-handler ref="Global_Error_Handler">',
        '+    <on-error-propagate><anypoint-mq:nack/></on-error-propagate>',
        '   </error-handler>',
      ],
    },
    {
      flow_name: 'analytics-events',
      file_path: 'src/main/mule/analytics-events.xml',
      status: 'scanned',
      score: 68,
      sha: 'e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3',
      summary: 'Mostly compliant — retry policy and timeout missing.',
      violations: [
        { severity: 'pass',    rule: 'Global Error Handler', detail: 'Global error handler correctly configured.' },
        { severity: 'pass',    rule: 'Secure Properties',    detail: 'Secure properties placeholder in use.' },
        { severity: 'warning', rule: 'Retry Policy',         detail: 'HTTP outbound calls should include a retry policy for transient failures.' },
        { severity: 'warning', rule: 'Response Timeout',     detail: 'HTTP request timeout not explicitly set.' },
        { severity: 'pass',    rule: 'Correlation ID',       detail: 'Correlation ID propagated correctly.' },
      ],
      fixed_code: `<?xml version="1.0" encoding="UTF-8"?>\n<mule xmlns="http://www.mulesoft.org/schema/mule/core">\n  <flow name="analyticsEvents - main flow">\n    <http:request config-ref="Analytics_Config" method="POST" path="/events" responseTimeout="30000">\n      <reconnect count="3" frequency="2000"/>\n    </http:request>\n    <error-handler ref="Global_Error_Handler"/>\n  </flow>\n</mule>`,
      diff: [
        '--- a/analytics-events.xml',
        '+++ b/analyticsEvents.xml',
        '@@ -3,3 +3,5 @@',
        '   <http:request config-ref="Analytics_Config" method="POST" path="/events"',
        '+             responseTimeout="30000">',
        '+    <reconnect count="3" frequency="2000"/>',
        '   </http:request>',
      ],
    },
    {
      flow_name: 'legacy-batch-processor',
      file_path: null,
      status: 'no_code',
      score: 0,
      sha: null,
      summary: null,
      violations: [],
      fixed_code: null,
      diff: [],
    },
  ],
};

function getBfStorageKey() {
  try {
    const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id || 'default';
    return `bf_state_${uid}`;
  } catch { return 'bf_state_default'; }
}

// "A scan is in flight" marker — persisted so a running scan can be resumed after
// sleep / disconnect / reload / remount (a React ref would be lost). Keyed by user.
function getBfScanKey() {
  try {
    const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id || 'default';
    return `bf_scan_${uid}`;
  } catch { return 'bf_scan_default'; }
}
function markScanRunning(sessionId) {
  try { localStorage.setItem(getBfScanKey(), JSON.stringify({ sessionId, ts: Date.now() })); } catch (_) {}
}
function readScanMark() {
  try { return JSON.parse(localStorage.getItem(getBfScanKey()) || 'null'); } catch { return null; }
}
function clearScanMark() {
  try { localStorage.removeItem(getBfScanKey()); } catch (_) {}
}

// ── Custom hook: owns all brownfield state + side-effects ──────────────────
export function useBrownfieldTab({ activeTab, sessionId }) {
  const BF_KEY = getBfStorageKey();
  const _p = (() => { try { return JSON.parse(localStorage.getItem(BF_KEY) || '{}'); } catch { return {}; } })();

  const [bfRepoAccounts,       setBfRepoAccounts]       = useState([]);
  const [bfConfluenceAccounts, setBfConfluenceAccounts] = useState([]);
  const [bfSelectedRepo,       setBfSelectedRepo]       = useState(null);
  const [bfSelectedConfluence, setBfSelectedConfluence] = useState(null);
  const [bfRepoUrl,            setBfRepoUrl]            = useState("");
  const [bfRepoOwner,          setBfRepoOwner]          = useState("");
  const [bfConfluenceSpaces,   setBfConfluenceSpaces]   = useState([]);
  const [bfSpaceKey,           setBfSpaceKey]           = useState("");
  const [bfLoadingSpaces,      setBfLoadingSpaces]      = useState(false);
  const [bfPages,              setBfPages]              = useState([]);
  const [bfLoadingPages,       setBfLoadingPages]       = useState(false);
  const [bfSelectedPages,      setBfSelectedPages]      = useState([]);
  const [bfRepoList,           setBfRepoList]           = useState([]);
  const [bfLoadingRepos,       setBfLoadingRepos]       = useState(false);
  const [bfBranch,             setBfBranch]             = useState('main');
  const [bfBranchList,         setBfBranchList]         = useState([]);
  const [bfLoadingBranches,    setBfLoadingBranches]    = useState(false);
  const [bfMessages,           setBfMessages]           = useState(_p.bfMessages      || []);
  const [bfLoading,            setBfLoading]            = useState(false);
  const [bfAnalysisDone,       setBfAnalysisDone]       = useState(_p.bfAnalysisDone  || false);
  const [bfChangedFiles,       setBfChangedFiles]       = useState(_p.bfChangedFiles  || []);
  const [bfResults,            setBfResults]            = useState(USE_MOCK_BF ? MOCK_BF_RESULTS : (_p.bfResults || null));
  const [bfLogs,               setBfLogs]               = useState(_p.bfLogs          || []);
  const [bfOrgStandards,       setBfOrgStandards]       = useState([]);

  const _BF_STALE_THRESHOLD_MS = 6 * 60 * 60 * 1000;
  const [showBfStaleBanner, setShowBfStaleBanner] = useState(Boolean(
    _p.lastActive &&
    (Date.now() - _p.lastActive > _BF_STALE_THRESHOLD_MS) &&
    _p.bfMessages &&
    _p.bfMessages.length > 0
  ));

  // Persist chat/analysis state so navigating away and back restores the session
  useEffect(() => {
    localStorage.setItem(getBfStorageKey(), JSON.stringify({
      bfMessages, bfLogs, bfResults, bfAnalysisDone, bfChangedFiles,
      lastActive: Date.now(),
    }));
  }, [bfMessages, bfLogs, bfResults, bfAnalysisDone, bfChangedFiles]);

  // Detect session drift: if the sessionId prop changes while there are active messages,
  // warn the user so they don't silently submit to the wrong backend session.
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    prevSessionIdRef.current = sessionId;
    if (prev && sessionId && prev !== sessionId) {
      const hasActiveConversation = bfMessages.some(function(m) { return m.role === "user"; });
      if (hasActiveConversation) {
        setBfMessages(function(msgs) {
          return [...msgs, {
            role: "bot",
            content: "⚠️ Your session context was reset while you were away. The AI no longer has context of this conversation. Start a new analysis to continue cleanly.",
            isNewSessionHint: true,
          }];
        });
      }
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBfAccounts = useCallback(() => {
    const headers = { 'x-access-token': localStorage.getItem('token') };
    fetch(`${domain}/api/v1/repo`, { headers })
      .then(r => r.json())
      .then(d => {
        console.log('[BF] /api/v1/repo ←', d);
        const list = Array.isArray(d.data || d) ? (d.data || d) : [];
        setBfRepoAccounts(list);
        if (list.length) setBfSelectedRepo(prev => prev ?? list[0]);
      })
      .catch(e => console.error('[BF] /api/v1/repo error:', e));
    fetch(`${domain}/api/v1/confluence`, { headers })
      .then(r => r.json())
      .then(d => {
        console.log('[BF] /api/v1/confluence ←', d);
        const list = Array.isArray(d) ? d : (d.data || []);
        setBfConfluenceAccounts(list);
        if (list.length) setBfSelectedConfluence(prev => prev ?? list[0]);
      })
      .catch(e => console.error('[BF] /api/v1/confluence error:', e));
    fetch(`${domain}/api/v1/standards`, { headers })
      .then(r => r.json())
      .then(d => { console.log('[BF] /api/v1/standards ←', d); setBfOrgStandards(Array.isArray(d) ? d : []); })
      .catch(e => console.error('[BF] /api/v1/standards error:', e));
  }, []);

  useEffect(() => {
    if (activeTab === "brownfield") loadBfAccounts();
  }, [activeTab]);

  useEffect(() => {
    if (!bfSelectedConfluence) return;
    setBfConfluenceSpaces([]);
    setBfSpaceKey("");
    setBfLoadingSpaces(true);
    fetch(`${domain}/api/v1/confluence/getConfluenceSpaces`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
      body: JSON.stringify({
        confluenceEmail:   bfSelectedConfluence.confluenceEmail,
        confluencePat:     bfSelectedConfluence.confluencePat,
        confluenceBaseUrl: bfSelectedConfluence.confluenceBaseUrl,
      }),
    })
      .then(r => r.json())
      .then(d => {
        console.log('[BF] /getConfluenceSpaces ←', d);
        const spaces = d.spaces || [];
        setBfConfluenceSpaces(spaces);
        if (spaces.length) setBfSpaceKey(spaces[0].key);
        setBfPages([]);
        setBfSelectedPages([]);
      })
      .catch(e => console.error('[BF] /getConfluenceSpaces error:', e))
      .finally(() => setBfLoadingSpaces(false));
  }, [bfSelectedConfluence]);

  useEffect(() => {
    if (!bfSelectedRepo) return;
    setBfRepoList([]);
    setBfRepoUrl("");
    setBfRepoOwner("");
    setBfBranchList([]);
    setBfBranch('main');
    setBfLoadingRepos(true);
    const params = new URLSearchParams({
      username: bfSelectedRepo.gitUsername,
      pat:      bfSelectedRepo.gitPat,
    });
    fetch(`${domain}/api/v1/github/repos?${params}`, {
      headers: { 'x-access-token': localStorage.getItem('token') },
    })
      .then(r => r.json())
      .then(data => {
        console.log('[BF] /github/repos ←', data);
        const list = Array.isArray(data) ? data : (data.repos || []);
        setBfRepoList(list);
        if (list.length) {
          setBfRepoUrl(list[0].name);
          setBfRepoOwner(list[0].fullName?.split('/')[0] || bfSelectedRepo.gitUsername);
        }
      })
      .catch(e => console.error('[BF] /github/repos error:', e))
      .finally(() => setBfLoadingRepos(false));
  }, [bfSelectedRepo]);

  const fetchBranches = useCallback((repo, selectedRepo, repoOwner) => {
    if (!selectedRepo || !repo.trim()) {
      console.warn('[BrownfieldTab] fetchBranches skipped — missing repo or account');
      return;
    }
    setBfLoadingBranches(true);
    setBfBranchList([]);
    const owner = repoOwner || selectedRepo.gitUsername;
    const params = new URLSearchParams({
      username: selectedRepo.gitUsername,
      pat:      selectedRepo.gitPat,
      repo:     repo.trim(),
      owner,
    });
    const url = `${domain}/api/v1/github/branches?${params}`;
    console.log('[BrownfieldTab] fetchBranches → GET', url, '| owner:', owner, '| repo:', repo.trim());
    fetch(url, {
      headers: { 'x-access-token': localStorage.getItem('token') },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        console.log('[BrownfieldTab] fetchBranches ← raw:', data);
        const list = Array.isArray(data) ? data : (data.branches || []);
        console.log('[BrownfieldTab] fetchBranches ← branches:', list);
        setBfBranchList(list);
        if (list.length) {
          setBfBranch(prev => {
            const next = list.includes(prev) ? prev : list[0];
            console.log('[BrownfieldTab] fetchBranches → setBfBranch:', prev, '→', next);
            return next;
          });
        } else {
          console.warn('[BrownfieldTab] fetchBranches → empty list, bfBranch stays as-is');
        }
      })
      .catch(err => console.error('[BrownfieldTab] fetchBranches ← error:', err.message))
      .finally(() => setBfLoadingBranches(false));
  }, []);

  useEffect(() => {
    if (bfRepoUrl.trim() && bfSelectedRepo) fetchBranches(bfRepoUrl, bfSelectedRepo, bfRepoOwner);
  }, [bfRepoUrl, bfSelectedRepo, bfRepoOwner]);

  useEffect(() => {
    if (!bfSelectedConfluence || !bfSpaceKey) return;
    setBfLoadingPages(true);
    setBfPages([]);
    setBfSelectedPages([]);
    const params = new URLSearchParams({
      baseUrl:  bfSelectedConfluence.confluenceBaseUrl,
      email:    bfSelectedConfluence.confluenceEmail,
      pat:      bfSelectedConfluence.confluencePat,
      spaceKey: bfSpaceKey,
    });
    fetch(`${domain}/api/v1/confluence/pages?${params}`, {
      headers: { 'x-access-token': localStorage.getItem('token') },
    })
      .then(r => r.json())
      .then(data => { console.log('[BF] /confluence/pages ←', data); setBfPages(Array.isArray(data) ? data : []); })
      .catch(e => console.error('[BF] /confluence/pages error:', e))
      .finally(() => setBfLoadingPages(false));
  }, [bfSpaceKey, bfSelectedConfluence]);

  const fetchConfluencePages = useCallback(() => {
    if (!bfSelectedConfluence || !bfSpaceKey) return;
    setBfLoadingPages(true);
    setBfPages([]);
    setBfSelectedPages([]);
    const params = new URLSearchParams({
      baseUrl:  bfSelectedConfluence.confluenceBaseUrl,
      email:    bfSelectedConfluence.confluenceEmail,
      pat:      bfSelectedConfluence.confluencePat,
      spaceKey: bfSpaceKey,
    });
    fetch(`${domain}/api/v1/confluence/pages?${params}`, {
      headers: { 'x-access-token': localStorage.getItem('token') },
    })
      .then(r => r.json())
      .then(data => { console.log('[BF] /confluence/pages ←', data); setBfPages(Array.isArray(data) ? data : []); })
      .catch(e => console.error('[BF] /confluence/pages error:', e))
      .finally(() => setBfLoadingPages(false));
  }, [bfSelectedConfluence, bfSpaceKey]);

  const togglePage = useCallback((id) => {
    setBfSelectedPages(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  }, []);

  const handleBfAnalyze = useCallback(() => {
    setBfLoading(true);
    setBfAnalysisDone(false);
    setBfResults(null);
    setBfMessages(prev => [...prev, { role: "bot", content: "🔍 Analysis started…" }]);
    const requestBody = {
      sessionId,
      githubUsername:    bfRepoOwner || bfSelectedRepo?.gitUsername,
      githubPat:         bfSelectedRepo?.gitPat,
      repoPath:          bfRepoUrl.trim(),
      githubBranch:      bfBranch || 'main',
      confluenceEmail:   bfSelectedConfluence?.confluenceEmail,
      confluencePat:     bfSelectedConfluence?.confluencePat,
      confluenceBaseUrl: bfSelectedConfluence?.confluenceBaseUrl,
      spaceKey:          bfSpaceKey,
      pageIds:           bfSelectedPages,
    };
    console.log('[BrownfieldTab] analyze → request:', requestBody);
    fetch(`${domain}/api/v1/brownfield/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
      body: JSON.stringify(requestBody),
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        console.log('[BrownfieldTab] analyze ← response:', data);
        setBfLoading(false);
        setBfAnalysisDone(true);
        if (data && data.results) {
          setBfResults(data);
          if (data.branch) setBfBranch(data.branch);
        } else {
          console.warn('[BrownfieldTab] analyze: no results in response', data);
          setBfMessages(prev => [...prev, { role: "bot", content: "❌ Analysis returned no results. Please check your configuration and try again." }]);
        }
      })
      .catch(err => {
        console.error('[BrownfieldTab] analyze ← error:', err.message, err);
        setBfLoading(false);
        setBfMessages(prev => [...prev, { role: "bot", content: `❌ Analysis failed: ${err.message}` }]);
      });
  }, [sessionId, bfSelectedRepo, bfRepoOwner, bfRepoUrl, bfBranch, bfSelectedConfluence, bfSpaceKey, bfSelectedPages, setBfBranch]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    bfRepoAccounts,       bfConfluenceAccounts,
    bfSelectedRepo,       setBfSelectedRepo,
    bfSelectedConfluence, setBfSelectedConfluence,
    bfRepoUrl,            setBfRepoUrl,
    bfRepoOwner,          setBfRepoOwner,
    bfRepoList,           bfLoadingRepos,
    bfBranch,             setBfBranch,
    bfBranchList,         bfLoadingBranches,
    bfConfluenceSpaces,
    bfSpaceKey,           setBfSpaceKey,
    bfLoadingSpaces,
    bfPages,              setBfPages,
    bfLoadingPages,       setBfLoadingPages,
    bfSelectedPages,      setBfSelectedPages,
    bfMessages,           setBfMessages,
    bfLoading,            setBfLoading,
    bfAnalysisDone,       setBfAnalysisDone,
    bfChangedFiles,       setBfChangedFiles,
    bfResults,            setBfResults,
    bfLogs,               setBfLogs,
    bfOrgStandards,
    loadBfAccounts,
    fetchConfluencePages,
    togglePage,
    handleBfAnalyze,
    showBfStaleBanner,    setShowBfStaleBanner,
    bfLastActive: _p.lastActive,
  };
}

// ── Standard preview rich renderer ───────────────────────────────────────────
function renderPreview(text) {
  if (!text) return null;

  const parseBold = (str) =>
    str.split(/(\*\*[^*]+\*\*)/).map((p, j) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={j} style={{ color: '#eaf2ff', fontWeight: 700 }}>{p.slice(2, -2)}</strong>
        : p
    );

  // Group lines into sections (numbered headings start a new section)
  const lines = text.split('\n');
  const sections = [];
  let current = null;

  lines.forEach((line) => {
    const t = line.trim();
    const numMatch = t.match(/^(\d+)\.\s+(.*)/);
    if (numMatch && t === numMatch[0] && !t.includes(' ') === false) {
      // It's a section heading like "1. Naming Conventions"
      if (current) sections.push(current);
      current = { num: numMatch[1], title: numMatch[2], lines: [] };
    } else {
      if (!current) current = { num: null, title: null, lines: [] };
      current.lines.push(t);
    }
  });
  if (current) sections.push(current);

  return sections.map((section, si) => {
    if (!section.title) {
      // Preamble / non-sectioned lines
      return (
        <div key={`pre-${si}`} style={{ marginBottom: 8 }}>
          {section.lines.map((t, i) => renderLine(t, i, parseBold))}
        </div>
      );
    }
    return (
      <div key={`sec-${si}`} style={{ marginBottom: 24, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', background: '#0a0d14' }}>
        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', background: 'rgba(59,127,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,127,255,0.15)', border: '1px solid rgba(59,127,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#3b7fff', fontFamily: 'monospace' }}>{section.num}</span>
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#dce8ff', letterSpacing: '-0.01em' }}>{section.title}</span>
        </div>
        {/* Section body */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {section.lines.map((t, i) => renderLine(t, i, parseBold))}
        </div>
      </div>
    );
  });
}

function renderLine(t, i, parseBold) {
  if (!t) return <div key={i} style={{ height: 4 }} />;

  // ⚙️ Configurable — amber banner
  if (t.startsWith('⚙️')) {
    return (
      <div key={i} style={{ display: 'flex', gap: 10, margin: '8px 0', padding: '8px 12px', background: 'rgba(245,166,35,0.07)', borderLeft: '3px solid rgba(245,166,35,0.6)', borderRadius: '0 6px 6px 0' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#f5a623', lineHeight: 1.6 }}>{parseBold(t)}</span>
      </div>
    );
  }

  // Bullet: "- " or "• "
  if (/^[-•]\s+/.test(t)) {
    return (
      <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', marginBottom: 3 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b7fff', flexShrink: 0, marginTop: 6 }} />
        <span style={{ fontSize: 12, color: '#c8dcf5', lineHeight: 1.7 }}>{parseBold(t.replace(/^[-•]\s+/, ''))}</span>
      </div>
    );
  }

  // Sub-heading: entirely bold **...**
  if (t.startsWith('**') && t.endsWith('**')) {
    return (
      <div key={i} style={{ marginTop: 12, marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#9ab4d4', fontFamily: 'monospace', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {t.slice(2, -2)}
      </div>
    );
  }

  // Plain line
  return (
    <div key={i} style={{ fontSize: 12, color: '#c8dcf5', lineHeight: 1.7, marginBottom: 2, paddingLeft: 4 }}>
      {parseBold(t)}
    </div>
  );
}

// ── DiffView ──────────────────────────────────────────────────────────────────
function DiffView({ diff }) {
  return (
    <div
      onWheel={e => e.stopPropagation()}
      className="sh-bf-diff-view"
      style={{ background: '#060910', borderRadius: 10, padding: '14px 16px', height: 360, overflowY: 'scroll', overflowX: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
      {(!diff || diff.length === 0) ? (
        <div style={{ color: '#3d5273', fontFamily: 'monospace', fontSize: 11 }}>No changes detected</div>
      ) : diff.map((line, i) => (
        <div key={i} style={{ fontSize: 11, fontFamily: 'monospace', color: line.startsWith('-') ? '#ff4f4f' : line.startsWith('+') ? '#00c98d' : '#3d5273', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {line}
        </div>
      ))}
    </div>
  );
}

// ── Sidebar: form + status ────────────────────────────────────────────────
export function BrownfieldSidebar({
  sessionId,
  bfRepoAccounts, bfSelectedRepo, setBfSelectedRepo, bfRepoUrl, setBfRepoUrl,
  bfRepoOwner, setBfRepoOwner,
  bfRepoList, bfLoadingRepos,
  bfBranch, setBfBranch, bfBranchList, bfLoadingBranches,
  bfConfluenceAccounts, bfSelectedConfluence, setBfSelectedConfluence,
  bfConfluenceSpaces, bfSpaceKey, setBfSpaceKey, bfLoadingSpaces,
  bfPages, setBfPages, bfLoadingPages, setBfLoadingPages, bfSelectedPages, setBfSelectedPages,
  bfLoading, setBfLoading, bfAnalysisDone, setBfAnalysisDone,
  bfChangedFiles, setBfMessages, setBfResults,
  loadBfAccounts, fetchConfluencePages, togglePage,
  handleBfAnalyze,
}) {
  const canAnalyze = bfSelectedRepo && bfRepoUrl.trim() && bfSelectedConfluence && bfSpaceKey && !bfLoading;

  return (
    <div className="sh-bf-sidebar">
      {/* ── GitHub ── */}
      <div className="sh-bf-sidebar-section">
        <div className="sh-tree-section-label">GITHUB ACCOUNT</div>
        {bfRepoAccounts.length === 0 ? (
          <div className="sh-bf-sidebar-no-acct">
            <span>No GitHub account connected.</span>
            <button onClick={() => { window.location.href = "/settings"; }}>⚙️ Settings</button>
            <button onClick={loadBfAccounts}>🔄</button>
          </div>
        ) : (
          <select className="sh-bf-sidebar-select"
            value={bfSelectedRepo?._id || ""}
            onChange={e => setBfSelectedRepo(bfRepoAccounts.find(a => a._id === e.target.value))}>
            {bfRepoAccounts.map(a => <option key={a._id} value={a._id}>{a.gitUsername}</option>)}
          </select>
        )}
      </div>

      {/* ── Repository ── */}
      <div className="sh-bf-sidebar-section">
        <div className="sh-tree-section-label">REPOSITORY</div>
        {bfLoadingRepos ? (
          <div className="sh-bf-sidebar-no-acct"><span>Loading repositories…</span></div>
        ) : bfRepoList.length > 0 ? (
          <select className="sh-bf-sidebar-select"
            value={bfRepoUrl}
            onChange={e => {
              setBfRepoUrl(e.target.value);
              const r = bfRepoList.find(x => x.name === e.target.value);
              setBfRepoOwner(r?.fullName?.split('/')[0] || bfSelectedRepo?.gitUsername || '');
            }}>
            {bfRepoList.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
          </select>
        ) : (
          <input className="sh-bf-sidebar-input" type="text" placeholder="repository-name"
            value={bfRepoUrl} onChange={e => setBfRepoUrl(e.target.value)} />
        )}
      </div>

      {/* ── Branch ── */}
      <div className="sh-bf-sidebar-section">
        <div className="sh-tree-section-label">BRANCH</div>
        {bfLoadingBranches ? (
          <div className="sh-bf-sidebar-no-acct"><span>Loading branches…</span></div>
        ) : bfBranchList.length > 0 ? (
          <select
            className="sh-bf-sidebar-select"
            value={bfBranch}
            onChange={e => {
              console.log('[BrownfieldSidebar] branch onChange:', e.target.value, '(was:', bfBranch, ')');
              setBfBranch(e.target.value);
            }}
          >
            {bfBranchList.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        ) : (
          <input className="sh-bf-sidebar-input" type="text" placeholder="main"
            value={bfBranch} onChange={e => setBfBranch(e.target.value)} />
        )}
      </div>

      {/* ── Confluence Account ── */}
      <div className="sh-bf-sidebar-section">
        <div className="sh-tree-section-label">CONFLUENCE ACCOUNT</div>
        {bfConfluenceAccounts.length === 0 ? (
          <div className="sh-bf-sidebar-no-acct">
            <span>No Confluence account connected.</span>
            <button onClick={() => { window.location.href = "/settings"; }}>⚙️ Settings</button>
            <button onClick={loadBfAccounts}>🔄</button>
          </div>
        ) : (
          <select className="sh-bf-sidebar-select"
            value={bfSelectedConfluence?.confluenceEmail || ""}
            onChange={e => setBfSelectedConfluence(bfConfluenceAccounts.find(a => a.confluenceEmail === e.target.value))}>
            {bfConfluenceAccounts.map((a, i) => <option key={i} value={a.confluenceEmail}>{a.confluenceEmail}</option>)}
          </select>
        )}
      </div>

      {/* ── Confluence Space ── */}
      <div className="sh-bf-sidebar-section">
        <div className="sh-tree-section-label">CONFLUENCE SPACE</div>
        {bfLoadingSpaces ? (
          <span className="sh-confluence-loading">Loading spaces…</span>
        ) : bfConfluenceSpaces.length > 0 ? (
          <select className="sh-bf-sidebar-select" value={bfSpaceKey} onChange={e => setBfSpaceKey(e.target.value)}>
            {bfConfluenceSpaces.map(s => <option key={s.key} value={s.key}>{s.name} ({s.key})</option>)}
          </select>
        ) : (
          <input className="sh-bf-sidebar-input" type="text" placeholder="Space key e.g. DEV"
            value={bfSpaceKey} onChange={e => setBfSpaceKey(e.target.value)} />
        )}
      </div>

      {/* ── Confluence Pages ── */}
      {bfSpaceKey && bfSelectedConfluence && (
        <div className="sh-bf-sidebar-section">
          <div className="sh-tree-section-label">CONFLUENCE PAGES</div>
          {bfLoadingPages && <div className="sh-bf-sidebar-no-acct"><span>Loading pages…</span></div>}
          {bfPages.length > 0 && (
            <div className="sh-bf-pages-select-wrap" style={{ marginTop: 6 }}>
              <select multiple className="sh-bf-pages-select"
                value={bfSelectedPages}
                onChange={e => {
                  const vals = Array.from(e.target.selectedOptions, o => o.value);
                  setBfSelectedPages(vals);
                }}>
                {bfPages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
              <div className="sh-bf-pages-select-actions">
                <button onClick={() => setBfSelectedPages(bfPages.map(p => p.id))}>All</button>
                <button onClick={() => setBfSelectedPages([])}>Clear</button>
                <span>{bfSelectedPages.length ? `${bfSelectedPages.length} page${bfSelectedPages.length > 1 ? 's' : ''} selected` : 'None selected — top 5 used'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Analyze ── */}
      <div className="sh-bf-sidebar-section">
        <button className="sh-bf-analyze-btn" style={{ width: '100%' }} onClick={handleBfAnalyze} disabled={!canAnalyze}>
          {bfLoading ? "Analyzing…" : "🔍 Analyze"}
        </button>
      </div>

      {/* ── Status ── */}
      <div className="sh-bf-sidebar-section">
        <div className="sh-tree-section-label">STATUS</div>
        <div className={`sh-bf-sidebar-status ${bfLoading ? "loading" : bfAnalysisDone ? "done" : "idle"}`}>
          {bfLoading ? "🔍 Analysing…" : bfAnalysisDone ? "✅ Analysis complete" : "—"}
        </div>
      </div>

      {/* ── Changed files ── */}
      {bfChangedFiles.length > 0 && (
        <div className="sh-bf-sidebar-section">
          <div className="sh-tree-section-label">CHANGED FILES</div>
          {bfChangedFiles.map((f, i) => (
            <div key={i} className="sh-nav-item sh-tree-file">
              <span className={`sh-bf-file-badge sh-bf-file-badge--${f.status || "modified"}`}>
                {(f.status || "M")[0].toUpperCase()}
              </span>
              <span className="sh-tree-file-name">{f.path}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
// ── SelectiveFixPreview ───────────────────────────────────────────────────────
function SelectiveFixPreview({ selectiveFixResult, selectedErrorCount, selectiveCommitUrl, applyingSelective, copied, copyCode, downloadCode, flowName, commitSelectiveFix, onReject, onDocumentIt, docResult }) {
  const sfCode = selectiveFixResult.fixed_code || selectiveFixResult.rewritten_code || '';
  const rawDiff = selectiveFixResult.diff || selectiveFixResult.patch || selectiveFixResult.changes || '';
  const sfDiff = Array.isArray(rawDiff)
    ? rawDiff
    : typeof rawDiff === 'string' && rawDiff.trim()
      ? rawDiff.split('\n')
      : [];

  return (
    <>
      <div style={{ padding: '12px 16px', background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#f5a623', marginBottom: 4 }}>🎯 Selective Fix Preview</div>
        <div style={{ fontSize: 11, color: '#7a93bc' }}>Only the {selectedErrorCount} selected violation{selectedErrorCount > 1 ? 's' : ''} have been fixed. Review the changes before committing.</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => copyCode(sfCode)}
          style={{ flex: 1, padding: '8px', borderRadius: 7, background: copied ? 'rgba(0,201,141,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${copied ? 'rgba(0,201,141,0.3)' : 'rgba(255,255,255,0.1)'}`, color: copied ? '#00c98d' : '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace' }}>
          {copied ? '✅ Copied!' : '📋 Copy Code'}
        </button>
        <button onClick={() => downloadCode(sfCode, `${flowName}-selective-fix.xml`)}
          style={{ flex: 1, padding: '8px', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace' }}>
          ⬇️ Download .xml
        </button>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#dce8ff', marginBottom: 8 }}>
          {sfDiff.length > 0 ? 'Diff — Selected Changes Only' : 'Fixed Code'}
          {sfDiff.length > 0 && <span style={{ fontSize: 10, color: '#3d5273', fontWeight: 400, marginLeft: 8 }}>Red = removed · Green = added</span>}
        </div>
        {sfDiff.length > 0 ? (
          <DiffView diff={sfDiff} />
        ) : (
          <textarea
            readOnly
            value={sfCode}
            style={{ width: '100%', height: 360, background: '#060910', color: '#dce8ff', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '14px 16px', fontSize: 11, fontFamily: 'monospace', lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
          />
        )}
      </div>
      {selectiveFixResult?.commitError && (
        <div style={{ padding: '10px 14px', background: 'rgba(255,79,79,0.08)', border: '1px solid rgba(255,79,79,0.2)', borderRadius: 8, marginBottom: 10, fontSize: 11, color: '#ff4f4f', fontFamily: 'monospace' }}>
          ❌ {selectiveFixResult.commitError}
        </div>
      )}
      {!selectiveCommitUrl && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button onClick={onReject}
            style={{ padding: '10px', borderRadius: 8, background: 'transparent', color: '#ff4f4f', border: '1px solid rgba(255,79,79,0.3)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            ✕ Reject
          </button>
          <button onClick={commitSelectiveFix} disabled={applyingSelective}
            style={{ padding: '10px', borderRadius: 8, background: applyingSelective ? '#141c27' : '#f5a623', color: applyingSelective ? '#3d5273' : '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: applyingSelective ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {applyingSelective ? '⏳ Committing...' : '🚀 Approve & Commit'}
          </button>
        </div>
      )}
      {selectiveCommitUrl && (
        <div style={{ padding: '14px', background: 'rgba(0,201,141,0.08)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#00c98d', marginBottom: 8 }}>✅ Selective Fix Committed</div>
          <a href={selectiveCommitUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace' }}>🔗 View commit →</a>
          {!docResult && onDocumentIt && (
            <button onClick={onDocumentIt}
              style={{ marginTop: 10, width: '100%', padding: '8px', borderRadius: 8, background: 'rgba(59,127,255,0.12)', border: '1px solid rgba(59,127,255,0.25)', color: '#6aa3ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              📄 Document It
            </button>
          )}
          {docResult?.error && <div style={{ marginTop: 8, fontSize: 11, color: '#ff4f4f' }}>⛔ {docResult.error}</div>}
          {docResult?.title && !docResult.error && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#00c98d' }}>
              ✓ Documented in <strong>{docResult.title}</strong>
              {docResult.url && <> · <a href={docResult.url} target="_blank" rel="noreferrer" style={{ color: '#3b7fff' }}>View page →</a></>}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Document It Modal ────────────────────────────────────────────────────────
function DocModal({
  onClose, fixPayload, bfSelectedConfluence, bfConfluenceAccounts,
  bfRepoUrl, selected, onDone, backendUrl,
  sessionId, bfSelectedRepo, bfBranch, bfResults,
}) {
  const [activeTab,    setActiveTab]    = React.useState('fix');
  const [localError,   setLocalError]   = React.useState(null);
  const [localAccount, setLocalAccount] = React.useState(
    bfSelectedConfluence || (bfConfluenceAccounts && bfConfluenceAccounts[0]) || null
  );
  const [localSpaces,   setLocalSpaces]   = React.useState([]);
  const [localSpaceKey, setLocalSpaceKey] = React.useState('');
  const [loadingSpaces, setLoadingSpaces] = React.useState(false);
  // Tab 1 state
  const [creating,    setCreating]    = React.useState(false);
  const [localResult, setLocalResult] = React.useState(null);
  // Tab 2 state
  const [projCreating, setProjCreating] = React.useState(false);
  const [projError,    setProjError]    = React.useState(null);
  const [projResult,   setProjResult]   = React.useState(null);

  const repoName    = bfRepoUrl?.split('/').pop() || 'Repository';
  const filePath    = fixPayload?.filePath || selected?.file_path || '';
  const scoreBefore = fixPayload?.scoreBefore ?? selected?.score ?? '—';
  const scoreAfter  = fixPayload?.scoreAfter  ?? '—';
  const scoreGain   = (typeof scoreBefore === 'number' && typeof scoreAfter === 'number') ? scoreAfter - scoreBefore : null;
  const violations  = fixPayload?.violationsFixed || [];
  const commitUrl   = fixPayload?.commitUrl;
  const fixType     = fixPayload?.fixType === 'selective' ? 'Selective Fix' : 'Full Rewrite';

  React.useEffect(() => {
    if (!localAccount) { setLocalSpaces([]); setLocalSpaceKey(''); return; }
    setLoadingSpaces(true);
    setLocalSpaces([]);
    setLocalSpaceKey('');
    fetch(`${domain}/api/v1/confluence/getConfluenceSpaces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify({
        confluenceEmail:   localAccount.confluenceEmail,
        confluencePat:     localAccount.confluencePat,
        confluenceBaseUrl: localAccount.confluenceBaseUrl,
      }),
    })
      .then(r => r.json())
      .then(d => {
        console.log('[DocModal] /getConfluenceSpaces ←', d);
        const spaces = d.spaces || [];
        setLocalSpaces(spaces);
        if (spaces.length) setLocalSpaceKey(spaces[0].key);
      })
      .catch(e => console.error('[DocModal] /getConfluenceSpaces error:', e))
      .finally(() => setLoadingSpaces(false));
  }, [localAccount]);

  const handlePublish = () => {
    setLocalError(null);
    setCreating(true);
    const body = {
      commitUrl, filePath, scoreBefore, scoreAfter,
      violationsFixed: violations,
      fixType: fixPayload?.fixType || 'full',
      confluenceEmail: localAccount?.confluenceEmail,
      confluenceToken: localAccount?.confluencePat,
      spaceBaseUrl:    localAccount?.confluenceBaseUrl || '',
      spaceKey:        localSpaceKey,
      scanResults:     [],
      repoName,
    };
    console.log('[DocModal] fix-doc publish body:', body);
    fetch(`${backendUrl}/api/v1/brownfield/sync-confluence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(data => {
        console.log('[DocModal] /sync-confluence ←', data);
        if (data.error) throw new Error(data.error);
        const result = { url: data.url || null, title: data.title || 'Confluence Page' };
        setLocalResult(result);
        setCreating(false);
        onDone(result);
      })
      .catch(e => { console.error('[DocModal] /sync-confluence error:', e); setLocalError(e.message); setCreating(false); });
  };

  const handleProjectPublish = () => {
    setProjError(null);
    setProjCreating(true);
    const body = {
      sessionId,
      spaceBaseUrl:      localAccount?.confluenceBaseUrl || '',
      spaceKey:          localSpaceKey,
      confluenceEmail:   localAccount?.confluenceEmail,
      confluenceToken:   localAccount?.confluencePat,
      githubRepo:        bfRepoUrl?.trim() || '',
      githubToken:       bfSelectedRepo?.gitPat || '',
      githubBranch:      bfBranch || 'main',
      scanResults:       bfResults?.results || [],
      detectedLanguage:  bfResults?.detected_language || 'generic',
      repoName,
    };
    console.log('[DocModal] project-doc body:', body);
    fetch(`${backendUrl}/api/v1/brownfield/auto-create-docs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(data => {
        console.log('[DocModal] /auto-create-docs ←', data);
        if (data.error) throw new Error(data.error);
        setProjResult({ pages: data.pages || [] });
      })
      .catch(e => { console.error('[DocModal] /auto-create-docs error:', e); setProjError(e.message); })
      .finally(() => setProjCreating(false));
  };

  // Shared confluence selector panel (used in both tabs)
  const confluenceSelector = (
    <>
      <div>
        <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 4 }}>CONFLUENCE ACCOUNT</div>
        {bfConfluenceAccounts && bfConfluenceAccounts.length > 0 ? (
          <select
            value={localAccount?.confluenceEmail || ''}
            onChange={e => {
              const acc = bfConfluenceAccounts.find(a => a.confluenceEmail === e.target.value) || null;
              setLocalAccount(acc);
            }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 12, fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
            {bfConfluenceAccounts.map(a => (
              <option key={a.confluenceEmail} value={a.confluenceEmail}>
                {a.confluenceEmail}{a.confluenceBaseUrl ? ` (${a.confluenceBaseUrl.replace(/https?:\/\//, '')})` : ''}
              </option>
            ))}
          </select>
        ) : (
          <div style={{ padding: '16px', background: 'rgba(245,166,35,0.07)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#f5a623', marginBottom: 8 }}>⚠️ No Confluence account connected</div>
            <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.6, marginBottom: 14 }}>
              Connect a Confluence account in <strong style={{ color: '#dce8ff' }}>Settings → Connected Apps</strong> to publish documentation.
            </div>
            <a href="/AIAgentHub/ConnectedApps" style={{ display: 'inline-block', padding: '8px 16px', borderRadius: 7, background: '#3b7fff', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
              Go to Settings →
            </a>
          </div>
        )}
      </div>
      {localAccount && (
        <div>
          <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 4 }}>SPACE</div>
          {loadingSpaces ? (
            <div style={{ fontSize: 11, color: '#3d5273' }}>Loading spaces…</div>
          ) : localSpaces.length > 0 ? (
            <select
              value={localSpaceKey}
              onChange={e => setLocalSpaceKey(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 12, fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
              {localSpaces.map(s => (
                <option key={s.key} value={s.key}>{s.name ? `${s.name} (${s.key})` : s.key}</option>
              ))}
            </select>
          ) : (
            <input
              value={localSpaceKey}
              onChange={e => setLocalSpaceKey(e.target.value)}
              placeholder="e.g. ENG"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
            />
          )}
        </div>
      )}
    </>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: '100%', maxWidth: 960, background: '#0d1117',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14,
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#dce8ff' }}>📄 Document Your Changes</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#3d5273', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0 20px', flexShrink: 0 }}>
          {[['fix', '🔧 Fix Documentation'], ['project', '📁 Project Documentation']].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)} style={{
              padding: '10px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none', fontFamily: 'inherit',
              color: activeTab === key ? '#3b7fff' : '#3d5273',
              borderBottom: activeTab === key ? '2px solid #3b7fff' : '2px solid transparent',
              marginBottom: -1,
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab 1: Fix Documentation ── */}
        {activeTab === 'fix' && (
          <div style={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden', flexWrap: 'wrap' }}>
            {/* Left — Preview */}
            <div style={{ flex: '1 1 340px', padding: '20px', borderRight: '1px solid rgba(255,255,255,0.07)', overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 14 }}>PAGE PREVIEW</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#dce8ff', marginBottom: 16 }}>Fix Documentation Report</div>
              {[
                ['Repository', repoName],
                ['File', filePath || '—'],
                ['Fix type', fixType],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: '#3d5273', minWidth: 80 }}>{label}</span>
                  <span style={{ fontSize: 11, color: '#7a93bc', fontFamily: 'monospace', wordBreak: 'break-all' }}>{val}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', padding: '10px 14px', background: 'rgba(0,201,141,0.08)', border: '1px solid rgba(0,201,141,0.18)', borderRadius: 8 }}>
                <span style={{ fontSize: 11, color: '#3d5273' }}>Compliance</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#ff4f4f' }}>{scoreBefore}%</span>
                <span style={{ fontSize: 11, color: '#3d5273' }}>→</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#00c98d' }}>{scoreAfter}%</span>
                {scoreGain !== null && <span style={{ fontSize: 11, color: '#00c98d' }}>(+{scoreGain}pts)</span>}
              </div>
              {commitUrl && (
                <div style={{ marginBottom: 14 }}>
                  <a href={commitUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace' }}>🔗 View commit →</a>
                </div>
              )}
              {violations.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 8 }}>VIOLATIONS FIXED ({violations.length})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 200, overflowY: 'auto' }}>
                    {violations.map((v, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: '#7a93bc' }}>
                        <span style={{ color: '#00c98d', flexShrink: 0 }}>✓</span>
                        <span>{v?.rule || v?.description || JSON.stringify(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right — Publish */}
            <div style={{ flex: '1 1 280px', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.1em' }}>PUBLISH TO CONFLUENCE</div>
              {confluenceSelector}
              {localError && (
                <div style={{ fontSize: 11, color: '#ff4f4f', padding: '8px 12px', background: 'rgba(255,79,79,0.07)', borderRadius: 7 }}>
                  ⛔ {localError}
                </div>
              )}
              {localResult ? (
                <div style={{ marginTop: 'auto', padding: '16px', background: 'rgba(0,201,141,0.07)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#00c98d', marginBottom: 8 }}>✅ Published successfully</div>
                  <div style={{ fontSize: 12, color: '#7a93bc', marginBottom: 10 }}>
                    Documented in <strong style={{ color: '#dce8ff' }}>{localResult.title}</strong>
                  </div>
                  {localResult.url && (
                    <a href={localResult.url} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 7, background: '#3b7fff', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                      🔗 View page →
                    </a>
                  )}
                </div>
              ) : localAccount && (
                <button onClick={handlePublish} disabled={creating || loadingSpaces}
                  style={{
                    marginTop: 'auto', padding: '11px', borderRadius: 9,
                    background: (creating || loadingSpaces) ? '#141c27' : '#3b7fff',
                    color: (creating || loadingSpaces) ? '#3d5273' : '#fff',
                    border: 'none', fontSize: 13, fontWeight: 700,
                    cursor: (creating || loadingSpaces) ? 'default' : 'pointer', fontFamily: 'inherit',
                  }}>
                  {creating ? '⏳ Publishing…' : '📄 Publish Fix Documentation'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Tab 2: Project Documentation ── */}
        {activeTab === 'project' && (
          <div style={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden', flexWrap: 'wrap' }}>
            {/* Left — Project info */}
            <div style={{ flex: '1 1 340px', padding: '20px', borderRight: '1px solid rgba(255,255,255,0.07)', overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 14 }}>PROJECT OVERVIEW</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#dce8ff', marginBottom: 16 }}>Project Documentation</div>
              {[
                ['Repository',  repoName],
                ['Branch',      bfBranch || 'main'],
                ['Language',    bfResults?.detected_language || '—'],
                ['Files scanned', bfResults?.scanned != null ? String(bfResults.scanned) : '—'],
                ['Avg score',   bfResults?.avg_score != null ? `${bfResults.avg_score}%` : '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: '#3d5273', minWidth: 100 }}>{label}</span>
                  <span style={{ fontSize: 11, color: '#7a93bc', fontFamily: 'monospace', wordBreak: 'break-all' }}>{val}</span>
                </div>
              ))}
              <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(59,127,255,0.06)', border: '1px solid rgba(59,127,255,0.15)', borderRadius: 8 }}>
                <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 6 }}>WHAT GETS CREATED</div>
                <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.7 }}>
                  A full project documentation page in Confluence covering architecture, API flows, compliance results, and all scanned files.
                </div>
              </div>
            </div>

            {/* Right — Publish */}
            <div style={{ flex: '1 1 280px', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.1em' }}>PUBLISH TO CONFLUENCE</div>
              {confluenceSelector}
              {projError && (
                <div style={{ fontSize: 11, color: '#ff4f4f', padding: '8px 12px', background: 'rgba(255,79,79,0.07)', borderRadius: 7 }}>
                  ⛔ {projError}
                </div>
              )}
              {projResult ? (
                <div style={{ marginTop: 'auto', padding: '16px', background: 'rgba(0,201,141,0.07)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#00c98d', marginBottom: 10 }}>✅ Project documentation created</div>
                  {projResult.pages && projResult.pages.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {projResult.pages.map((page, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 7 }}>
                          <span style={{ fontSize: 11, color: '#dce8ff', flex: 1, marginRight: 8 }}>{page.title || `Page ${i + 1}`}</span>
                          {page.url && (
                            <a href={page.url} target="_blank" rel="noreferrer"
                              style={{ fontSize: 11, color: '#3b7fff', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
                              View →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: '#7a93bc' }}>Documentation published to Confluence.</div>
                  )}
                </div>
              ) : localAccount && (
                <button onClick={handleProjectPublish} disabled={projCreating || loadingSpaces}
                  style={{
                    marginTop: 'auto', padding: '11px', borderRadius: 9,
                    background: (projCreating || loadingSpaces) ? '#141c27' : '#3b7fff',
                    color: (projCreating || loadingSpaces) ? '#3d5273' : '#fff',
                    border: 'none', fontSize: 13, fontWeight: 700,
                    cursor: (projCreating || loadingSpaces) ? 'default' : 'pointer', fontFamily: 'inherit',
                  }}>
                  {projCreating ? '⏳ Creating documentation…' : '📁 Create Project Documentation'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrownfieldPanel({
  sessionId, createSession,
  // GitHub props
  bfRepoAccounts, bfSelectedRepo, setBfSelectedRepo,
  bfRepoUrl, setBfRepoUrl, bfRepoOwner, setBfRepoOwner,
  bfRepoList, bfLoadingRepos,
  bfBranch, setBfBranch, bfBranchList, bfLoadingBranches,
  // Confluence props
  bfConfluenceAccounts, bfSelectedConfluence, setBfSelectedConfluence,
  bfConfluenceSpaces, bfSpaceKey, setBfSpaceKey, bfLoadingSpaces,
  bfPages, bfLoadingPages, bfSelectedPages, setBfSelectedPages,
  // Analysis state
  bfMessages, setBfMessages, bfLoading, setBfLoading, bfAnalysisDone, setBfAnalysisDone,
  bfResults, setBfResults,
  bfLogs, setBfLogs, loadBfAccounts,
  bfOrgStandards,
  // Stale session banner
  showBfStaleBanner, setShowBfStaleBanner, bfLastActive,
  // Token usage props
  usageData, liveTokens, extendingUsage, extensionStatus, handleExtendUsage,
  greenfieldLoading,
  onUsageRefresh,
  // Handoff from New Integrations → compliance scan on the generated folder, and back.
  pendingBfJob, onPendingBfJobConsumed, onScanStart, onGoToNewIntegrations, onReturnToDeployment,
}) {
  const messagesEndRef = useRef(null);
  const bfPromptRef    = useRef(null);
  const canCommit = true; // role restriction removed — all users can commit
  // Handoff: the generated project folder to auto-answer the repo step with, and a flag
  // that this compliance session came from New Integrations (so we offer "back to deploy").
  const pendingProjectPathRef = useRef(null);
  const originGreenfieldRef   = useRef(false);

  // Persist the add-feature panel state (mode, repo, generated files, commit/download)
  // so navigating away and back restores the results AND the file view. The hook above
  // persists chat/scan; this panel's feature state lives here, so it needs its own key.
  const BF_FEATURE_KEY = (() => {
    try { const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id || 'default'; return `bf_feature_${uid}`; }
    catch { return 'bf_feature_default'; }
  })();
  const _pf = (() => { try { return JSON.parse(localStorage.getItem(BF_FEATURE_KEY) || '{}'); } catch { return {}; } })();
  const [bfPrompt, setBfPrompt] = useState('');
  const scanPollTimerRef      = useRef(null);
  const scanningRef           = useRef(false); // true while a compliance scan is running (for wake/reconnect recovery)
  const lastAnalyzeMessageRef = useRef(null);

  // Panel-local state (not persisted — transient UI)
  const [selected,          setSelected]          = useState(null);
  const [panelTab,          setPanelTab]          = useState('violations');
  const [filter,            setFilter]            = useState('all');
  const [applying,          setApplying]          = useState(false);
  const [commitUrl,         setCommitUrl]         = useState(null);
  // Upload/local source (New Integrations handoff or ZIP) has no GitHub commit — the fix
  // is written into the local folder. Track that success state separately from commitUrl.
  const [codeUpdated,       setCodeUpdated]       = useState(false);
  const [updatedDownloadUrl, setUpdatedDownloadUrl] = useState(null);
  const [applyError,        setApplyError]        = useState(null);
  const [copied,            setCopied]            = useState(false);
  const [requirement,       setRequirement]       = useState('');
  const [stdModalIdx,       setStdModalIdx]       = useState(null); // index of standard_review msg open in modal
  const [generating,        setGenerating]        = useState(false);
  const [featureResult,     setFeatureResult]     = useState(null);
  const [committingFeature, setCommittingFeature] = useState(false);
  const [featureCommitUrl,  setFeatureCommitUrl]  = useState(null);
  const [featureCodeUpdated, setFeatureCodeUpdated] = useState(false); // upload/local source: feature written into the folder in place
  const [featureUpdateUrl,   setFeatureUpdateUrl]   = useState(null);  // upload/local source: re-zipped download link
  const [commitFeatureError, setCommitFeatureError] = useState(null);

  // ── Start-mode: compliance review (existing flow) vs add-feature (repo + prompt) ──
  const [bfMode,             setBfMode]             = useState(_pf.bfMode ?? null);  // null | 'compliance' | 'addfeature'
  const [featureRepo,        setFeatureRepo]        = useState(_pf.featureRepo ?? null);  // { github_repo, github_token, github_branch } | { source:'upload', projectPath }
  const [featureRequirement, setFeatureRequirement] = useState(_pf.featureRequirement ?? '');
  const [featureGenerating,  setFeatureGenerating]  = useState(false);
  const [featureFiles,       setFeatureFiles]       = useState(_pf.featureFiles ?? null);  // response files[] | { error }
  // Add-feature IDE-style preview modal
  const [featurePreviewOpen, setFeaturePreviewOpen] = useState(false);
  const [featurePreviewIdx,  setFeaturePreviewIdx]  = useState(0);
  const [featurePreviewTab,  setFeaturePreviewTab]  = useState('code'); // 'code' | 'diff'
  // Add-feature commit (generate → preview → explicit commit, no auto-commit)
  const [featureCommitting,  setFeatureCommitting]  = useState(false);
  const [featureCommitted,   setFeatureCommitted]   = useState(_pf.featureCommitted ?? false);
  const [featureCommitError, setFeatureCommitError] = useState('');
  const [featureDownloadUrl, setFeatureDownloadUrl] = useState(_pf.featureDownloadUrl ?? '');  // uploaded-ZIP apply → download link

  // Persist the feature panel state so the results + Preview survive navigation.
  // NEVER persist a transient error/empty result — otherwise a failed generation
  // would restore its error card on reload and look permanent (only clearable by a
  // fresh generation). Only successful generated files[] are worth restoring.
  useEffect(() => {
    try {
      const persistFiles = (Array.isArray(featureFiles) && featureFiles.length) ? featureFiles : null;
      localStorage.setItem(BF_FEATURE_KEY, JSON.stringify({
        bfMode, featureRepo, featureRequirement, featureFiles: persistFiles, featureCommitted, featureDownloadUrl,
      }));
    } catch (_) { /* quota / serialization — non-fatal */ }
  }, [bfMode, featureRepo, featureRequirement, featureFiles, featureCommitted, featureDownloadUrl]);

  // Selective fix state
  const [selectedViolations,  setSelectedViolations]  = useState(new Set());
  const [selectiveFixResult,  setSelectiveFixResult]  = useState(null);
  const [applyingSelective,   setApplyingSelective]   = useState(false);
  const [selectiveCommitUrl,  setSelectiveCommitUrl]  = useState(null);

  // Standard type chosen at session start: 'A'=Confluence, 'B'=Upload, 'C'=Intelog, 'D'=Org
  const [bfStandardType, setBfStandardType] = useState(null);

  // Document It modal state
  const [showDocModal,  setShowDocModal]  = useState(false);
  const [docFixPayload, setDocFixPayload] = useState(null);
  const [docSpaceKey,   setDocSpaceKey]   = useState('');
  const [docSpaceUrl,   setDocSpaceUrl]   = useState('');
  const [docCreating,   setDocCreating]   = useState(false);
  const [docResult,     setDocResult]     = useState(null); // { url, title } | { error }

  const [syncingConfluence,      setSyncingConfluence]      = useState(false);
  const [confluenceSyncResult,   setConfluenceSyncResult]   = useState(null);
  const [confluenceReportPageUrl, setConfluenceReportPageUrl] = useState(null);
  const [docReviewState,         setDocReviewState]         = useState(null);
  const [autoDocPrompt,          setAutoDocPrompt]          = useState(null);
  const [autoDocSpaceUrl,        setAutoDocSpaceUrl]        = useState('');
  const [autoDocSpaceKey,        setAutoDocSpaceKey]        = useState('');
  const [autoDocCreating,        setAutoDocCreating]        = useState(false);
  const [autoDocResult,          setAutoDocResult]          = useState(null);

  // Elicitation step — drives which picker card is shown
  const [elicitStep,  setElicitStep]  = useState(null);
  const [uploadState, setUploadState] = useState({ loading: false, error: null, validating: false, validationFailed: false, reason: null });

  // Thinking state cycling messages — phase-aware so setup turns (fetching a
  // standard, adding a document, answering) don't falsely say "Scanning repo…".
  const [thinkingMsg, setThinkingMsg] = useState('Thinking…');
  const isScanningPhase = bfMessages.some(m => m.scanning);
  useEffect(() => {
    if (!bfLoading) { setThinkingMsg('Thinking…'); return; }
    // Only the real repo scan gets scan-specific copy; everything else (the
    // setup conversation: standard selection, Confluence fetch, doc upload,
    // repo details) gets neutral copy that doesn't claim a scan is happening.
    const scanMsgs = [
      'Scanning repository…',
      'Reading file contents…',
      'Analysing violations…',
      'Checking compliance standards…',
      'Generating recommendations…',
      'Thinking…',
    ];
    const setupMsgs = [
      'Thinking…',
      'Processing your input…',
      'Working on it…',
      'One moment…',
    ];
    const msgs = isScanningPhase ? scanMsgs : setupMsgs;
    let idx = 0;
    setThinkingMsg(msgs[0]);
    const timer = setInterval(() => { idx = (idx + 1) % msgs.length; setThinkingMsg(msgs[idx]); }, 2500);
    return () => clearInterval(timer);
  }, [bfLoading, isScanningPhase]);
  // Manual-entry fallback states (used when no connected app is configured)
  const [bfConfUrl,   setBfConfUrl]   = useState('');
  const [bfConfEmail, setBfConfEmail] = useState('');
  const [bfConfToken, setBfConfToken] = useState('');
  const [bfRepoToken, setBfRepoToken] = useState('');
  // Repo source tab on the repo_details card: 'github' (URL) or 'upload' (ZIP).
  const [bfRepoTab, setBfRepoTab] = useState('github');
  const [bfZipUploading, setBfZipUploading] = useState(false);
  const [bfZipError, setBfZipError] = useState('');
  const [bfZipName, setBfZipName] = useState('');
  // Each entry: { spaceKey, pages, selectedPageIds, loading }
  const [bfAdditionalSelections, setBfAdditionalSelections] = useState([]);
  const [selectedOrgStandard,    setSelectedOrgStandard]    = useState('');
  const submittedAnswersRef  = useRef({});   // step → last submitted answer string
  const submittedRetriesRef  = useRef({});   // step → retry count

  const setupDone = bfAnalysisDone || bfLoading || !!bfResults;
  const [promptGiven, setPromptGiven] = useState(bfMessages.length > 0 || setupDone);
  const [showIde, setShowIde] = useState(!!bfResults);

  // Compliance Check handoff from New Integrations must always be a FRESH session.
  // bfResults lives in the parent hook (survives this panel's remount), so on a handoff
  // mount, hard-clear it BEFORE first paint — otherwise a stale/other-project IDE flashes
  // until the async auto-drive effect runs.
  useLayoutEffect(() => {
    if (pendingBfJob && pendingBfJob.mode === 'compliance' && pendingBfJob.projectPath) {
      setBfResults(null);
      setShowIde(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Track which picker steps have already been submitted so the card doesn't reappear if the backend loops
  const submittedStepsRef  = useRef(new Set());
  const scoreBeforeFixRef  = useRef(null); // captures selected.score before setSelected overwrites it after commit

  // On mount: if there are no messages (truly fresh visit, not a restored session),
  // clear the old langraph_session_id so the backend starts a new session.
  // EXCEPTION: a Compliance Check handoff from New Integrations must KEEP the current
  // sessionId — the generated folder was registered server-side under it (register-project).
  // Regenerating here would orphan that registration and trip the "session drift" warning.
  useEffect(() => {
    const isComplianceHandoff = pendingBfJob && pendingBfJob.mode === 'compliance' && pendingBfJob.projectPath;
    if (bfMessages.length === 0 && !bfResults && !bfAnalysisDone && !isComplianceHandoff) {
      localStorage.removeItem('langraph_session_id');
      if (createSession) createSession();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open IDE view when results arrive (either via REST response or socket)
  useEffect(() => {
    if (bfResults) setShowIde(true);
  }, [bfResults]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [bfMessages]);

  // Reset local UI when a new analysis kicks off
  useEffect(() => {
    if (bfLoading) {
      setSelected(null);
      setFilter('all');
      setCommitUrl(null);
      setFeatureResult(null);
      setFeatureCommitUrl(null);
      setSelectedViolations(new Set());
      setSelectiveFixResult(null);
      setSelectiveCommitUrl(null);
    }
  }, [bfLoading]);

  // Auto-select first scanned result only when results arrive fresh (no file selected yet)
  useEffect(() => {
    if (bfResults && !selected) {
      const first = bfResults.results?.find(r => r.status === 'scanned') || bfResults.results?.[0];
      if (first) setSelected(first);
    }
  }, [bfResults]);

  const applyFix = () => {
    if (!selected?.fixed_code || !selected?.sha) return;
    scoreBeforeFixRef.current = selected.score ?? null;
    const resolvedBranch = bfBranch || 'main';
    console.log('[applyFix] → POST /api/v1/brownfield/pushfix', { repoPath: bfRepoUrl.trim(), file_path: selected.file_path, branch: resolvedBranch, sha: selected.sha });
    setApplying(true);
    setApplyError(null);
    fetch(`${backendUrl}/api/v1/brownfield/pushfix`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token"), "x-user-role": getUserRole() },
      body: JSON.stringify({
        sessionId,
        githubUsername: bfRepoOwner || bfSelectedRepo?.gitUsername,
        githubPat:      bfSelectedRepo?.gitPat,
        repoPath:       bfRepoUrl.trim(),
        baseBranch:     resolvedBranch,
        files:          [{ file_path: selected.file_path, sha: selected.sha, fixed_code: selected.fixed_code }],
        // convenience fields used by controller.pushFix → storeCommittedFix cache
        file_path:      selected.file_path,
        fixed_code:     selected.fixed_code,
        score:          100,
        summary:        'All violations fixed',
        violations:     selected.violations,
        // Uploaded/generated project → write the fix into the local folder (no GitHub).
        ...(featureRepo?.source === 'upload' ? { source: 'upload', projectPath: featureRepo.projectPath } : {}),
      }),
    })
      .then(r => r.json())
      .then(data => {
        console.log('[BF] /brownfield/pushfix (applyFix) ←', data);
        if (data.error) throw new Error(data.error);
        const isUpload = featureRepo?.source === 'upload';
        const commitUrl = data.commitUrl || data.pushed?.[0]?.commit_url;
        // Upload mode writes the fix into the folder in place (no commit URL) — success is
        // just the absence of an error; GitHub mode still requires a commit URL.
        if (!isUpload && !commitUrl) throw new Error(data.errors?.[0]?.error || 'Commit failed');
        {
          setCommitUrl(commitUrl || null);
          if (isUpload) {
            // Local folder was updated in place → mark success (no commit URL); offer the zip.
            setCodeUpdated(true);
            setUpdatedDownloadUrl(data.downloadUrl ? `${backendUrl}${data.downloadUrl}` : null);
          }
          // Fix applied to the generated project from New Integrations → offer to go deploy it.
          if (isUpload && originGreenfieldRef.current) {
            setBfMessages(prev => [...prev, {
              role: 'bot',
              content: '✅ Compliance fix applied to your generated project. You can now deploy the updated project.',
              returnDeploy: true,
            }]);
          }
          // Full rewrite fixes ALL violations → score = 100%
          const updatedSelected = {
            ...selected,
            score: 100,
            violations: selected.violations?.map(v => ({ ...v, severity: 'pass' })),
            summary: 'All violations fixed ✅',
          };
          setSelected(updatedSelected);
          setBfResults(prev => {
            if (!prev) return prev;
            const updatedResults = prev.results.map(r => r.flow_name === selected.flow_name ? updatedSelected : r);
            const scanned = updatedResults.filter(r => r.status === 'scanned');
            const newAvg = scanned.length > 0 ? Math.round(scanned.reduce((a, r) => a + (r.score || 0), 0) / scanned.length) : 0;
            return { ...prev, avg_score: newAvg, results: updatedResults };
          });
        }
      })
      .catch(e => { setApplyError(e.message); })
      .finally(() => setApplying(false));
  };

  const generateFeature = () => {
    if (!requirement.trim() || !selected) return;
    const resolvedBranch = bfBranch || 'main';
    console.log('[generateFeature] bfResults?.branch:', bfResults?.branch, '| bfBranch:', bfBranch, '| sending branch:', resolvedBranch);
    setGenerating(true);
    setFeatureResult(null);
    setFeatureCommitUrl(null);
    setFeatureCodeUpdated(false);
    setFeatureUpdateUrl(null);
    setCommitFeatureError(null);
    fetch(`${domain}/api/v1/brownfield/addfeature`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token"), "x-user-role": getUserRole() },
      body: JSON.stringify({
        sessionId,
        githubUsername: bfRepoOwner || bfSelectedRepo?.gitUsername,
        githubPat:      bfSelectedRepo?.gitPat,
        repoPath:       bfRepoUrl.trim(),
        githubBranch:   resolvedBranch,
        file_path:      selected.file_path,
        sha:            selected.sha,
        current_code:   selected.fixed_code || null,
        requirement,
        commit:         false,
        // Uploaded/generated project → read the file from the local folder (no GitHub).
        ...(featureRepo?.source === 'upload' ? { source: 'upload', projectPath: featureRepo.projectPath } : {}),
      }),
    })
      .then(r => r.json())
      .then(data => { console.log('[BF] /brownfield/addfeature (generate) ←', data); setFeatureResult(data); })
      .catch(e => setFeatureResult({ error: e.message }))
      .finally(() => { setGenerating(false); if (onUsageRefresh) onUsageRefresh(); });
  };

  // Add-feature mode: repo-wide generation (empty file_path) — the backend generates
  // across the repo and, with commit:true, pushes the changes and returns commit URLs.
  const generateFeatureRepoWide = () => {
    if (!featureRequirement.trim() || !featureRepo) return;
    const req = featureRequirement.trim();
    setFeatureGenerating(true);
    setFeatureFiles(null);
    setFeatureCommitted(false);
    setFeatureCommitError('');
    setFeatureDownloadUrl('');
    setElicitStep(null);
    setBfMessages(prev => [...prev, { role: 'user', content: req }]);
    const reqBody = {
      sessionId,
      file_path: '',
      language: '',
      requirement: req,
      commit: false,   // generate only — user reviews the preview, then Commits explicitly
      // Contract naming (per the add-feature spec)
      github_repo:   featureRepo.github_repo,
      github_token:  featureRepo.github_token,
      github_branch: featureRepo.github_branch,
      // Legacy naming (matches the working per-file generateFeature; the running
      // backend reads repo/branch from these, not the github_* keys)
      githubUsername: featureRepo.owner || undefined,
      githubPat:      featureRepo.github_token,
      repoPath:       featureRepo.repo || featureRepo.github_repo,
      githubBranch:   featureRepo.github_branch,
      // Uploaded-ZIP source: read the extracted project locally instead of GitHub.
      ...(featureRepo.source === 'upload' ? { source: 'upload', projectPath: featureRepo.projectPath } : {}),
    };
    const _mask = t => (t ? `${String(t).slice(0, 4)}…(${String(t).length} chars)` : t);
    // Repo-wide generation must reach the intelog-be generator directly (like
    // analyze/answer). The controller (`domain`) intercepts commit:true and tries
    // to commit pre-generated code, which doesn't exist for a repo-wide request.
    console.log('[BF] /brownfield/addfeature (repo-wide) → POST', `${backendUrl}/api/v1/brownfield/addfeature`);
    console.log('[BF] /brownfield/addfeature (repo-wide) → request body:', {
      ...reqBody, github_token: _mask(reqBody.github_token), githubPat: _mask(reqBody.githubPat),
    });
    fetch(`${backendUrl}/api/v1/brownfield/addfeature`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token'), 'x-user-role': getUserRole() },
      body: JSON.stringify(reqBody),
    })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        console.log('[BF] /brownfield/addfeature (repo-wide) ← status', r.status, '| response:', data);
        return data;
      })
      .then(data => {
        if (data.error || data.success === false) { setFeatureFiles({ error: data.error || 'Feature generation failed.' }); return; }
        setFeatureFiles(Array.isArray(data.files) ? data.files : []);
      })
      .catch(e => { console.error('[BF] /brownfield/addfeature (repo-wide) ✖ error:', e); setFeatureFiles({ error: e.message }); })
      .finally(() => { setFeatureGenerating(false); if (onUsageRefresh) onUsageRefresh(); });
  };

  // Add-feature mode: commit the previewed repo-wide files via /pushfix (same as
  // the compliance-side commitFeature). Commits exactly what was generated — no
  // regeneration — and merges the resulting commit URLs back onto featureFiles.
  const commitFeatureRepoWide = () => {
    if (!Array.isArray(featureFiles) || !featureFiles.length || !featureRepo) return;
    const files = featureFiles
      .filter(f => f.new_code != null)
      .map(f => ({ file_path: f.file_path, sha: f.sha ?? null, fixed_code: f.new_code }));
    if (!files.length) { setFeatureCommitError('Nothing to commit.'); return; }
    setFeatureCommitting(true);
    setFeatureCommitError('');
    fetch(`${backendUrl}/api/v1/brownfield/pushfix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token'), 'x-user-role': getUserRole() },
      body: JSON.stringify({
        sessionId,
        githubUsername: featureRepo.owner || undefined,
        githubPat:      featureRepo.github_token,
        repoPath:       featureRepo.repo || featureRepo.github_repo,
        baseBranch:     featureRepo.github_branch,
        files,
        // convenience fields (controller.pushFix → storeCommittedFix cache)
        file_path:      files[0].file_path,
        fixed_code:     files[0].fixed_code,
        // Uploaded-ZIP: apply = write files back + re-zip (returns a downloadUrl).
        ...(featureRepo.source === 'upload' ? { source: 'upload', projectPath: featureRepo.projectPath } : {}),
      }),
    })
      .then(r => r.json())
      .then(data => {
        console.log('[BF] /brownfield/pushfix (commit feature repo-wide) ←', data);
        if (data.error) throw new Error(data.error);
        // Uploaded-ZIP apply → a re-zipped downloadable project (no commit URLs).
        // Trigger the download immediately so it's a single action for the user.
        if (data.downloadUrl) {
          const fullUrl = `${backendUrl}${data.downloadUrl}`;
          setFeatureDownloadUrl(fullUrl);
          setFeatureCommitted(true);
          try {
            const a = document.createElement('a');
            a.href = fullUrl; a.rel = 'noopener';
            document.body.appendChild(a); a.click(); a.remove();
          } catch (_) { /* popup/other blocked — the fallback link below still works */ }
          return;
        }
        const pushed = Array.isArray(data.pushed) ? data.pushed : [];
        const overallUrl = data.commitUrl || pushed[0]?.commit_url;
        if (!overallUrl && !pushed.length) throw new Error(data.errors?.[0]?.error || 'Commit failed');
        // Merge commit URLs back onto the files by path (fallback to the overall URL).
        setFeatureFiles(prev => Array.isArray(prev) ? prev.map(f => {
          const hit = pushed.find(p => p.file_path === f.file_path);
          return { ...f, commit_url: hit?.commit_url || f.commit_url || overallUrl };
        }) : prev);
        setFeatureCommitted(true);
      })
      .catch(e => { console.error('[BF] /brownfield/pushfix (commit feature) error:', e); setFeatureCommitError(e.message || 'Commit failed'); })
      .finally(() => { setFeatureCommitting(false); if (onUsageRefresh) onUsageRefresh(); });
  };

  const commitFeature = () => {
    if ((!featureResult?.new_code && !featureResult?.files?.length) || !selected) return;
    const resolvedBranch = bfBranch || 'main';
    // Commit exactly what was previewed — no second generation. Combine the
    // preview's files[] and projectUpdates[] (both now carry new_code) and push
    // them via /pushfix, which only writes the content it's given.
    const previewFiles = featureResult.files?.length
      ? featureResult.files
      : [{ file_path: featureResult.file_path || selected.file_path, sha: featureResult.sha || selected.sha, new_code: featureResult.new_code }];
    const files = [
      ...previewFiles.map(f => ({ file_path: f.file_path, sha: f.sha, fixed_code: f.new_code })),
      ...(featureResult.projectUpdates || [])
        .filter(p => p.new_code != null)
        .map(p => ({ file_path: p.file_path, sha: p.sha, fixed_code: p.new_code })),
    ].filter(f => f.file_path && f.fixed_code != null);
    if (!files.length) return;
    const isUpload = featureRepo?.source === 'upload';
    console.log('[commitFeature] → POST /api/v1/brownfield/pushfix', { branch: resolvedBranch, files: files.length, upload: isUpload });
    setCommittingFeature(true);
    setCommitFeatureError(null);
    fetch(`${backendUrl}/api/v1/brownfield/pushfix`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token"), "x-user-role": getUserRole() },
      body: JSON.stringify({
        sessionId,
        githubUsername: bfRepoOwner || bfSelectedRepo?.gitUsername,
        githubPat:      bfSelectedRepo?.gitPat,
        repoPath:       bfRepoUrl.trim(),
        baseBranch:     resolvedBranch,
        files,
        // convenience fields for controller.pushFix → storeCommittedFix cache
        file_path:      files[0].file_path,
        fixed_code:     files[0].fixed_code,
        // Uploaded/generated project → write the feature into the local folder (no GitHub).
        ...(isUpload ? { source: 'upload', projectPath: featureRepo.projectPath } : {}),
      }),
    })
      .then(r => r.json())
      .then(data => {
        console.log('[BF] /brownfield/pushfix (commitFeature) ←', data);
        if (data.error) throw new Error(data.error);
        const commitUrl = data.commitUrl || data.pushed?.[0]?.commit_url;
        // Upload mode writes the feature into the folder in place (no commit URL) — success is
        // just the absence of an error; GitHub mode still requires a commit URL.
        if (isUpload) {
          setFeatureCodeUpdated(true);
          setFeatureUpdateUrl(data.downloadUrl ? `${backendUrl}${data.downloadUrl}` : null);
          if (originGreenfieldRef.current) {
            setBfMessages(prev => [...prev, {
              role: 'bot',
              content: '✅ Feature added to your generated project. You can now deploy the updated project.',
              returnDeploy: true,
            }]);
          }
          return;
        }
        if (!commitUrl) throw new Error(data.errors?.[0]?.error || 'Commit failed');
        setFeatureCommitUrl(commitUrl);
      })
      .catch(e => { console.error('[BF] /brownfield/pushfix (commit feature) error:', e); setCommitFeatureError(e.message || 'Update failed'); })
      .finally(() => setCommittingFeature(false));
  };

  const toggleViolation = (index) => {
    setSelectedViolations(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
    setSelectiveFixResult(null);
    setSelectiveCommitUrl(null);
  };

  const selectAllErrors = () => {
    // Select every actionable violation (errors AND warnings) — many files have only
    // warnings, so limiting to severity==='error' would select nothing and look broken.
    const indexes = new Set();
    (selected?.violations || []).forEach((v, i) => {
      if (v && v.severity !== 'pass') indexes.add(i);
    });
    setSelectedViolations(indexes);
    setSelectiveFixResult(null);
    setSelectiveCommitUrl(null);
  };

  const clearSelection = () => {
    setSelectedViolations(new Set());
    setSelectiveFixResult(null);
    setSelectiveCommitUrl(null);
  };

  const fixSelectedViolations = () => {
    if (selectedViolations.size === 0 || !selected) return;
    const resolvedBranch = bfBranch || 'main';
    const violationsToFix = selected.violations
      ?.filter((_, i) => selectedViolations.has(i))
      .filter(v => v.severity !== 'pass') || [];
    if (violationsToFix.length === 0) { setApplyingSelective(false); return; }
    const requestBody = {
      sessionId,
      githubUsername: bfRepoOwner || bfSelectedRepo?.gitUsername,
      githubPat:      bfSelectedRepo?.gitPat,
      repoPath:       bfRepoUrl.trim(),
      githubBranch:   resolvedBranch,
      file_path:      selected.file_path,
      sha:            selected.sha,
      violations_to_fix: violationsToFix,
    };
    console.log('[fixSelectedViolations] → POST /api/v1/brownfield/selective — body:', JSON.stringify(requestBody, null, 2));
    console.log('[fixSelectedViolations] token:', localStorage.getItem("token") ? 'present' : 'MISSING');
    setApplyingSelective(true);
    setSelectiveFixResult(null);
    setSelectiveCommitUrl(null);
    fetch(`${domain}/api/v1/brownfield/selective`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token"), "x-user-role": getUserRole() },
      body: JSON.stringify(requestBody),
    })
      .then(r => {
        console.log('[fixSelectedViolations] ← HTTP', r.status);
        return r.json();
      })
      .then(data => {
        console.log('[fixSelectedViolations] ← response:', data);
        setSelectiveFixResult(data);
      })
      .catch(e => {
        console.error('[fixSelectedViolations] ← error:', e.message);
        setSelectiveFixResult({ error: e.message });
      })
      .finally(() => setApplyingSelective(false));
  };

  const commitSelectiveFix = () => {
    const code = selectiveFixResult?.fixed_code || selectiveFixResult?.rewritten_code;
    if (!code || !selected) return;
    scoreBeforeFixRef.current = selected.score ?? null;
    const resolvedBranch = bfBranch || 'main';
    console.log('[commitSelectiveFix] → POST /api/v1/brownfield/pushfix', { repoPath: bfRepoUrl.trim(), file_path: selected.file_path, branch: resolvedBranch, sha: selectiveFixResult?.sha || selected.sha });
    setApplyingSelective(true);
    fetch(`${backendUrl}/api/v1/brownfield/pushfix`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token"), "x-user-role": getUserRole() },
      body: JSON.stringify({
        sessionId,
        githubUsername: bfRepoOwner || bfSelectedRepo?.gitUsername,
        githubPat:      bfSelectedRepo?.gitPat,
        repoPath:       bfRepoUrl.trim(),
        baseBranch:     resolvedBranch,
        files:          [{ file_path: selected.file_path, sha: selectiveFixResult.sha || selected.sha, fixed_code: code }],
        // convenience fields used by controller.pushFix → storeCommittedFix cache
        file_path:      selected.file_path,
        fixed_code:     code,
        violations:     selected.violations,
      }),
    })
      .then(r => r.json())
      .then(data => {
        console.log('[BF] /brownfield/pushfix (commitSelectiveFix) ←', data);
        if (data.error) throw new Error(data.error);
        const commitUrl = data.commitUrl || data.pushed?.[0]?.commit_url;
        if (!commitUrl) throw new Error(data.errors?.[0]?.error || 'Commit failed');
        {
          setSelectiveCommitUrl(commitUrl);
          const fixedIndexes = selectedViolations;
          const totalRules = selected.violations?.length || 0;
          const newViolations = selected.violations?.map((v, i) => ({
            ...v,
            severity: fixedIndexes.has(i) && v.severity !== 'pass' ? 'pass' : v.severity,
          }));
          const fixedCount = [...fixedIndexes].filter(i => {
            const v = selected.violations?.[i];
            return v && v.severity !== 'pass';
          }).length;
          const scoreIncrease = totalRules > 0 ? Math.round((fixedCount / totalRules) * 100) : 0;
          const newScore = Math.min(100, selected.score + scoreIncrease);
          const updatedSelected = {
            ...selected,
            score: newScore,
            sha: data.newSha || selectiveFixResult.sha || selected.sha, // refresh SHA so full rewrite can commit
            violations: newViolations,
            summary: `${fixedIndexes.size} selected violation${fixedIndexes.size > 1 ? 's' : ''} fixed — score updated to ${newScore}%`,
          };
          setSelected(updatedSelected);
          setBfResults(prev => {
            if (!prev) return prev;
            const updatedResults = prev.results.map(r => r.flow_name === selected.flow_name ? updatedSelected : r);
            const scanned = updatedResults.filter(r => r.status === 'scanned');
            const newAvg = scanned.length > 0 ? Math.round(scanned.reduce((a, r) => a + (r.score || 0), 0) / scanned.length) : 0;
            return { ...prev, avg_score: newAvg, results: updatedResults };
          });
          setSelectedViolations(new Set());
        }
      })
      .catch(e => { setSelectiveFixResult(prev => ({ ...prev, commitError: e.message })); })
      .finally(() => setApplyingSelective(false));
  };

  const copyCode = code => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCode = (code, filename) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u; a.download = filename; a.click();
    URL.revokeObjectURL(u);
  };

  const syncFixToConfluence = ({ commitUrl: cUrl, fixType, scoreBefore, scoreAfter, violationsFixed }) => {
    if (!cUrl) return;
    setSyncingConfluence(true);
    setConfluenceSyncResult(null);
    const reqBody = {
      commitUrl: cUrl, filePath: selected?.file_path,
      scoreBefore, scoreAfter, violationsFixed, fixType,
      reportPageUrl: confluenceReportPageUrl || null,
      confluenceEmail: bfSelectedConfluence?.confluenceEmail,
      confluenceToken: bfSelectedConfluence?.confluencePat,
      spaceBaseUrl: bfSelectedConfluence?.confluenceBaseUrl,
      spaceKey: bfSpaceKey,
      scanResults: bfResults?.results || [],
      repoName: bfRepoUrl?.split('/').pop() || 'Repository',
    };
    console.log('[syncFixToConfluence] request body:', reqBody);
    console.log('[syncFixToConfluence] bfSelectedConfluence:', bfSelectedConfluence);
    fetch(`${backendUrl}/api/v1/brownfield/sync-confluence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(reqBody),
    })
      .then(r => { console.log('[syncFixToConfluence] response status:', r.status); return r.json(); })
      .then(data => {
        console.log('[syncFixToConfluence] response body:', data);
        if (data.error) throw new Error(data.error);
        if (data.url && !confluenceReportPageUrl) setConfluenceReportPageUrl(data.url);
        setConfluenceSyncResult({ url: data.url || null, title: data.title || 'Compliance Report' });
      })
      .catch(e => { console.error('[syncFixToConfluence] error:', e); setConfluenceSyncResult({ error: e.message }); })
      .finally(() => setSyncingConfluence(false));
  };

  const handleAutoCreateDocs = () => {
    if (!autoDocSpaceUrl.trim() || !autoDocSpaceKey.trim()) return;
    setAutoDocCreating(true);
    setAutoDocResult(null);
    fetch(`${backendUrl}/api/v1/brownfield/auto-create-docs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify({
        sessionId,
        spaceBaseUrl: autoDocSpaceUrl.trim(), spaceKey: autoDocSpaceKey.trim(),
        confluenceEmail: bfSelectedConfluence?.confluenceEmail,
        confluenceToken: bfSelectedConfluence?.confluencePat,
        githubRepo: bfRepoUrl.trim(), githubToken: bfSelectedRepo?.gitPat,
        githubBranch: bfBranch || 'main',
        scanResults: bfResults?.results || [],
        detectedLanguage: bfResults?.detected_language || 'generic',
        repoName: bfRepoUrl?.split('/').pop() || 'Project',
      }),
    })
      .then(r => r.json())
      .then(data => {
        console.log('[BF] /brownfield/auto-create-docs ←', data);
        if (data.error) throw new Error(data.error);
        setAutoDocResult({ pages: data.pages });
        setAutoDocPrompt(null);
      })
      .catch(e => { console.error('[BF] /auto-create-docs error:', e); setAutoDocResult({ error: e.message }); })
      .finally(() => setAutoDocCreating(false));
  };

  const handleAgentResponse = (data) => {
    console.log('[BF] handleAgentResponse', { phase: data.phase, step: data.elicitation_step, questions: data.questions, message: data.message });
    setBfLoading(false);
    // Reflect token spend from this brownfield call in the Token Usage panel.
    if (onUsageRefresh) onUsageRefresh();
    if (data.error) {
      setBfMessages(prev => [...prev, { role: 'bot', content: `Error: ${data.error}` }]);
      return;
    }
    if (data.phase === 'elicitation') {
      const step = data.elicitation_step || null;
      console.log('[BF] elicitation step →', step, '| already submitted:', submittedStepsRef.current.has(step));
      // Backend re-asked a step that was already submitted — previous answer was rejected.
      // Clear submitted state and re-show the picker card so the user can correct their input.
      if (step && submittedStepsRef.current.has(step)) {
        submittedStepsRef.current.delete(step);
        delete submittedRetriesRef.current[step];
        delete submittedAnswersRef.current[step];
        if (data.message) {
          setBfMessages(prev => [...prev, { role: 'bot', content: data.message }]);
        }
        setElicitStep(step);
        return;
      }
      setElicitStep(step);

      // Handoff from New Integrations: auto-answer the repo step with the already-generated
      // project folder (source:'upload' + local projectPath) — no upload/repo card shown.
      if (step === 'repo_details' && pendingProjectPathRef.current) {
        const projectPath = pendingProjectPathRef.current;
        pendingProjectPathRef.current = null;
        submittedStepsRef.current.add('repo_details');
        const answer = ['github_repo: generated-project', 'github_token: local', 'github_branch: main'].join('\n');
        submittedAnswersRef.current['repo_details'] = answer;
        submittedRetriesRef.current['repo_details'] = 0;
        setFeatureRepo({ source: 'upload', projectPath, github_branch: 'main' });
        setElicitStep(null);
        setBfMessages(prev => [...prev, { role: 'user', content: 'Using the generated project from New Integrations (no upload needed)' }]);
        callAnswer(answer, { source: 'upload', projectPath });
        return;
      }

      if (step === 'standard_selection') {
        // Show text message then an options chip row
        const msgs = [];
        if (data.message) msgs.push({ role: 'bot', content: data.message });
        msgs.push({ role: 'bot', type: 'options' });
        setBfMessages(prev => [...prev, ...msgs]);
        return;
      }

      if (step === 'standard_review') {
        // Rich review card — text input stays visible for edits
        // Excluded from submittedStepsRef because edit loop intentionally revisits this step
        const preview = data.standard_preview || data.standard_summary;
        const msgs = [];
        if (data.message) msgs.push({ role: 'bot', content: data.message });
        if (preview) msgs.push({ role: 'bot', type: 'standard_review', preview, link: data.standard_link || null });
        else msgs.push({ role: 'bot', content: 'Please review and type "accept" or describe changes below.' });
        setBfMessages(prev => [
          ...prev.map(m => m.type === 'standard_review' && !m.accepted ? { ...m, superseded: true } : m),
          ...msgs,
        ]);
        setElicitStep(null); // text input stays visible
        return;
      }

      // For card-driven steps (confluence_details, upload_instruction, repo_details)
      // only show the intro message — the picker card appears below
      const isCardStep = ['confluence_details', 'upload_instruction', 'repo_details'].includes(step);
      const parts = [];
      if (data.message) parts.push(data.message);
      if (!isCardStep && data.questions?.length) {
        data.questions.forEach((q, qi) => {
          const text = data.questions.length > 1 ? `${qi + 1}. ${q.question}` : q.question;
          parts.push(q.hint ? `${text}\n${q.hint}` : text);
        });
      }
      setBfMessages(prev => [...prev, { role: 'bot', content: parts.join('\n\n') || 'Please continue below.' }]);
      return;
    }
    if (data.phase === 'scanning') {
      setElicitStep(null);
      submittedStepsRef.current.clear();
      submittedAnswersRef.current = {};
      submittedRetriesRef.current = {};
      setBfLoading(true);
      setBfAnalysisDone(false);
      setBfMessages(prev => {
        if (prev.some(m => m.scanning)) return prev;
        return [...prev, { role: 'bot', content: data.message || 'Scan started — results will appear shortly.', scanning: true }];
      });
      // Poll every 5 s — backend returns 'scanning' until done, then 'complete' with results
      scanningRef.current = true;
      markScanRunning(sessionId); // persist so the scan survives sleep/disconnect/reload
      if (scanPollTimerRef.current) clearInterval(scanPollTimerRef.current);
      scanPollTimerRef.current = setInterval(pollForResults, 5000);
      return;
    }
    if (data.phase === 'complete') {
      scanningRef.current = false;
      clearScanMark();
      setBfLoading(false);
      if (scanPollTimerRef.current) { clearInterval(scanPollTimerRef.current); scanPollTimerRef.current = null; }
      // Backend returns scan_results; UI expects results
      const normalized = {
        results:           data.scan_results || data.results || [],
        avg_score:         data.avg_score   ?? 0,
        total:             data.total        ?? 0,
        scanned:           data.scanned      ?? 0,
        no_code:           data.no_code      ?? 0,
        detected_language: data.detected_language || 'generic',
        branch:            data.branch || null,
      };
      setBfResults(normalized);
      setBfAnalysisDone(true);
      // Nothing scannable found — don't open an empty IDE; explain clearly so it
      // doesn't look like the bot went silent.
      if (!normalized.scanned || !(normalized.results && normalized.results.length)) {
        setBfMessages(prev => [...prev, {
          role: 'bot',
          content: `⚠️ Scan finished but found no scannable ${normalized.detected_language !== 'generic' ? normalized.detected_language + ' ' : ''}files in the ${featureRepo?.source === 'upload' ? 'uploaded project' : 'repository'}. Check that the ZIP/repo contains the source code (e.g. \`src/main/mule\` for Mule) and try again.`,
          isError: true,
        }]);
        return;
      }
      setShowIde(true);
      setBfMessages(prev => [...prev, {
        role: 'bot',
        content: `Scan complete — ${normalized.scanned} file${normalized.scanned !== 1 ? 's' : ''} scanned · avg compliance score ${normalized.avg_score}%`,
      }]);
      return;
    }
    if (data.phase === 'error') {
      scanningRef.current = false;
      clearScanMark();
      setBfLoading(false);
      setBfMessages(prev => [...prev, { role: 'bot', content: `Scan failed: ${(data.errors || []).join(', ')}`, isError: true }]);
      return;
    }
  };

  const callAnalyze = (message) => {
    lastAnalyzeMessageRef.current = message;
    const body = { sessionId, message };
    console.log('[BF] callAnalyze → POST /analyze', body);
    if (onScanStart) onScanStart(); // mark a scan started by THIS session so its completion is accepted
    setBfLoading(true);
    fetch(`${backendUrl}/api/v1/brownfield/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(data => { console.log('[BF] /analyze ←', data); handleAgentResponse(data); })
      .catch(e => { console.error('[BF] /analyze error', e); setBfLoading(false); setBfMessages(prev => [...prev, { role: 'bot', content: `Error: ${e.message}` }]); });
  };

  const callAnswer = (userAnswers, extra = {}) => {
    const body = { sessionId, userAnswers, ...extra };
    console.log('[BF] callAnswer → POST /answer', body);
    if (onScanStart) onScanStart(); // an answer can trigger the scan phase — accept its completion
    setBfLoading(true);
    fetch(`${backendUrl}/api/v1/brownfield/answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(data => { console.log('[BF] /answer ←', data); handleAgentResponse(data); })
      .catch(e => { console.error('[BF] /answer error', e); setBfLoading(false); setBfMessages(prev => [...prev, { role: 'bot', content: `Error: ${e.message}` }]); });
  };

  // Handoff from New Integrations: when a pending compliance job arrives on a FRESH
  // panel, auto-start the compliance flow against the generated folder (which is
  // registered server-side by the caller). Standard selection still asks the user;
  // the repo step is auto-answered above via pendingProjectPathRef.
  useEffect(() => {
    if (!pendingBfJob || pendingBfJob.mode !== 'compliance' || !pendingBfJob.projectPath) return;
    // Explicit user intent (clicked Compliance Check) — reset any prior brownfield session
    // and start a fresh compliance run against the generated folder.
    pendingProjectPathRef.current = pendingBfJob.projectPath;
    originGreenfieldRef.current = true;
    setBfResults(null);
    submittedStepsRef.current = new Set();
    // Local generated folder — treat as an 'upload' source so apply-fix uses "Update Code"
    // (write files in place) instead of committing to GitHub. The backend skips the
    // repo_details step (elicitation backfill), so set this here rather than relying on it.
    setFeatureRepo({ source: 'upload', projectPath: pendingBfJob.projectPath, github_branch: 'main' });
    setBfMode('compliance');
    setBfMessages([{ role: 'user', content: 'Compliance, Standards & Business Logic Testing review' }]);
    callAnalyze('I want to run a compliance and standards review on my repository.');
    if (onPendingBfJobConsumed) onPendingBfJobConsumed();
  }, [pendingBfJob]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent poll used during scanning phase — does NOT set bfLoading
  const pollForResults = () => {
    fetch(`${backendUrl}/api/v1/brownfield/answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify({ sessionId, poll: true }),
    })
      .then(r => {
        if (!r.ok) return null; // 4xx/5xx — retry silently on next tick
        return r.json();
      })
      .then(data => {
        if (!data) return; // non-2xx — keep interval running
        console.log('[BF] poll ←', data.phase);
        if (data.phase === 'scanning' || !data.phase) return; // still running
        clearInterval(scanPollTimerRef.current);
        scanPollTimerRef.current = null;
        handleAgentResponse(data);
      })
      .catch(() => {}); // network error — next interval tick will retry
  };

  // Recover the scan after the tab was hidden / the system slept / the network dropped.
  // The scan keeps running on the server; on wake/reconnect we immediately re-poll for the
  // result and re-arm the interval if it was lost — same idea as New Integrations recovery.
  useEffect(() => {
    const recover = () => {
      if (!scanningRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // wait for connectivity
      pollForResults();                                   // immediate check
      if (!scanPollTimerRef.current) {                    // interval was lost — re-arm it
        scanPollTimerRef.current = setInterval(pollForResults, 5000);
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') recover(); };
    window.addEventListener('online', recover);
    window.addEventListener('focus', recover);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', recover);
      window.removeEventListener('focus', recover);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resume a scan that was in flight before a reload / remount. scanningRef is a ref
  // (lost on reload), so on mount we read the persisted marker and, if it matches the
  // restored sessionId, re-arm polling. The backend keeps the result under the same
  // sessionId, so this pulls the finished (or still-running) scan back in.
  const recoveredRef = useRef(false);
  useEffect(() => {
    if (recoveredRef.current) return;
    if (!sessionId) return; // wait until sessionId is restored from localStorage
    if (pendingBfJob && pendingBfJob.mode === 'compliance') return; // fresh handoff — never resume
    if (bfResults) return; // already have results
    const mark = readScanMark();
    if (!mark || mark.sessionId !== sessionId) return;
    if (mark.ts && Date.now() - mark.ts > 2 * 60 * 60 * 1000) { clearScanMark(); return; } // stale (>2h)
    recoveredRef.current = true;
    scanningRef.current = true;
    if (onScanStart) onScanStart(); // accept a socket completion replayed after reconnect
    setBfLoading(true);
    setPromptGiven(true);
    setBfMessages(prev => (prev.some(m => m.scanning)
      ? prev
      : [...prev, { role: 'bot', content: 'Resuming your scan…', scanning: true }]));
    pollForResults(); // immediate check
    if (!scanPollTimerRef.current) {
      scanPollTimerRef.current = setInterval(pollForResults, 5000);
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = () => {
    const txt = bfPrompt.trim();
    if (!txt || bfLoading) return;
    if (greenfieldLoading) {
      setBfMessages(prev => [...prev, {
        role: 'bot',
        content: '⚠️ New integration generation is in progress in the New Integrations tab. Please wait for it to complete before starting an analysis to avoid session conflicts.',
      }]);
      return;
    }
    setBfMessages(prev => [...prev, { role: 'user', content: txt }]);
    setBfPrompt('');

    // Scan already complete — guide the user to start a new session
    if (bfResults) {
      setBfMessages(prev => [...prev, {
        role: 'bot',
        content: 'The current analysis is complete. To analyse a new repository or re-run the scan, please start a New Session using the button above.',
        isNewSessionHint: true,
      }]);
      return;
    }

    if (!promptGiven) {
      setPromptGiven(true);
      callAnalyze(txt);
      return;
    }
    callAnswer(txt);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleBfNewSession = () => {
    if (scanPollTimerRef.current) { clearInterval(scanPollTimerRef.current); scanPollTimerRef.current = null; }
    scanningRef.current = false;
    clearScanMark();
    localStorage.removeItem('langraph_session_id');
    localStorage.removeItem(getBfStorageKey());
    localStorage.removeItem(BF_FEATURE_KEY);
    if (createSession) createSession();
    // reset hook-owned state
    setBfMessages([]);
    setBfLoading(false);
    setBfAnalysisDone(false);
    setBfResults(null);
    if (setBfLogs) setBfLogs([]);
    // reset panel-local state
    setBfPrompt('');
    setPromptGiven(false);
    setShowIde(false);
    setElicitStep(null);
    setUploadState({ loading: false, error: null, validating: false, validationFailed: false, reason: null });
    setBfConfUrl('');
    setBfConfEmail('');
    setBfConfToken('');
    setBfRepoToken('');
    setBfAdditionalSelections([]);
    setSelectedOrgStandard('');
    setSelected(null);
    setPanelTab('violations');
    setFilter('all');
    setApplying(false);
    setCommitUrl(null);
    setCodeUpdated(false);
    setUpdatedDownloadUrl(null);
    setApplyError(null);
    setCopied(false);
    setRequirement('');
    setGenerating(false);
    setFeatureResult(null);
    setCommittingFeature(false);
    setFeatureCommitUrl(null);
    setBfMode(null);
    setFeatureRepo(null);
    setFeatureRequirement('');
    setFeatureGenerating(false);
    setFeatureFiles(null);
    setFeaturePreviewOpen(false);
    setFeaturePreviewIdx(0);
    setFeaturePreviewTab('code');
    setFeatureCommitting(false);
    setFeatureCommitted(false);
    setFeatureCommitError('');
    setFeatureDownloadUrl('');
    setBfRepoTab('github');
    setBfZipName('');
    setBfZipError('');
    setSelectedViolations(new Set());
    setSelectiveFixResult(null);
    setApplyingSelective(false);
    setSelectiveCommitUrl(null);
    setBfStandardType(null);
    setShowDocModal(false);
    setDocFixPayload(null);
    setDocSpaceKey('');
    setDocSpaceUrl('');
    setDocCreating(false);
    setDocResult(null);
    submittedAnswersRef.current  = {};
    submittedRetriesRef.current  = {};
    submittedStepsRef.current    = new Set();
  };

  const handleConfluenceManualSubmit = () => {
    const urls = bfConfUrl.trim().split('\n').map(u => u.trim()).filter(Boolean).join(', ');
    if (!urls || !bfConfEmail.trim() || !bfConfToken.trim()) return;
    const answer = `confluence_urls: ${urls}\nconfluence_email: ${bfConfEmail.trim()}\nconfluence_token: ${bfConfToken.trim()}`;
    submittedStepsRef.current.add('confluence_details');
    submittedAnswersRef.current['confluence_details'] = answer;
    submittedRetriesRef.current['confluence_details'] = 0;
    setBfMessages(prev => [...prev, { role: 'user', content: `Confluence: ${urls}` }]);
    setElicitStep(null);
    callAnswer(answer);
  };

  const handleRepoManualSubmit = () => {
    if (!bfRepoUrl.trim() || !bfRepoToken.trim()) return;
    const branch = bfBranch.trim() || 'main';
    // Add-feature mode: capture repo details locally and move to the feature prompt
    // (skip the elicitation graph entirely).
    if (bfMode === 'addfeature') {
      const raw = bfRepoUrl.trim();
      const [o, r] = raw.includes('/') ? [raw.slice(0, raw.indexOf('/')), raw.slice(raw.indexOf('/') + 1)] : ['', raw];
      setFeatureRepo({
        owner: o,
        repo:  r,
        github_repo: raw,
        github_token: bfRepoToken.trim(),
        github_branch: branch,
      });
      setBfMessages(prev => [...prev, { role: 'user', content: `Repo: ${raw} (${branch})` }]);
      setElicitStep('feature_prompt');
      return;
    }
    const answer = `github_repo: ${bfRepoUrl.trim()}\ngithub_token: ${bfRepoToken.trim()}\ngithub_branch: ${branch}`;
    submittedStepsRef.current.add('repo_details');
    submittedAnswersRef.current['repo_details'] = answer;
    submittedRetriesRef.current['repo_details'] = 0;
    setBfMessages(prev => [...prev, { role: 'user', content: `Repo: ${bfRepoUrl.trim()} (${branch})` }]);
    setElicitStep(null);
    callAnswer(answer);
  };

  const errorViolations = selected?.violations?.filter(v => v.severity === 'error' || v.severity === 'warning') || [];
  const selectedErrorCount = [...selectedViolations].filter(i => {
    const v = selected?.violations?.[i];
    return v && v.severity !== 'pass';
  }).length;

  // ── Card submit handlers ──────────────────────────────────────────────────────

  const loadExtraPages = (idx, spaceKey) => {
    if (!bfSelectedConfluence || !spaceKey) return;
    setBfAdditionalSelections(prev => prev.map((e, i) => i === idx ? { ...e, loading: true, pages: [], selectedPageIds: [] } : e));
    const params = new URLSearchParams({
      baseUrl:  bfSelectedConfluence.confluenceBaseUrl,
      email:    bfSelectedConfluence.confluenceEmail,
      pat:      bfSelectedConfluence.confluencePat,
      spaceKey,
    });
    fetch(`${domain}/api/v1/confluence/pages?${params}`, {
      headers: { 'x-access-token': localStorage.getItem('token') },
    })
      .then(r => r.json())
      .then(data => setBfAdditionalSelections(prev => prev.map((e, i) => i === idx ? { ...e, pages: Array.isArray(data) ? data : [], loading: false } : e)))
      .catch(() => setBfAdditionalSelections(prev => prev.map((e, i) => i === idx ? { ...e, loading: false } : e)));
  };

  const addSpaceSelection = () => {
    const firstSpace = bfConfluenceSpaces[0]?.key || '';
    const newIdx = bfAdditionalSelections.length;
    setBfAdditionalSelections(prev => [...prev, { spaceKey: firstSpace, pages: [], selectedPageIds: [], loading: false }]);
    if (firstSpace) loadExtraPages(newIdx, firstSpace);
  };

  const removeSpaceSelection = (idx) => {
    setBfAdditionalSelections(prev => prev.filter((_, i) => i !== idx));
  };

  const handleConfluenceSubmit = () => {
    if (!bfSelectedConfluence || !bfSpaceKey) return;
    submittedStepsRef.current.add('confluence_details');
    const base = (bfSelectedConfluence.confluenceBaseUrl || '').replace(/\/$/, '');

    const pageUrls = [];
    if (bfSelectedPages.length > 0) {
      bfSelectedPages.forEach(id => pageUrls.push(`${base}/wiki/spaces/${bfSpaceKey}/pages/${id}`));
    } else {
      pageUrls.push(`${base}/wiki/spaces/${bfSpaceKey}`);
    }
    bfAdditionalSelections.forEach(({ spaceKey, selectedPageIds }) => {
      if (!spaceKey) return;
      if (selectedPageIds.length > 0) {
        selectedPageIds.forEach(id => pageUrls.push(`${base}/wiki/spaces/${spaceKey}/pages/${id}`));
      } else {
        pageUrls.push(`${base}/wiki/spaces/${spaceKey}`);
      }
    });
    const answer = [
      `confluence_email: ${bfSelectedConfluence.confluenceEmail}`,
      `confluence_token: ${bfSelectedConfluence.confluencePat}`,
      `confluence_urls: ${pageUrls.join(', ')}`,
    ].join('\n');
    submittedAnswersRef.current['confluence_details'] = answer;
    submittedRetriesRef.current['confluence_details'] = 0;
    const totalPages = bfSelectedPages.length + bfAdditionalSelections.reduce((a, e) => a + e.selectedPageIds.length, 0);
    const allSpaces = [bfSpaceKey, ...bfAdditionalSelections.map(e => e.spaceKey).filter(Boolean)];
    const label = `Confluence: ${bfSelectedConfluence.confluenceEmail} | Space${allSpaces.length > 1 ? 's' : ''}: ${allSpaces.join(', ')}${totalPages ? ` (${totalPages} pages)` : ''}`;
    setElicitStep(null);
    setBfMessages(prev => [...prev, { role: 'user', content: label }]);
    callAnswer(answer);
  };

  const handleRepoSubmit = () => {
    if (!bfSelectedRepo || !bfRepoUrl.trim()) return;
    const repoOwnerLabel = bfRepoOwner || bfSelectedRepo.gitUsername;
    // Add-feature mode: capture repo details locally and move to the feature prompt.
    if (bfMode === 'addfeature') {
      const branch = bfBranch || 'main';
      setFeatureRepo({
        owner: repoOwnerLabel,
        repo:  bfRepoUrl.trim(),
        github_repo: `${repoOwnerLabel}/${bfRepoUrl.trim()}`,
        github_token: bfSelectedRepo.gitPat,
        github_branch: branch,
      });
      setBfMessages(prev => [...prev, { role: 'user', content: `Repository: ${repoOwnerLabel}/${bfRepoUrl.trim()} (branch: ${branch})` }]);
      setElicitStep('feature_prompt');
      return;
    }
    submittedStepsRef.current.add('repo_details');
    const answer = [
      `github_repo: ${repoOwnerLabel}/${bfRepoUrl.trim()}`,
      `github_token: ${bfSelectedRepo.gitPat}`,
      `github_branch: ${bfBranch || 'main'}`,
    ].join('\n');
    submittedAnswersRef.current['repo_details'] = answer;
    submittedRetriesRef.current['repo_details'] = 0;
    const label = `Repository: ${repoOwnerLabel}/${bfRepoUrl.trim()} (branch: ${bfBranch || 'main'})`;
    setElicitStep(null);
    setBfMessages(prev => [...prev, { role: 'user', content: label }]);
    callAnswer(answer);
  };

  // Upload a ZIP of the codebase as an alternative to a GitHub repo. Extracts on the
  // server and returns a projectPath the scan/add-feature flows read locally.
  const handleRepoZipUpload = async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { setBfZipError('Please choose a .zip file'); return; }
    setBfZipError('');
    setBfZipName(file.name);
    setBfZipUploading(true);
    try {
      const form = new FormData();
      form.append('sessionId', sessionId);
      form.append('file', file);
      const res = await fetch(`${backendUrl}/api/v1/brownfield/upload-repo`, {
        method: 'POST',
        headers: { 'x-access-token': localStorage.getItem('token') },   // no Content-Type — browser sets multipart boundary
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.projectPath) {
        throw new Error(data.error || 'Upload failed');
      }
      const projectPath = data.projectPath;
      // Add-feature: capture the uploaded project locally, go to the feature prompt.
      if (bfMode === 'addfeature') {
        setFeatureRepo({ source: 'upload', projectPath, repo: file.name, github_branch: 'main' });
        setBfMessages(prev => [...prev, { role: 'user', content: `Uploaded: ${file.name} (${data.files} files)` }]);
        setElicitStep('feature_prompt');
      } else {
        // Compliance: complete the repo_details step. Send a placeholder repo string
        // (the scan ignores it for uploads) plus structured source/projectPath fields.
        submittedStepsRef.current.add('repo_details');
        const answer = ['github_repo: upload/uploaded-project', 'github_token: uploaded', 'github_branch: main'].join('\n');
        submittedAnswersRef.current['repo_details'] = answer;
        submittedRetriesRef.current['repo_details'] = 0;
        setElicitStep(null);
        setBfMessages(prev => [...prev, { role: 'user', content: `Uploaded project: ${file.name} (${data.files} files)` }]);
        callAnswer(answer, { source: 'upload', projectPath });
      }
    } catch (e) {
      console.error('[BF] upload-repo error', e);
      setBfZipError(e.message || 'Upload failed');
    } finally {
      setBfZipUploading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    submittedStepsRef.current.add('upload_instruction');
    setUploadState({ loading: true, error: null, validating: false, validationFailed: false, reason: null });

    const form = new FormData();
    form.append('document', file);

    const repoLang = bfResults?.detected_language || '';
    const uploadParams = new URLSearchParams();
    if (repoLang) uploadParams.set('language', repoLang);
    if (sessionId) uploadParams.set('sessionId', sessionId);
    const uploadQuery = uploadParams.toString() ? `?${uploadParams.toString()}` : '';

    try {
      setUploadState({ loading: true, error: null, validating: !!repoLang, validationFailed: false, reason: null });

      const res = await fetch(`${backendUrl}/api/v1/brownfield/upload-standard${uploadQuery}`, {
        method: 'POST',
        headers: { 'x-access-token': localStorage.getItem('token') },
        body: form,
      });
      const data = await res.json();

      if (data.validation_failed) {
        setUploadState({
          loading: false, error: null, validating: false, validationFailed: true,
          reason: data.reason || `This document doesn't appear to be a ${data.expected_language} standard.`,
          documentType: data.document_type || null,
        });
        return;
      }

      if (data.error) throw new Error(data.error);

      submittedAnswersRef.current['upload_instruction'] = data.text;
      submittedRetriesRef.current['upload_instruction'] = 0;
      setUploadState({ loading: false, error: null, validating: false, validationFailed: false, reason: null });
      setElicitStep(null);
      setBfMessages(prev => [...prev, { role: 'user', content: `Uploaded: ${file.name} (${data.chars || data.text?.length || '?'} chars)${data.validated ? ` ✓ validated for ${data.language}` : ''}` }]);
      callAnswer(data.text);
    } catch (e) {
      setUploadState({ loading: false, error: e.message, validating: false, validationFailed: false, reason: null });
    }
  };

  const filtered = bfResults?.results?.filter(r => {
    if (filter === 'all')     return true;
    if (filter === 'error')   return r.violations?.some(v => v.severity === 'error');
    if (filter === 'pass')    return r.score === 100;
    if (filter === 'no_code') return r.status === 'no_code';
    return true;
  }) || [];

  const scannedCount = bfResults?.scanned   ?? bfResults?.results?.filter(r => r.status === 'scanned').length ?? 0;
  const noCodeCount  = bfResults?.no_code   ?? bfResults?.results?.filter(r => r.status === 'no_code').length ?? 0;
  const totalCount   = bfResults?.total     ?? bfResults?.results?.length ?? 0;
  const avgScore     = bfResults?.avg_score ?? 0;

  const totalErrors   = (bfResults?.results || []).reduce((a, r) => a + (r.violations?.filter(v => v.severity === 'error').length || 0), 0);
  const totalWarnings = (bfResults?.results || []).reduce((a, r) => a + (r.violations?.filter(v => v.severity === 'warning').length || 0), 0);

  return (
    <div className="sh-bf-container">

      {greenfieldLoading && (
        <div className="sh-cross-tab-warn">
          ⚠️ New integration is being generated — analysis is paused until it completes.
        </div>
      )}

      {/* ── Add-feature IDE-style preview (file list + code / diff) ── */}
      {featurePreviewOpen && Array.isArray(featureFiles) && featureFiles.length > 0 && (() => {
        const f = featureFiles[Math.min(featurePreviewIdx, featureFiles.length - 1)] || featureFiles[0];
        const hasCode = typeof f.new_code === 'string' && f.new_code.length > 0;
        const tab = (!hasCode && featurePreviewTab === 'code') ? 'diff' : featurePreviewTab;
        const base = (p) => (p || '').replace(/\\/g, '/').split('/').filter(Boolean).pop() || p;
        const codeLines = hasCode ? f.new_code.replace(/\r\n/g, '\n').split('\n') : [];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: '#050709', display: 'flex', flexDirection: 'column' }}>
            {/* Top bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', height: 48, background: '#090c12', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
              <button onClick={() => setFeaturePreviewOpen(false)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#7a93bc', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                ← Close
              </button>
              <div style={{ fontSize: 12, color: '#3d5273', fontFamily: 'monospace' }}>
                Generated Feature
                <span style={{ margin: '0 6px', color: '#1d2a3a' }}>/</span>
                <span style={{ color: '#7a93bc' }}>{featureRepo?.github_repo || 'repo'}</span>
                {featureRepo?.github_branch && <span style={{ color: '#3d5273' }}> @ {featureRepo.github_branch}</span>}
              </div>
              <div style={{ flex: 1 }} />
              {!featureCommitted ? (
                <button
                  onClick={commitFeatureRepoWide}
                  disabled={featureCommitting}
                  style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: featureCommitting ? '#141c27' : '#00c98d', color: featureCommitting ? '#3d5273' : '#000', fontSize: 11, fontWeight: 700, cursor: featureCommitting ? 'default' : 'pointer' }}>
                  {featureCommitting ? 'Committing…' : `✅ Commit to ${featureRepo?.github_branch || 'branch'}`}
                </button>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#00c98d' }}>✓ Committed</span>
              )}
              <div style={{ padding: '3px 10px', borderRadius: 5, background: 'rgba(0,201,141,0.1)', border: '1px solid rgba(0,201,141,0.25)', fontSize: 11, fontWeight: 700, color: '#00c98d', fontFamily: 'monospace' }}>
                {featureFiles.length} file{featureFiles.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Body */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              {/* Left: file list */}
              <div className="sh-bf-detail-scroll" style={{ width: 280, flexShrink: 0, background: '#090c12', borderRight: '1px solid rgba(255,255,255,0.06)', overflowY: 'auto', padding: '8px 6px' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.1em', padding: '4px 8px 8px' }}>FILES</div>
                {featureFiles.map((ff, i) => {
                  const active = i === Math.min(featurePreviewIdx, featureFiles.length - 1);
                  return (
                    <div key={i} onClick={() => { setFeaturePreviewIdx(i); setFeaturePreviewTab('code'); }}
                      style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 2, background: active ? '#141c27' : 'transparent', border: `1px solid ${active ? 'rgba(59,127,255,0.25)' : 'transparent'}` }}>
                      <div style={{ fontSize: 12, color: active ? '#dce8ff' : '#adc4e6', fontWeight: active ? 600 : 400, wordBreak: 'break-all' }}>📄 {base(ff.file_path)}</div>
                      <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 2 }}>{ff.file_path}</div>
                    </div>
                  );
                })}
              </div>

              {/* Right: viewer */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                  <code style={{ fontSize: 12, color: '#dce8ff', wordBreak: 'break-all', flex: 1 }}>{f.file_path}</code>
                  <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, overflow: 'hidden' }}>
                    {['code', 'diff'].map(t => (
                      <button key={t} onClick={() => setFeaturePreviewTab(t)}
                        disabled={t === 'code' && !hasCode}
                        style={{ padding: '4px 12px', fontSize: 11, fontWeight: 700, border: 'none', cursor: (t === 'code' && !hasCode) ? 'default' : 'pointer', background: tab === t ? 'rgba(59,127,255,0.25)' : 'transparent', color: tab === t ? '#6aa3ff' : (t === 'code' && !hasCode) ? '#3d5273' : '#7a93bc' }}>
                        {t === 'code' ? 'Code' : 'Diff'}
                      </button>
                    ))}
                  </div>
                  {featureCommitted && f.commit_url && (
                    <a href={f.commit_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff', textDecoration: 'none', whiteSpace: 'nowrap' }}>View commit →</a>
                  )}
                </div>
                {f.reasoning && (
                  <div style={{ fontSize: 11, color: '#7a93bc', padding: '8px 16px', lineHeight: 1.6, borderBottom: '1px solid rgba(255,255,255,0.04)', flexShrink: 0 }}>{f.reasoning}</div>
                )}
                <div className="sh-bf-detail-scroll" style={{ flex: 1, overflow: 'auto', background: '#06080f' }}>
                  {tab === 'code' ? (
                    <pre style={{ margin: 0, padding: '12px 16px', fontSize: 12, lineHeight: 1.55, fontFamily: 'monospace', color: '#c9d6ea', whiteSpace: 'pre' }}>
                      {codeLines.map((ln, li) => (
                        <div key={li} style={{ display: 'flex' }}>
                          <span style={{ width: 44, flexShrink: 0, textAlign: 'right', paddingRight: 12, color: '#2d3d52', userSelect: 'none' }}>{li + 1}</span>
                          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ln || ' '}</span>
                        </div>
                      ))}
                    </pre>
                  ) : (
                    <pre style={{ margin: 0, padding: '12px 16px', fontSize: 12, lineHeight: 1.55, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {(Array.isArray(f.diff) ? f.diff : []).map((line, li) => {
                        const c = line.startsWith('+') ? '#00c98d' : line.startsWith('-') ? '#ff6b6b' : '#7a93bc';
                        return <div key={li} style={{ color: c }}>{line || ' '}</div>;
                      })}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* FULL IDE VIEW                                           */}
      {/* ═══════════════════════════════════════════════════════ */}
      {bfResults && showIde && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: '#050709' }}>

          {/* ── IDE Top Bar ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', height: 48, background: '#090c12', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
            {/* Back to chat */}
            <button onClick={() => setShowIde(false)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#7a93bc', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              ← Chat
            </button>
            {onGoToNewIntegrations && (
              <button onClick={onGoToNewIntegrations}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, background: 'rgba(0,201,141,0.1)', border: '1px solid rgba(0,201,141,0.3)', color: '#00c98d', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                🌱 New Integrations
              </button>
            )}
            {/* Breadcrumb */}
            <div style={{ fontSize: 12, color: '#3d5273', fontFamily: 'monospace', flexShrink: 0 }}>
              Brownfield
              <span style={{ margin: '0 6px', color: '#1d2a3a' }}>/</span>
              <span style={{ color: '#7a93bc' }}>{bfResults.detected_language || 'scan'}</span>
            </div>
            <div style={{ flex: 1 }} />
            {/* Stats chips */}
            {[
              { label: `${scannedCount} files`,        col: '#dce8ff' },
              { label: `avg ${avgScore}%`,              col: scoreColor(avgScore) },
              { label: `${totalErrors} errors`,         col: totalErrors > 0 ? '#ff4f4f' : '#3d5273' },
              { label: `${totalWarnings} warnings`,     col: totalWarnings > 0 ? '#f5a623' : '#3d5273' },
            ].map((c, ci) => (
              <div key={ci} style={{ padding: '3px 10px', borderRadius: 5, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', fontSize: 11, fontWeight: 700, color: c.col, fontFamily: 'monospace' }}>
                {c.label}
              </div>
            ))}
            {/* New analysis */}
            <button onClick={handleBfNewSession}
              style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(59,127,255,0.12)', border: '1px solid rgba(59,127,255,0.3)', color: '#6aa3ff', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
              + New Analysis
            </button>
          </div>

          {/* ── IDE Body ── */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

            {/* ── LEFT: File Explorer ── */}
            <div style={{ width: 260, flexShrink: 0, background: '#090c12', borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Section label + filter */}
              <div style={{ padding: '10px 12px 6px', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 8 }}>EXPLORER</div>
                <div style={{ display: 'flex', gap: 3 }}>
                  {['all', 'error', 'pass'].map(f => (
                    <div key={f} onClick={() => setFilter(f)}
                      style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 5, cursor: 'pointer', fontFamily: 'monospace', background: filter === f ? 'rgba(59,127,255,0.15)' : 'transparent', color: filter === f ? '#3b7fff' : '#3d5273', border: `1px solid ${filter === f ? 'rgba(59,127,255,0.3)' : 'transparent'}` }}>
                      {f.toUpperCase()}
                    </div>
                  ))}
                  <div style={{ flex: 1 }} />
                  <div style={{ fontSize: 9, color: '#3d5273', fontFamily: 'monospace', paddingTop: 4 }}>{filtered.length}/{totalCount}</div>
                </div>
              </div>

              {/* File list */}
              <div className="sh-bf-detail-scroll" style={{ flex: 1, overflowY: 'auto', padding: '4px 6px' }}>
                {filtered.map((r, idx) => {
                  const isActive = selected?.flow_name === r.flow_name;
                  const errCount = r.violations?.filter(v => v.severity === 'error').length || 0;
                  const warnCount = r.violations?.filter(v => v.severity === 'warning').length || 0;
                  return (
                    <div key={`${r.flow_name}-${idx}`}
                      onClick={() => { setSelected(r); setPanelTab('violations'); setCommitUrl(null); setCodeUpdated(false); setUpdatedDownloadUrl(null); setFeatureResult(null); setFeatureCommitUrl(null); setRequirement(''); setCopied(false); setSelectedViolations(new Set()); setSelectiveFixResult(null); setSelectiveCommitUrl(null); setDocResult(null); }}
                      style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 2, background: isActive ? '#141c27' : 'transparent', border: `1px solid ${isActive ? 'rgba(59,127,255,0.25)' : 'transparent'}`, transition: 'all 0.12s' }}>

                      {/* File name row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: r.status === 'scanned' ? 5 : 0 }}>
                        <span style={{ fontSize: 12, flexShrink: 0 }}>{r.status === 'no_code' ? '📄' : r.score === 100 ? '✅' : errCount > 0 ? '❌' : '⚠️'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 500, color: isActive ? '#dce8ff' : '#adc4e6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.flow_name}
                          </div>
                          {r.file_path && (
                            <div style={{ fontSize: 9, color: '#2a3b52', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.file_path}
                            </div>
                          )}
                        </div>
                        {r.status === 'scanned' && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: scoreColor(r.score), fontFamily: 'monospace', flexShrink: 0 }}>{r.score}%</div>
                        )}
                      </div>

                      {r.status === 'scanned' && (
                        <>
                          {/* Score bar */}
                          <div style={{ height: 3, background: '#0e1219', borderRadius: 2, overflow: 'hidden', marginBottom: 4 }}>
                            <div style={{ width: `${r.score}%`, height: '100%', background: scoreColor(r.score), borderRadius: 2 }} />
                          </div>
                          {/* Badges */}
                          <div style={{ display: 'flex', gap: 4 }}>
                            {errCount > 0 && (
                              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(255,79,79,0.12)', color: '#ff4f4f', fontFamily: 'monospace', fontWeight: 700 }}>{errCount} err</span>
                            )}
                            {warnCount > 0 && (
                              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(245,166,35,0.1)', color: '#f5a623', fontFamily: 'monospace', fontWeight: 700 }}>{warnCount} warn</span>
                            )}
                            {errCount === 0 && warnCount === 0 && (
                              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(0,201,141,0.1)', color: '#00c98d', fontFamily: 'monospace', fontWeight: 700 }}>clean</span>
                            )}
                          </div>
                        </>
                      )}
                      {r.status === 'no_code' && (
                        <div style={{ fontSize: 9, color: '#3d5273', fontFamily: 'monospace' }}>no file found</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── RIGHT: Detail Panel ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#050709' }}>
              {!selected ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14 }}>
                  <div style={{ fontSize: 48 }}>🔬</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#dce8ff' }}>Select a file to review</div>
                  <div style={{ fontSize: 12, color: '#3d5273', fontFamily: 'monospace', textAlign: 'center', maxWidth: 360, lineHeight: 1.8 }}>
                    Click any file in the explorer to inspect violations,<br />apply a full rewrite, or add a new feature.
                  </div>
                </div>
              ) : (
                <>
                  {/* File header */}
                  <div style={{ padding: '14px 24px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, background: '#070a0f' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#dce8ff', letterSpacing: '-0.02em' }}>{selected.flow_name}</div>
                        <div style={{ fontSize: 10, color: '#2a3b52', fontFamily: 'monospace', marginTop: 3 }}>{selected.file_path || 'No file found'}</div>
                      </div>
                      {selected.status === 'scanned' && (
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 28, fontWeight: 800, color: scoreColor(selected.score), fontFamily: 'monospace', lineHeight: 1 }}>{selected.score}%</div>
                          <div style={{ fontSize: 9, color: '#3d5273', fontFamily: 'monospace', marginTop: 2, letterSpacing: '0.06em' }}>COMPLIANCE</div>
                        </div>
                      )}
                    </div>
                    {selected.status === 'scanned' && (
                      <div style={{ display: 'flex', gap: 2 }}>
                        {[
                          { id: 'violations', label: `Violations`, count: selected.violations?.filter(v => v.severity !== 'pass').length || 0 },
                          { id: 'rewrite',    label: 'Full Rewrite', count: null },
                          { id: 'feature',    label: 'Add Feature',  count: null },
                        ].map(t => (
                          <div key={t.id} onClick={() => setPanelTab(t.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '8px 16px', cursor: 'pointer', borderBottom: `2px solid ${panelTab === t.id ? '#3b7fff' : 'transparent'}`, color: panelTab === t.id ? '#3b7fff' : '#3d5273', transition: 'all 0.12s' }}>
                            {t.label}
                            {t.count !== null && t.count > 0 && (
                              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: panelTab === t.id ? 'rgba(59,127,255,0.2)' : 'rgba(255,255,255,0.06)', color: panelTab === t.id ? '#6aa3ff' : '#7a93bc', fontFamily: 'monospace' }}>
                                {t.count}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Tab content */}
                  <div className="sh-bf-detail-scroll" style={{ flex: 1, overflowY: 'scroll', overflowX: 'hidden', padding: '20px 24px' }}>

                    {/* violations tab content — reused from below */}
                    {panelTab === 'violations' && (
                      selected.status === 'no_code' ? (
                        <div style={{ padding: '20px', background: '#0e1219', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', color: '#3d5273', fontFamily: 'monospace', fontSize: 12 }}>
                          No XML file found in GitHub for this flow.
                        </div>
                      ) : (
                        <>
                          {selected.summary && (
                            <div style={{ padding: '10px 14px', background: '#0e1219', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 16, fontSize: 12, color: '#7a93bc' }}>
                              {selected.summary}
                            </div>
                          )}
                          {errorViolations.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '10px 14px', background: '#0e1219', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
                              <div style={{ fontSize: 11, color: '#7a93bc', flex: 1 }}>
                                {selectedErrorCount > 0
                                  ? <span><strong style={{ color: '#dce8ff' }}>{selectedErrorCount}</strong> violation{selectedErrorCount > 1 ? 's' : ''} selected</span>
                                  : 'Click a violation to select it for a targeted fix'}
                              </div>
                              <button onClick={selectAllErrors}
                                style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(59,127,255,0.15)', border: '1px solid rgba(59,127,255,0.35)', color: '#6aa3ff', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                Select All Errors
                              </button>
                              {selectedErrorCount > 0 && (
                                <button onClick={clearSelection}
                                  style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', color: '#aac0dc', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                  Clear
                                </button>
                              )}
                            </div>
                          )}
                          <div style={{ marginBottom: 10, fontSize: 11, fontWeight: 700, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.06em' }}>
                            ALL RULES — {selected.violations?.length || 0} CHECKS
                          </div>
                          {selected.violations?.map((v, i) => {
                            const isSelectable = v.severity === 'error' || v.severity === 'warning';
                            const isSelected = selectedViolations.has(i);
                            return (
                              <div key={i} onClick={() => isSelectable && toggleViolation(i)}
                                style={{ display: 'flex', gap: 10, padding: '10px 14px', background: isSelected ? `${SEV_COLOR[v.severity]}08` : '#0e1219', borderRadius: 8, border: `1px solid ${isSelected ? (SEV_COLOR[v.severity] || '#7a93bc') + '40' : v.severity === 'error' ? 'rgba(255,79,79,0.15)' : v.severity === 'warning' ? 'rgba(245,166,35,0.12)' : 'rgba(0,201,141,0.1)'}`, marginBottom: 6, cursor: isSelectable ? 'pointer' : 'default', transition: 'all 0.15s' }}>
                                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', paddingTop: 1 }}>
                                  {isSelectable ? (
                                    <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${isSelected ? (SEV_COLOR[v.severity] || '#7a93bc') : 'rgba(255,255,255,0.15)'}`, background: isSelected ? (SEV_COLOR[v.severity] || '#7a93bc') : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
                                      {isSelected && <div style={{ width: 8, height: 8, background: '#fff', borderRadius: 2 }} />}
                                    </div>
                                  ) : <div style={{ width: 16, height: 16 }} />}
                                </div>
                                <div style={{ fontSize: 14, flexShrink: 0 }}>{SEV_ICON[v.severity] || '•'}</div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: SEV_COLOR[v.severity] || '#7a93bc', marginBottom: 3 }}>{v.rule}</div>
                                  <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.6 }}>{v.detail}</div>
                                </div>
                                <div style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${SEV_COLOR[v.severity] || '#7a93bc'}18`, color: SEV_COLOR[v.severity] || '#7a93bc', fontFamily: 'monospace', flexShrink: 0, height: 'fit-content' }}>
                                  {(v.severity || 'info').toUpperCase()}
                                </div>
                              </div>
                            );
                          })}
                          {selectedErrorCount > 0 && !selectiveFixResult && (
                            <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 10 }}>
                              <div style={{ fontSize: 12, color: '#7a93bc', marginBottom: 10 }}>
                                Fix {selectedErrorCount} selected violation{selectedErrorCount > 1 ? 's' : ''} only — other violations will remain unchanged.
                              </div>
                              <button onClick={fixSelectedViolations} disabled={applyingSelective}
                                style={{ width: '100%', padding: '10px', borderRadius: 8, background: applyingSelective ? '#141c27' : '#f5a623', color: applyingSelective ? '#3d5273' : '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: applyingSelective ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                                {applyingSelective ? '⏳ Fixing selected violations...' : `🎯 Fix ${selectedErrorCount} Selected Violation${selectedErrorCount > 1 ? 's' : ''}`}
                              </button>
                            </div>
                          )}
                          {selectiveFixResult && (
                            <div style={{ marginTop: 16 }}>
                              {selectiveFixResult.error ? (
                                <div style={{ padding: '12px', background: 'rgba(255,79,79,0.08)', borderRadius: 8, color: '#ff4f4f', fontSize: 12 }}>
                                  ❌ {selectiveFixResult.error}
                                </div>
                              ) : (
                                <SelectiveFixPreview
                                  selectiveFixResult={selectiveFixResult}
                                  selectedErrorCount={selectedErrorCount}
                                  selectiveCommitUrl={selectiveCommitUrl}
                                  applyingSelective={applyingSelective}
                                  copied={copied}
                                  copyCode={copyCode}
                                  downloadCode={downloadCode}
                                  flowName={selected.flow_name}
                                  commitSelectiveFix={commitSelectiveFix}
                                  onReject={() => { setSelectiveFixResult(null); setSelectiveCommitUrl(null); }}
                                  onDocumentIt={() => {
                                    setDocFixPayload({ commitUrl: selectiveCommitUrl, filePath: selected?.file_path, fixType: 'selective', scoreBefore: scoreBeforeFixRef.current ?? selected?.score, scoreAfter: selected?.score, violationsFixed: [...selectedViolations].map(i => selected?.violations?.[i]).filter(Boolean) });
                                    setDocResult(null);
                                    setShowDocModal(true);
                                  }}
                                  docResult={docResult}
                                />
                              )}
                            </div>
                          )}
                          {selectedErrorCount === 0 && !selectiveFixResult && selected.fixed_code && (
                            <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(59,127,255,0.06)', border: '1px solid rgba(59,127,255,0.15)', borderRadius: 10 }}>
                              <div style={{ fontSize: 12, color: '#7a93bc', marginBottom: 8 }}>Ready to fix all violations? Switch to the Full Rewrite tab.</div>
                              <button onClick={() => setPanelTab('rewrite')}
                                style={{ padding: '8px 16px', borderRadius: 7, background: '#3b7fff', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                🔧 Go to Full Rewrite →
                              </button>
                            </div>
                          )}
                        </>
                      )
                    )}

                    {/* rewrite tab */}
                    {panelTab === 'rewrite' && (
                      !selected.fixed_code ? (
                        <div style={{ padding: '20px', background: '#0e1219', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', color: '#3d5273', fontFamily: 'monospace', fontSize: 12, textAlign: 'center' }}>
                          No rewrite available. Re-run the scan to generate a full rewrite.
                        </div>
                      ) : (
                        <>
                          <div style={{ padding: '12px 16px', background: 'rgba(0,201,141,0.06)', border: '1px solid rgba(0,201,141,0.15)', borderRadius: 10, marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#00c98d', marginBottom: 6 }}>🔧 Full Rewrite</div>
                            <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.6 }}>
                              The AI has rewritten this file fixing every violation. Review the diff, then copy, download, or commit directly to GitHub.
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                            <button onClick={() => copyCode(selected.fixed_code)}
                              style={{ flex: 1, padding: '9px', borderRadius: 8, background: copied ? 'rgba(0,201,141,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${copied ? 'rgba(0,201,141,0.3)' : 'rgba(255,255,255,0.1)'}`, color: copied ? '#00c98d' : '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace', transition: 'all 0.2s' }}>
                              {copied ? '✅ Copied!' : '📋 Copy Rewritten Code'}
                            </button>
                            <button onClick={() => downloadCode(selected.fixed_code, `${selected.flow_name}-rewritten.xml`)}
                              style={{ flex: 1, padding: '9px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace' }}>
                              ⬇️ Download
                            </button>
                          </div>
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 10 }}>
                              DIFF — CHANGES MADE <span style={{ fontWeight: 400, color: '#1d2a3a' }}>red = removed · green = added</span>
                            </div>
                            <DiffView diff={selected.diff || []} />
                          </div>
                          {applyError && (
                            <div style={{ padding: '10px 14px', background: 'rgba(255,79,79,0.08)', border: '1px solid rgba(255,79,79,0.2)', borderRadius: 8, marginBottom: 12, fontSize: 11, color: '#ff4f4f', fontFamily: 'monospace' }}>
                              ❌ {featureRepo?.source === 'upload' ? 'Update' : 'Commit'} failed: {applyError}
                            </div>
                          )}
                          {(featureRepo?.source === 'upload' ? !codeUpdated : !commitUrl) && (
                            <div style={{ padding: '14px 16px', background: 'rgba(59,127,255,0.06)', border: '1px solid rgba(59,127,255,0.15)', borderRadius: 10 }}>
                              <div style={{ fontSize: 11, color: '#7a93bc', marginBottom: 12, lineHeight: 1.5 }}>
                                Review the diff above carefully. Nothing is {featureRepo?.source === 'upload' ? 'changed' : 'committed'} until you click below.
                              </div>
                              <button onClick={applyFix} disabled={applying}
                                style={{ width: '100%', padding: '11px', borderRadius: 9, background: applying ? '#141c27' : '#3b7fff', color: applying ? '#3d5273' : '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: applying ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                                {applying
                                  ? (featureRepo?.source === 'upload' ? '⏳ Updating...' : '⏳ Committing...')
                                  : (featureRepo?.source === 'upload' ? '🔧 Update Code' : '🚀 Approve & Commit to GitHub')}
                              </button>
                            </div>
                          )}
                          {(featureRepo?.source === 'upload' ? codeUpdated : commitUrl) && (
                            <div style={{ padding: '16px', background: 'rgba(0,201,141,0.08)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 10, marginTop: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#00c98d', marginBottom: 8 }}>{featureRepo?.source === 'upload' ? '✅ Code Updated' : '✅ Committed to GitHub'}</div>
                              {commitUrl && (
                                <a href={commitUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace', display: 'block' }}>
                                  🔗 View commit →
                                </a>
                              )}
                              {featureRepo?.source === 'upload' && updatedDownloadUrl && (
                                <a href={updatedDownloadUrl} style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace', display: 'block' }}>
                                  ⬇ Download updated project →
                                </a>
                              )}
                              {commitUrl && !docResult && (
                                <button onClick={() => {
                                  setDocFixPayload({ commitUrl, filePath: selected?.file_path, fixType: 'full', scoreBefore: scoreBeforeFixRef.current ?? selected?.score, scoreAfter: 100, violationsFixed: selected?.violations?.filter(v => v.severity !== 'pass') });
                                  setDocResult(null);
                                  setShowDocModal(true);
                                }}
                                  style={{ marginTop: 10, width: '100%', padding: '8px', borderRadius: 8, background: 'rgba(59,127,255,0.12)', border: '1px solid rgba(59,127,255,0.25)', color: '#6aa3ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  📄 Document It
                                </button>
                              )}
                              {docResult?.error && <div style={{ marginTop: 8, fontSize: 11, color: '#ff4f4f' }}>⛔ {docResult.error}</div>}
                              {docResult?.title && !docResult.error && (
                                <div style={{ marginTop: 8, fontSize: 11, color: '#00c98d' }}>
                                  ✓ Documented in <strong>{docResult.title}</strong>
                                  {docResult.url && <> · <a href={docResult.url} target="_blank" rel="noreferrer" style={{ color: '#3b7fff' }}>View page →</a></>}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )
                    )}

                    {/* add feature tab */}
                    {panelTab === 'feature' && (
                      <>
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#dce8ff', marginBottom: 4 }}>✨ Add a New Feature</div>
                          <div style={{ fontSize: 12, color: '#3d5273', fontFamily: 'monospace', lineHeight: 1.6, marginBottom: 16 }}>
                            Describe what you want added. The AI implements it following the exact style of your existing code.
                          </div>
                          <div style={{ marginBottom: 6, fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em' }}>REQUIREMENT</div>
                          <textarea
                            value={requirement}
                            onChange={e => { setRequirement(e.target.value); setFeatureResult(null); setFeatureCommitUrl(null); setFeatureCodeUpdated(false); setFeatureUpdateUrl(null); setCommitFeatureError(null); }}
                            placeholder="e.g. Add a Salesforce connector after the FTP write step that creates a Case record with the file name and processing timestamp."
                            style={{ width: '100%', fontSize: 11, padding: '8px 11px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.11)', background: '#0e1219', color: '#dce8ff', outline: 'none', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6, minHeight: 120 }}
                          />
                          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button onClick={generateFeature} disabled={generating || !requirement.trim()}
                              style={{ flex: 1, padding: '10px', borderRadius: 9, background: generating || !requirement.trim() ? '#141c27' : '#8b7cf8', color: generating || !requirement.trim() ? '#3d5273' : '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: generating || !requirement.trim() ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                              {generating ? '⏳ Implementing...' : '✨ Preview Generated Code'}
                            </button>
                            {(featureResult?.new_code || featureResult?.files?.length) && (
                              <button onClick={() => { setFeatureResult(null); setRequirement(''); setFeatureCommitUrl(null); setFeatureCodeUpdated(false); setFeatureUpdateUrl(null); setCommitFeatureError(null); }}
                                style={{ padding: '10px 14px', borderRadius: 9, background: 'transparent', color: '#3d5273', border: '1px solid rgba(255,255,255,0.08)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                        {featureResult && !featureResult.new_code && !featureResult.files?.length && (
                          <div style={{ padding: '12px', background: 'rgba(255,79,79,0.08)', borderRadius: 8, color: '#ff4f4f', fontSize: 12, marginBottom: 16 }}>
                            ❌ {featureResult.error || 'Feature generation failed.'}
                          </div>
                        )}
                        {featureResult && (featureResult.files?.length || featureResult.new_code) && (
                          <>
                            <div style={{ padding: '12px 16px', background: 'rgba(139,124,248,0.08)', border: '1px solid rgba(139,124,248,0.2)', borderRadius: 10, marginBottom: 16 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#8b7cf8', marginBottom: 6 }}>✨ Generated Feature</div>
                              <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.6 }}>Review the change{(featureResult.files?.length || 1) > 1 ? 's' : ''} below, then {featureRepo?.source === 'upload' ? 'update your local project' : 'commit to GitHub'}.</div>
                            </div>
                            {(featureResult.files?.length
                              ? featureResult.files
                              : [{ file_path: featureResult.file_path || selected.file_path, new_code: featureResult.new_code, diff: featureResult.diff, reasoning: featureResult.reasoning }]
                            ).map((f, fi) => (
                              <div key={fi} style={{ marginBottom: 18, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden' }}>
                                <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.03)' }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: '#dce8ff', fontFamily: 'monospace', wordBreak: 'break-all' }}>📄 {f.file_path}</span>
                                </div>
                                {f.reasoning && (
                                  <div style={{ padding: '8px 12px', fontSize: 11, color: '#7a93bc', lineHeight: 1.6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{f.reasoning}</div>
                                )}
                                <div style={{ display: 'flex', gap: 8, padding: '10px 12px' }}>
                                  <button onClick={() => copyCode(f.new_code)}
                                    style={{ flex: 1, padding: '7px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace' }}>
                                    📋 Copy
                                  </button>
                                  <button onClick={() => downloadCode(f.new_code, (f.file_path || 'file').split('/').pop())}
                                    style={{ flex: 1, padding: '7px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace' }}>
                                    ⬇️ Download
                                  </button>
                                </div>
                                <div style={{ padding: '0 12px 12px' }}>
                                  <DiffView diff={f.diff || []} />
                                </div>
                              </div>
                            ))}
                            {featureResult.projectUpdates?.length > 0 && (
                              <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(59,127,255,0.06)', border: '1px solid rgba(59,127,255,0.18)', borderRadius: 10 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#6aa3ff', marginBottom: 8, fontFamily: 'monospace', letterSpacing: '0.06em' }}>PROJECT / METADATA UPDATES ({featureResult.projectUpdates.length})</div>
                                {featureResult.projectUpdates.map((p, pi) => (
                                  <div key={pi} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 11, color: '#7a93bc', marginBottom: 4 }}>
                                    <span style={{ color: '#6aa3ff', flexShrink: 0 }}>•</span>
                                    <span><strong style={{ color: '#dce8ff', fontFamily: 'monospace' }}>{p.file_path}</strong>{p.reason ? ` — ${p.reason}` : ''}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(featureRepo?.source === 'upload' ? !featureCodeUpdated : !featureCommitUrl) ? (
                              <div style={{ padding: '14px 16px', background: 'rgba(139,124,248,0.06)', border: '1px solid rgba(139,124,248,0.2)', borderRadius: 10 }}>
                                <div style={{ fontSize: 11, color: '#7a93bc', marginBottom: 12, lineHeight: 1.5 }}>Review the diff above. Nothing is {featureRepo?.source === 'upload' ? 'changed' : 'committed'} until you approve.</div>
                                {commitFeatureError && (
                                  <div style={{ fontSize: 11, color: '#ff4f4f', marginBottom: 10 }}>❌ {featureRepo?.source === 'upload' ? 'Update' : 'Commit'} failed: {commitFeatureError}</div>
                                )}
                                <button onClick={commitFeature} disabled={committingFeature}
                                  style={{ width: '100%', padding: '11px', borderRadius: 9, background: committingFeature ? '#141c27' : '#8b7cf8', color: committingFeature ? '#3d5273' : '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: committingFeature ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                                  {committingFeature
                                    ? (featureRepo?.source === 'upload' ? '⏳ Updating...' : '⏳ Committing...')
                                    : (featureRepo?.source === 'upload' ? '🔧 Update Code' : '🚀 Approve & Commit Feature')}
                                </button>
                              </div>
                            ) : (
                              <div style={{ padding: '16px', background: 'rgba(0,201,141,0.08)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 10, marginTop: 8 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#00c98d', marginBottom: 8 }}>{featureRepo?.source === 'upload' ? '✅ Code Updated' : '✅ Feature Committed'}</div>
                                {featureRepo?.source === 'upload'
                                  ? (featureUpdateUrl && <a href={featureUpdateUrl} style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace', textDecoration: 'none' }}>⬇ Download updated project</a>)
                                  : (<a href={featureCommitUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace' }}>🔗 View commit →</a>)}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}

                  </div>
                  {/* end tab content */}
                </>
              )}
            </div>
            {/* end right detail */}

          </div>
          {/* end IDE body */}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* CHAT VIEW (shown when no results or IDE closed, and no standard review open) */}
      {/* ═══════════════════════════════════════════════════════ */}
      {(!bfResults || !showIde) && stdModalIdx === null && (
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* ── Left: chat messages + input bar ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, borderRight: '1px solid rgba(255,255,255,0.08)' }}>

          {onGoToNewIntegrations && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
              <button onClick={onGoToNewIntegrations}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, background: 'rgba(0,201,141,0.1)', border: '1px solid rgba(0,201,141,0.3)', color: '#00c98d', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                ← New Integrations
              </button>
            </div>
          )}

          {/* Messages scroll area */}
          <div className="sh-bf-messages" style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', minHeight: 0 }}>

            {/* Welcome message — always visible */}
            <div className="sh-msg bot">
              <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
              <div className="sh-msg-body">
                <div className="sh-msg-bubble">
                  👋 Hi! I'm the <strong>Compliance & Enhancement Agent</strong>. I can review your existing codebase against your best practices, or add a new feature to it.
                  {bfMode == null && !promptGiven && !setupDone && <><br /><br />What would you like to do?</>}
                  {bfMode === 'compliance' && !promptGiven && !setupDone && <><br /><br />What would you like me to analyze? Describe your project or just say "analyze my repo" to get started.</>}
                </div>
              </div>
            </div>

            {/* ── Start-mode chooser — pick Add Feature or Compliance Review ── */}
            {bfMode == null && !promptGiven && !setupDone && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => { setBfMode('addfeature'); setElicitStep('repo_details'); }}
                      style={{ padding: '8px 18px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#dce8ff', transition: 'all 0.15s' }}>
                      🛠 Add a Feature
                    </button>
                    <button
                      onClick={() => {
                        setBfMode('compliance');
                        // Kick off the elicitation immediately so the bot responds with the
                        // standard-selection question — otherwise the user is left staring at
                        // the empty chooser and has to type to proceed.
                        setBfMessages(prev => [...prev, { role: 'user', content: 'Compliance, Standards & Business Logic Testing review' }]);
                        callAnalyze('I want to run a compliance and standards review on my repository.');
                      }}
                      style={{ padding: '8px 18px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#dce8ff', transition: 'all 0.15s' }}>
                      ✅ Compliance, Standards &amp; Business Logic Testing
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Stale session warning */}
            {showBfStaleBanner && (
              <StaleBanner
                lastActive={bfLastActive}
                onNewSession={() => { handleBfNewSession(); setShowBfStaleBanner(false); }}
                onDismiss={() => setShowBfStaleBanner(false)}
              />
            )}

            {/* Chat messages */}
            {bfMessages.map((msg, i) => {
              if (msg.returnDeploy) {
                return (
                  <div key={i} className="sh-msg bot">
                    <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                    <div className="sh-msg-body">
                      <div className="sh-msg-bubble">{msg.content}</div>
                      <button
                        onClick={() => { if (onReturnToDeployment) onReturnToDeployment(); }}
                        style={{ marginTop: 8, padding: '8px 16px', borderRadius: 8, background: '#00c98d', color: '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        🚀 Proceed to Deployment
                      </button>
                    </div>
                  </div>
                );
              }
              if (msg.type === 'standard_review') {
                // Extract first heading or first meaningful line as title
                const firstLine = (msg.preview || '').split('\n').find(l => l.trim());
                const title = firstLine ? firstLine.replace(/^\*\*|\*\*$/g, '').trim() : 'Standard';
                return (
                  <div key={i} className="sh-msg bot">
                    <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                    <div className="sh-msg-body">
                      <div className="sh-msg-bubble" style={{ minWidth: 280, opacity: msg.superseded ? 0.45 : 1, transition: 'opacity 0.2s' }}>
                        {/* Compact header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: msg.superseded ? '#3d5273' : '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.08em', fontWeight: 700 }}>
                            STANDARD REVIEW
                          </div>
                          {msg.superseded && (
                            <div style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.06em' }}>
                              SUPERSEDED
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: msg.superseded ? '#3d5273' : '#dce8ff', marginBottom: 4 }}>Summary</div>
                        {title && <div style={{ fontSize: 11, color: msg.superseded ? '#2d3d52' : '#7a93bc', marginBottom: 12 }}>{title}</div>}

                        {msg.superseded ? (
                          /* ── Superseded state ── */
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
                            <span style={{ fontSize: 11, color: '#3d5273' }}>↩ Replaced by revised version below</span>
                            <button onClick={() => setStdModalIdx(i)}
                              style={{ marginLeft: 'auto', fontSize: 11, color: '#3d5273', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                              View
                            </button>
                          </div>
                        ) : msg.accepted ? (
                          /* ── Accepted state ── */
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(0,201,141,0.08)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 8 }}>
                            <span>✓</span>
                            <span style={{ fontSize: 12, color: '#00c98d', fontWeight: 600 }}>Standard locked in</span>
                            <button onClick={() => setStdModalIdx(i)}
                              style={{ marginLeft: 'auto', fontSize: 11, color: '#3b7fff', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                              View →
                            </button>
                          </div>
                        ) : (
                          /* ── Pending review ── */
                          <>
                            <button onClick={() => setStdModalIdx(i)}
                              style={{ width: '100%', padding: '9px', borderRadius: 8, background: 'rgba(59,127,255,0.12)', border: '1px solid rgba(59,127,255,0.3)', color: '#6aa3ff', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 8 }}>
                              📋 Open standard for review →
                            </button>
                            <button
                              onClick={() => {
                                setBfMessages(prev => prev.map((m, mi) => mi === i ? { ...m, accepted: true } : m));
                                callAnswer('accept');
                              }}
                              style={{ width: '100%', padding: '9px', borderRadius: 8, background: '#00c98d', color: '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 8 }}>
                              ✓ Accept this standard
                            </button>
                            <div style={{ fontSize: 11, color: '#7a93bc', textAlign: 'center' }}>
                              ✏️ Or type changes in the input below
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              if (msg.type === 'options') {
                const OPTIONS = [
                  { value: 'A', label: 'Confluence',        icon: '📋' },
                  { value: 'B', label: 'Upload document',   icon: '📄' },
                  { value: 'C', label: 'Intelog Standard', icon: '🏢' },
                  ...((bfOrgStandards || []).length > 0
                    ? [{ value: 'D', label: 'Org Standard', icon: '🏛️' }]
                    : []),
                ];
                return (
                  <div key={i} className="sh-msg bot">
                    <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                    <div className="sh-msg-body">
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {OPTIONS.map(opt => (
                          <button key={opt.value}
                            onClick={() => {
                              if (msg.selected != null) return;
                              setBfMessages(prev => prev.map((m, mi) => mi === i ? { ...m, selected: opt.value } : m));
                              setBfStandardType(opt.value);
                              if (opt.value === 'D') {
                                setElicitStep('org_standard_selection');
                                return;
                              }
                              setElicitStep(null);
                              callAnswer(opt.value);
                            }}
                            disabled={msg.selected != null}
                            style={{
                              padding: '8px 18px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                              cursor: msg.selected != null ? 'default' : 'pointer',
                              background: msg.selected === opt.value ? 'rgba(59,127,255,0.25)' : 'rgba(255,255,255,0.05)',
                              border: msg.selected === opt.value ? '1px solid rgba(59,127,255,0.6)' : '1px solid rgba(255,255,255,0.12)',
                              color: msg.selected === opt.value ? '#3b7fff' : '#dce8ff',
                              transition: 'all 0.15s',
                            }}>
                            {opt.icon} {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }


              return msg.role === 'user' ? (
                <div key={i} className="sh-msg user">
                  <div className="sh-msg-avatar" style={{ background: 'rgba(99,120,150,0.15)', border: '1px solid rgba(99,120,150,0.2)', fontSize: 14 }}>👤</div>
                  <div className="sh-msg-body">
                    <div className="sh-msg-bubble" style={{ background: '#0f766e', border: 'none', color: '#ffffff' }}>{msg.content}</div>
                  </div>
                </div>
              ) : (
                <div key={i} className="sh-msg bot">
                  <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                  <div className="sh-msg-body">
                    {msg.isLogBlock ? (
                      <div className="sh-msg-bubble sh-bf-log-bubble">
                        {msg.logs.map((line, j) => (
                          <div key={j} className={`sh-bf-log-line${j === msg.logs.length - 1 ? ' sh-bf-log-line--active' : ''}`}>
                            <span className="sh-bf-log-dot" />
                            {line}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="sh-msg-bubble">
                        {formatBotMessage(msg.content)}
                        {msg.streaming && <span className="sh-typing-dot" />}
                        {/skip/i.test(msg.content) && !msg.skipped && (
                          <button
                            onClick={() => {
                              setBfMessages(prev => prev.map((m, mi) =>
                                mi === i ? { ...m, skipped: true } : m
                              ));
                              setBfMessages(prev => [...prev, { role: 'user', content: 'skip' }]);
                              callAnswer('skip');
                            }}
                            style={{
                              display: 'inline-block', marginTop: 10,
                              background: 'rgba(255,255,255,0.06)',
                              border: '1px solid rgba(255,255,255,0.12)',
                              color: '#7a93bc', borderRadius: 6,
                              padding: '5px 14px', fontSize: 11,
                              fontWeight: 600, cursor: 'pointer',
                              letterSpacing: '0.03em',
                            }}
                          >
                            Skip →
                          </button>
                        )}
                        {msg.isNewSessionHint && (
                          <button
                            onClick={handleBfNewSession}
                            style={{
                              display: 'block', marginTop: 12,
                              background: '#0f766e', color: '#fff', border: 'none',
                              borderRadius: 6, padding: '7px 16px', fontSize: 12,
                              fontWeight: 600, cursor: 'pointer', letterSpacing: '0.03em',
                            }}
                          >
                            + New Session
                          </button>
                        )}
                        {msg.isError && (
                          <button
                            onClick={() => {
                              setBfMessages(prev => prev.map((m, mi) => mi === i ? { ...m, isError: false } : m));
                              setBfMessages(prev => [...prev, { role: 'user', content: '↻ Retrying scan…' }]);
                              callAnalyze(lastAnalyzeMessageRef.current);
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              marginTop: 12,
                              background: 'rgba(0,188,212,0.10)',
                              border: '1px solid rgba(0,188,212,0.35)',
                              color: '#00bcd4', borderRadius: 6,
                              padding: '6px 14px', fontSize: 11,
                              fontWeight: 600, cursor: 'pointer', letterSpacing: '0.03em',
                            }}
                          >
                            ↻ Retry Scan
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}


            {/* Results ready — show IDE button */}
            {bfResults && false && (
              <div style={{ marginTop: 8, marginBottom: 8, height: 600, display: 'grid', gridTemplateColumns: '260px 1fr', overflow: 'hidden', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' }}>

                {/* ── LEFT: file list ── */}
                <div style={{ background: '#090c12', borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 600 }}>

                  {/* summary stats + filter tabs */}
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 8 }}>
                      {[
                        { label: 'SCANNED FILES', val: scannedCount, col: '#dce8ff' },
                        { label: 'AVG SCORE',     val: `${avgScore}%`, col: scoreColor(avgScore) },
                      ].map((k, i) => (
                        <div key={i} style={{ background: '#0e1219', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '7px 10px' }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 3 }}>{k.label}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: k.col }}>{k.val}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {['all', 'error', 'pass'].map(f => (
                        <div key={f} onClick={() => setFilter(f)}
                          style={{ fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 5, cursor: 'pointer', fontFamily: 'monospace', background: filter === f ? 'rgba(59,127,255,0.15)' : 'transparent', color: filter === f ? '#3b7fff' : '#3d5273', border: `1px solid ${filter === f ? 'rgba(59,127,255,0.3)' : 'transparent'}` }}>
                          {f.toUpperCase()}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* file list */}
                  <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
                    {filtered.map((r, idx) => (
                      <div key={`${r.flow_name}-${r.file_path || idx}`}
                        onClick={() => { setSelected(r); setPanelTab('violations'); setCommitUrl(null); setCodeUpdated(false); setUpdatedDownloadUrl(null); setFeatureResult(null); setFeatureCommitUrl(null); setRequirement(''); setCopied(false); setSelectedViolations(new Set()); setSelectiveFixResult(null); setSelectiveCommitUrl(null); setDocResult(null); }}
                        style={{ padding: '9px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 3, background: selected?.flow_name === r.flow_name ? '#141c27' : 'transparent', border: `1px solid ${selected?.flow_name === r.flow_name ? 'rgba(255,255,255,0.11)' : 'transparent'}`, transition: 'all 0.15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: r.status === 'scanned' ? 4 : 0 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 500, color: '#dce8ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.flow_name}</div>
                          </div>
                          {r.status === 'scanned' && (
                            <div style={{ fontSize: 11, fontWeight: 700, color: scoreColor(r.score), fontFamily: 'monospace', flexShrink: 0 }}>{r.score}%</div>
                          )}
                        </div>
                        {r.status === 'scanned' && (
                          <>
                            <div style={{ height: 3, background: '#141c27', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ width: `${r.score}%`, height: '100%', background: scoreColor(r.score), borderRadius: 2 }} />
                            </div>
                            {r.violations?.filter(v => v.severity === 'error').length > 0 && (
                              <div style={{ fontSize: 9, color: '#ff4f4f', fontFamily: 'monospace', marginTop: 3 }}>
                                {r.violations.filter(v => v.severity === 'error').length} errors
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── RIGHT: detail panel ── */}
                <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#050709', height: 600 }}>
                  {!selected ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
                      <div style={{ fontSize: 36 }}>🔬</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#dce8ff' }}>Select a file to review</div>
                      <div style={{ fontSize: 12, color: '#3d5273', fontFamily: 'monospace', textAlign: 'center', maxWidth: 320, lineHeight: 1.8 }}>
                        Click any file in the list to see violations, the full rewrite, or to add a new feature.
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* file header + tabs */}
                      <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#dce8ff', letterSpacing: '-0.02em' }}>{selected.flow_name}</div>
                            <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', marginTop: 2 }}>{selected.file_path || 'No file found'}</div>
                          </div>
                          {selected.status === 'scanned' && (
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 24, fontWeight: 800, color: scoreColor(selected.score), fontFamily: 'monospace', lineHeight: 1 }}>{selected.score}%</div>
                              <div style={{ fontSize: 9, color: '#3d5273', fontFamily: 'monospace', marginTop: 2 }}>COMPLIANCE</div>
                            </div>
                          )}
                        </div>
                        {selected.status === 'scanned' && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                            {[
                              { id: 'violations', label: `❌ Violations (${selected.violations?.filter(v => v.severity !== 'pass').length || 0})` },
                              { id: 'rewrite',    label: '🔧 Full Rewrite' },
                              { id: 'feature',    label: '✨ Add Feature' },
                            ].map(t => (
                              <div key={t.id} onClick={() => setPanelTab(t.id)}
                                style={{ fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 7, cursor: 'pointer', border: `1px solid ${panelTab === t.id ? 'rgba(59,127,255,0.4)' : 'rgba(255,255,255,0.08)'}`, background: panelTab === t.id ? 'rgba(59,127,255,0.12)' : 'transparent', color: panelTab === t.id ? '#3b7fff' : '#3d5273', transition: 'all 0.15s' }}>
                                {t.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* tab content */}
                      <div className="sh-bf-detail-scroll" style={{ flex: 1, overflowY: 'scroll', overflowX: 'hidden', padding: '16px 20px' }}>

                {/* ── TAB: VIOLATIONS ── */}
                {panelTab === 'violations' && (
                  selected.status === 'no_code' ? (
                    <div style={{ padding: '20px', background: '#0e1219', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', color: '#3d5273', fontFamily: 'monospace', fontSize: 12 }}>
                      No XML file found in GitHub for this flow.
                    </div>
                  ) : (
                    <>
                      {selected.summary && (
                        <div style={{ padding: '10px 14px', background: '#0e1219', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 16, fontSize: 12, color: '#7a93bc' }}>
                          {selected.summary}
                        </div>
                      )}

                      {errorViolations.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '10px 14px', background: '#0e1219', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
                          <div style={{ fontSize: 11, color: '#7a93bc', flex: 1 }}>
                            {selectedErrorCount > 0
                              ? <span><strong style={{ color: '#dce8ff' }}>{selectedErrorCount}</strong> violation{selectedErrorCount > 1 ? 's' : ''} selected</span>
                              : 'Click a violation to select it, or use the buttons →'}
                          </div>
                          <button onClick={selectAllErrors}
                            style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(59,127,255,0.15)', border: '1px solid rgba(59,127,255,0.35)', color: '#6aa3ff', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
                            Select All Errors
                          </button>
                          {selectedErrorCount > 0 && (
                            <button onClick={clearSelection}
                              style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', color: '#aac0dc', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              Clear
                            </button>
                          )}
                        </div>
                      )}

                      <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 700, color: '#dce8ff' }}>
                        All Rules — {selected.violations?.length || 0} checks
                      </div>

                      {selected.violations?.map((v, i) => {
                        const isSelectable = v.severity === 'error' || v.severity === 'warning';
                        const isSelected = selectedViolations.has(i);
                        return (
                          <div key={i} onClick={() => isSelectable && toggleViolation(i)}
                            style={{ display: 'flex', gap: 10, padding: '10px 14px', background: isSelected ? `${SEV_COLOR[v.severity]}08` : '#0e1219', borderRadius: 8, border: `1px solid ${isSelected ? (SEV_COLOR[v.severity] || '#7a93bc') + '40' : v.severity === 'error' ? 'rgba(255,79,79,0.15)' : v.severity === 'warning' ? 'rgba(245,166,35,0.12)' : 'rgba(0,201,141,0.1)'}`, marginBottom: 6, cursor: isSelectable ? 'pointer' : 'default', transition: 'all 0.15s' }}>
                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', paddingTop: 1 }}>
                              {isSelectable ? (
                                <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${isSelected ? (SEV_COLOR[v.severity] || '#7a93bc') : 'rgba(255,255,255,0.15)'}`, background: isSelected ? (SEV_COLOR[v.severity] || '#7a93bc') : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
                                  {isSelected && <div style={{ width: 8, height: 8, background: '#fff', borderRadius: 2 }} />}
                                </div>
                              ) : <div style={{ width: 16, height: 16 }} />}
                            </div>
                            <div style={{ fontSize: 14, flexShrink: 0 }}>{SEV_ICON[v.severity] || '•'}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: SEV_COLOR[v.severity] || '#7a93bc', marginBottom: 3 }}>{v.rule}</div>
                              <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.6 }}>{v.detail}</div>
                            </div>
                            <div style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${SEV_COLOR[v.severity] || '#7a93bc'}18`, color: SEV_COLOR[v.severity] || '#7a93bc', fontFamily: 'monospace', flexShrink: 0, height: 'fit-content' }}>
                              {(v.severity || 'info').toUpperCase()}
                            </div>
                          </div>
                        );
                      })}

                      {selectedErrorCount > 0 && !selectiveFixResult && (
                        <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 10 }}>
                          <div style={{ fontSize: 12, color: '#7a93bc', marginBottom: 10 }}>
                            Fix {selectedErrorCount} selected violation{selectedErrorCount > 1 ? 's' : ''} only — other violations will remain unchanged.
                          </div>
                          <button onClick={fixSelectedViolations} disabled={applyingSelective}
                            style={{ width: '100%', padding: '10px', borderRadius: 8, background: applyingSelective ? '#141c27' : '#f5a623', color: applyingSelective ? '#3d5273' : '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: applyingSelective ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                            {applyingSelective ? '⏳ Fixing selected violations...' : `🎯 Fix ${selectedErrorCount} Selected Violation${selectedErrorCount > 1 ? 's' : ''}`}
                          </button>
                        </div>
                      )}

                      {selectiveFixResult && (
                        <div style={{ marginTop: 16 }}>
                          {selectiveFixResult.error ? (
                            <div style={{ padding: '12px', background: 'rgba(255,79,79,0.08)', borderRadius: 8, color: '#ff4f4f', fontSize: 12 }}>
                              ❌ {selectiveFixResult.error}
                            </div>
                          ) : (
                            <SelectiveFixPreview
                              selectiveFixResult={selectiveFixResult}
                              selectedErrorCount={selectedErrorCount}
                              selectiveCommitUrl={selectiveCommitUrl}
                              applyingSelective={applyingSelective}
                              copied={copied}
                              copyCode={copyCode}
                              downloadCode={downloadCode}
                              flowName={selected.flow_name}
                              commitSelectiveFix={commitSelectiveFix}
                              onReject={() => { setSelectiveFixResult(null); setSelectiveCommitUrl(null); }}
                              onDocumentIt={() => {
                                setDocFixPayload({ commitUrl: selectiveCommitUrl, filePath: selected?.file_path, fixType: 'selective', scoreBefore: scoreBeforeFixRef.current ?? selected?.score, scoreAfter: selected?.score, violationsFixed: [...selectedViolations].map(i => selected?.violations?.[i]).filter(Boolean) });
                                setDocResult(null);
                                setShowDocModal(true);
                              }}
                              docResult={docResult}
                            />
                          )}
                        </div>
                      )}

                      {selectedErrorCount === 0 && !selectiveFixResult && selected.fixed_code && (
                        <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(59,127,255,0.06)', border: '1px solid rgba(59,127,255,0.15)', borderRadius: 10 }}>
                          <div style={{ fontSize: 12, color: '#7a93bc', marginBottom: 8 }}>Ready to fix all violations? Switch to the Full Rewrite tab.</div>
                          <button onClick={() => setPanelTab('rewrite')}
                            style={{ padding: '8px 16px', borderRadius: 7, background: '#3b7fff', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            🔧 Go to Full Rewrite →
                          </button>
                        </div>
                      )}
                    </>
                  )
                )}

                {/* ── TAB: FULL REWRITE ── */}
                {panelTab === 'rewrite' && (
                  !selected.fixed_code ? (
                    <div style={{ padding: '20px', background: '#0e1219', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', color: '#3d5273', fontFamily: 'monospace', fontSize: 12, textAlign: 'center' }}>
                      No rewrite available. Re-run the scan to generate a full rewrite.
                    </div>
                  ) : (
                    <>
                      <div style={{ padding: '12px 16px', background: 'rgba(0,201,141,0.06)', border: '1px solid rgba(0,201,141,0.15)', borderRadius: 10, marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#00c98d', marginBottom: 6 }}>🔧 Full Rewrite — Based on Your Best Practice Document</div>
                        <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.6 }}>
                          The AI has rewritten this file fixing every violation. Review the diff, then copy or download the code — or commit directly to GitHub.
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <button onClick={() => copyCode(selected.fixed_code)}
                          style={{ flex: 1, padding: '9px', borderRadius: 8, background: copied ? 'rgba(0,201,141,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${copied ? 'rgba(0,201,141,0.3)' : 'rgba(255,255,255,0.1)'}`, color: copied ? '#00c98d' : '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace', transition: 'all 0.2s' }}>
                          {copied ? '✅ Copied!' : '📋 Copy Rewritten Code'}
                        </button>
                        <button onClick={() => downloadCode(selected.fixed_code, `${selected.flow_name}-rewritten.xml`)}
                          style={{ flex: 1, padding: '9px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace' }}>
                          ⬇️ Download {selected.flow_name}-rewritten.xml
                        </button>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#dce8ff', marginBottom: 10 }}>
                          Diff — Changes Made
                          <span style={{ fontSize: 10, color: '#3d5273', fontWeight: 400, marginLeft: 8 }}>Red = removed · Green = added</span>
                        </div>
                        <DiffView diff={selected.diff || []} />
                      </div>

                      {/* {!canCommit && (
                        <div style={{ padding: '10px 14px', background: 'rgba(255,79,79,0.08)', border: '1px solid rgba(255,79,79,0.2)', borderRadius: 8, marginTop: 16, fontSize: 11, color: '#ff4f4f', fontFamily: 'monospace' }}>
                          🔒 Only admin or lead engineers can commit to GitHub.
                        </div>
                      )} */}
                      {applyError && (
                        <div style={{ padding: '10px 14px', background: 'rgba(255,79,79,0.08)', border: '1px solid rgba(255,79,79,0.2)', borderRadius: 8, marginTop: 16, fontSize: 11, color: '#ff4f4f', fontFamily: 'monospace' }}>
                          ❌ {featureRepo?.source === 'upload' ? 'Update' : 'Commit'} failed: {applyError}
                        </div>
                      )}
                      { (featureRepo?.source === 'upload' ? !codeUpdated : !commitUrl) && (
                        <div style={{ padding: '14px 16px', background: 'rgba(59,127,255,0.06)', border: '1px solid rgba(59,127,255,0.15)', borderRadius: 10, marginTop: 16 }}>
                          <div style={{ fontSize: 11, color: '#7a93bc', marginBottom: 12, lineHeight: 1.5 }}>
                            Review the diff above carefully. Nothing is {featureRepo?.source === 'upload' ? 'changed' : 'committed'} until you click below.
                          </div>
                          <button onClick={applyFix} disabled={applying}
                            style={{ width: '100%', padding: '11px', borderRadius: 9, background: applying ? '#141c27' : '#3b7fff', color: applying ? '#3d5273' : '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: applying ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                            {applying
                              ? (featureRepo?.source === 'upload' ? '⏳ Updating...' : '⏳ Committing...')
                              : (featureRepo?.source === 'upload' ? '🔧 Update Code' : '🚀 Approve & Commit to GitHub')}
                          </button>
                        </div>
                      )}
                      {(featureRepo?.source === 'upload' ? codeUpdated : commitUrl) && (
                        <div style={{ padding: '16px', background: 'rgba(0,201,141,0.08)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 10, marginTop: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#00c98d', marginBottom: 8 }}>{featureRepo?.source === 'upload' ? '✅ Code Updated' : '✅ Committed to GitHub'}</div>
                          {commitUrl && (
                            <a href={commitUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace', display: 'block', marginBottom: 12 }}>
                              🔗 View commit →
                            </a>
                          )}
                          {featureRepo?.source === 'upload' && updatedDownloadUrl && (
                            <a href={updatedDownloadUrl} style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace', display: 'block', marginBottom: 12 }}>
                              ⬇ Download updated project →
                            </a>
                          )}
                          {commitUrl && !docResult && (
                            <button onClick={() => {
                              setDocFixPayload({ commitUrl, filePath: selected?.file_path, fixType: 'full', scoreBefore: scoreBeforeFixRef.current ?? selected?.score, scoreAfter: 100, violationsFixed: selected?.violations?.filter(v => v.severity !== 'pass') });
                              setDocResult(null);
                              setShowDocModal(true);
                            }}
                              style={{ marginTop: 10, width: '100%', padding: '8px', borderRadius: 8, background: 'rgba(59,127,255,0.12)', border: '1px solid rgba(59,127,255,0.25)', color: '#6aa3ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                              📄 Document It
                            </button>
                          )}
                          {docResult?.error && <div style={{ marginTop: 8, fontSize: 11, color: '#ff4f4f' }}>⛔ {docResult.error}</div>}
                          {docResult?.title && !docResult.error && (
                            <div style={{ marginTop: 8, fontSize: 11, color: '#00c98d' }}>
                              ✓ Documented in <strong>{docResult.title}</strong>
                              {docResult.url && <> · <a href={docResult.url} target="_blank" rel="noreferrer" style={{ color: '#3b7fff' }}>View page →</a></>}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )
                )}

                {/* ── TAB: ADD FEATURE ── */}
                {panelTab === 'feature' && (
                  <>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#dce8ff', marginBottom: 4 }}>✨ Add a New Feature</div>
                      <div style={{ fontSize: 12, color: '#3d5273', fontFamily: 'monospace', lineHeight: 1.6, marginBottom: 16 }}>
                        Describe what you want added. The AI implements it following the exact style of your existing code. You review the diff before anything is committed.
                      </div>
                      <div style={{ marginBottom: 6, fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em' }}>REQUIREMENT</div>
                      <textarea
                        value={requirement}
                        onChange={e => { setRequirement(e.target.value); setFeatureResult(null); setFeatureCommitUrl(null); }}
                        placeholder="e.g. Add a Salesforce connector after the FTP write step that creates a Case record with the file name and processing timestamp. Include error handling if Salesforce is unavailable."
                        style={{ width: '100%', fontSize: 11, padding: '8px 11px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.11)', background: '#0e1219', color: '#dce8ff', outline: 'none', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6, minHeight: 120 }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button onClick={generateFeature} disabled={generating || !requirement.trim()}
                          style={{ flex: 1, padding: '10px', borderRadius: 9, background: generating || !requirement.trim() ? '#141c27' : '#8b7cf8', color: generating || !requirement.trim() ? '#3d5273' : '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: generating || !requirement.trim() ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                          {generating ? '⏳ Implementing...' : '✨ Preview Generated Code'}
                        </button>
                        {featureResult?.new_code && (
                          <button onClick={() => { setFeatureResult(null); setRequirement(''); setFeatureCommitUrl(null); }}
                            style={{ padding: '10px 14px', borderRadius: 9, background: 'transparent', color: '#3d5273', border: '1px solid rgba(255,255,255,0.08)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {featureResult && (
                      featureResult.error ? (
                        <div style={{ padding: '12px', background: 'rgba(255,79,79,0.08)', borderRadius: 8, color: '#ff4f4f', fontSize: 12, marginBottom: 16 }}>
                          ❌ {featureResult.error}
                        </div>
                      ) : (
                        <>
                          <div style={{ padding: '12px 16px', background: 'rgba(139,124,248,0.08)', border: '1px solid rgba(139,124,248,0.2)', borderRadius: 10, marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#8b7cf8', marginBottom: 4 }}>✨ Feature Generated — Review Before Committing</div>
                            <div style={{ fontSize: 11, color: '#7a93bc', lineHeight: 1.5 }}>
                              Your requirement has been implemented. Review the diff carefully. Nothing is committed until you click Approve.
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                            <button onClick={() => copyCode(featureResult.new_code)}
                              style={{ flex: 1, padding: '9px', borderRadius: 8, background: copied ? 'rgba(0,201,141,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${copied ? 'rgba(0,201,141,0.3)' : 'rgba(255,255,255,0.1)'}`, color: copied ? '#00c98d' : '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace', transition: 'all 0.2s' }}>
                              {copied ? '✅ Copied!' : '📋 Copy New Code'}
                            </button>
                            <button onClick={() => downloadCode(featureResult.new_code, `${selected.flow_name}-with-feature.xml`)}
                              style={{ flex: 1, padding: '9px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace' }}>
                              ⬇️ Download .xml
                            </button>
                          </div>

                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#dce8ff', marginBottom: 10 }}>
                              Changes Preview
                              <span style={{ fontSize: 10, color: '#3d5273', fontWeight: 400, marginLeft: 8 }}>Green = added · Red = removed</span>
                            </div>
                            <DiffView diff={featureResult.diff || []} />
                          </div>

                          {(featureRepo?.source === 'upload' ? !featureCodeUpdated : !featureCommitUrl) && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              <button onClick={() => setFeatureResult(null)}
                                style={{ padding: '11px', borderRadius: 9, background: 'transparent', color: '#ff4f4f', border: '1px solid rgba(255,79,79,0.3)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                ✕ Reject — Try Again
                              </button>
                              <button onClick={commitFeature} disabled={committingFeature}
                                style={{ padding: '11px', borderRadius: 9, background: committingFeature ? '#141c27' : '#8b7cf8', color: committingFeature ? '#3d5273' : '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: committingFeature ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                                {committingFeature
                                  ? (featureRepo?.source === 'upload' ? '⏳ Updating...' : '⏳ Committing...')
                                  : (featureRepo?.source === 'upload' ? '🔧 Update Code' : '🚀 Approve & Commit to GitHub')}
                              </button>
                            </div>
                          )}
                          {(featureRepo?.source === 'upload' ? !featureCodeUpdated : !featureCommitUrl) && commitFeatureError && (
                            <div style={{ fontSize: 11, color: '#ff4f4f', marginTop: 8 }}>❌ {featureRepo?.source === 'upload' ? 'Update' : 'Commit'} failed: {commitFeatureError}</div>
                          )}

                          {(featureRepo?.source === 'upload' ? featureCodeUpdated : featureCommitUrl) && (
                            <div style={{ padding: '16px', background: 'rgba(0,201,141,0.08)', border: '1px solid rgba(0,201,141,0.2)', borderRadius: 10, marginTop: 10 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#00c98d', marginBottom: 8 }}>{featureRepo?.source === 'upload' ? '✅ Code Updated' : '✅ Feature Committed to GitHub'}</div>
                              {featureRepo?.source === 'upload'
                                ? (featureUpdateUrl && <a href={featureUpdateUrl} style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace', textDecoration: 'none' }}>⬇ Download updated project</a>)
                                : (<a href={featureCommitUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff', fontFamily: 'monospace' }}>
                                🔗 View commit on GitHub →
                              </a>)}
                              <div style={{ marginTop: 10 }}>
                                <button onClick={() => { setFeatureResult(null); setRequirement(''); setFeatureCommitUrl(null); setFeatureCodeUpdated(false); setFeatureUpdateUrl(null); setCommitFeatureError(null); }}
                                  style={{ padding: '8px 14px', borderRadius: 7, background: 'transparent', color: '#3d5273', border: '1px solid rgba(255,255,255,0.08)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  + Add Another Feature
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )
                    )}
                  </>
                )}

                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Results ready banner */}
            {bfResults && !showIde && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ padding: '14px 18px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#dce8ff', marginBottom: 10 }}>
                      Scan complete — {bfResults.scanned} file{bfResults.scanned !== 1 ? 's' : ''} analysed
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                      {[
                        { label: 'Avg Score', val: `${bfResults.avg_score}%`, col: scoreColor(bfResults.avg_score) },
                        { label: 'Errors', val: (bfResults.results || []).reduce((a, r) => a + (r.violations?.filter(v => v.severity === 'error').length || 0), 0), col: '#ff4f4f' },
                        { label: 'Warnings', val: (bfResults.results || []).reduce((a, r) => a + (r.violations?.filter(v => v.severity === 'warning').length || 0), 0), col: '#f5a623' },
                      ].map((k, ki) => (
                        <div key={ki} style={{ padding: '6px 12px', borderRadius: 8, background: '#0e1219', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
                          <div style={{ fontSize: 9, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em' }}>{k.label}</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: k.col, fontFamily: 'monospace' }}>{k.val}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setShowIde(true)}
                      style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#3b7fff', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      Open IDE View →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Org Standard picker card ── */}
            {elicitStep === 'org_standard_selection' && !setupDone && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ minWidth: 280, maxWidth: 420 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 12 }}>
                      🏛️ SELECT ORGANISATION STANDARD
                    </div>
                    <select
                      className="sh-bf-sidebar-select"
                      value={selectedOrgStandard}
                      onChange={e => setSelectedOrgStandard(e.target.value)}
                      style={{ marginBottom: 12 }}
                    >
                      <option value="">— select a standard —</option>
                      {(bfOrgStandards || []).map(s => (
                        <option key={s._id || s.id} value={s._id || s.id}>
                          {s.name} ({s.type})
                        </option>
                      ))}
                    </select>
                    <button
                      disabled={!selectedOrgStandard || bfLoading}
                      onClick={() => {
                        const std = (bfOrgStandards || []).find(s => (s._id || s.id) === selectedOrgStandard);
                        if (!std) return;
                        submittedStepsRef.current.add('org_standard_selection');
                        setElicitStep(null);
                        setBfMessages(prev => [...prev, { role: 'user', content: `Org Standard: ${std.name}` }]);
                        const answer = std.content || '';
                        callAnswer(answer);
                      }}
                      style={{
                        width: '100%', padding: '9px 0', borderRadius: 8,
                        background: selectedOrgStandard ? 'linear-gradient(135deg,#00c98d,#00a87a)' : 'rgba(255,255,255,0.05)',
                        border: 'none', color: selectedOrgStandard ? '#fff' : '#3d5273',
                        fontSize: 12, fontWeight: 700, cursor: selectedOrgStandard ? 'pointer' : 'default',
                      }}
                    >
                      Use this standard →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Confluence picker card ── */}
            {elicitStep === 'confluence_details' && !setupDone && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#dce8ff', marginBottom: 12 }}>📚 Confluence standard</div>

                    {bfConfluenceAccounts.length > 0 ? (
                      /* ── PICKER PATH — connected app ── */
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>ACCOUNT</div>
                          <select className="sh-bf-sidebar-select"
                            value={bfSelectedConfluence?.confluenceEmail || ''}
                            onChange={e => setBfSelectedConfluence(bfConfluenceAccounts.find(a => a.confluenceEmail === e.target.value))}>
                            {bfConfluenceAccounts.map((a, i) => <option key={i} value={a.confluenceEmail}>{a.confluenceEmail}</option>)}
                          </select>
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>SPACE</div>
                          {bfLoadingSpaces ? (
                            <div style={{ fontSize: 11, color: '#7a93bc' }}>Loading spaces…</div>
                          ) : bfConfluenceSpaces.length > 0 ? (
                            <select className="sh-bf-sidebar-select" value={bfSpaceKey} onChange={e => setBfSpaceKey(e.target.value)}>
                              <option value="">— select space —</option>
                              {bfConfluenceSpaces.map(s => <option key={s.key} value={s.key}>{s.name} ({s.key})</option>)}
                            </select>
                          ) : (
                            <input className="sh-bf-sidebar-input" type="text" placeholder="Space key e.g. DEV"
                              value={bfSpaceKey} onChange={e => setBfSpaceKey(e.target.value)} />
                          )}
                        </div>

                        {bfSpaceKey && bfPages.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>
                              PAGES <span style={{ color: '#3d5273', fontWeight: 400 }}>(leave empty to use top 5)</span>
                            </div>
                            {bfLoadingPages ? (
                              <div style={{ fontSize: 11, color: '#7a93bc' }}>Loading pages…</div>
                            ) : (
                              <div className="sh-bf-pages-select-wrap">
                                <select multiple className="sh-bf-pages-select"
                                  value={bfSelectedPages}
                                  onChange={e => setBfSelectedPages(Array.from(e.target.selectedOptions, o => o.value))}>
                                  {bfPages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                                </select>
                                <div className="sh-bf-pages-select-actions">
                                  <button onClick={() => setBfSelectedPages(bfPages.map(p => p.id))}>All</button>
                                  <button onClick={() => setBfSelectedPages([])}>Clear</button>
                                  <span>{bfSelectedPages.length ? `${bfSelectedPages.length} selected` : 'None — top 5 used'}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Additional space selections ── */}
                        {bfAdditionalSelections.map((extra, idx) => (
                          <div key={idx} style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em' }}>ADDITIONAL SPACE {idx + 1}</div>
                              <button onClick={() => removeSpaceSelection(idx)}
                                style={{ background: 'none', border: 'none', color: '#3d5273', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>×</button>
                            </div>
                            <select className="sh-bf-sidebar-select" value={extra.spaceKey}
                              onChange={e => {
                                const key = e.target.value;
                                setBfAdditionalSelections(prev => prev.map((en, i) => i === idx ? { ...en, spaceKey: key } : en));
                                loadExtraPages(idx, key);
                              }}
                              style={{ marginBottom: extra.pages.length ? 8 : 0 }}>
                              <option value="">— select space —</option>
                              {bfConfluenceSpaces.map(s => <option key={s.key} value={s.key}>{s.name} ({s.key})</option>)}
                            </select>
                            {extra.loading && <div style={{ fontSize: 11, color: '#7a93bc' }}>Loading pages…</div>}
                            {!extra.loading && extra.pages.length > 0 && (
                              <div className="sh-bf-pages-select-wrap">
                                <select multiple className="sh-bf-pages-select"
                                  value={extra.selectedPageIds}
                                  onChange={e => setBfAdditionalSelections(prev => prev.map((en, i) => i === idx
                                    ? { ...en, selectedPageIds: Array.from(e.target.selectedOptions, o => o.value) }
                                    : en))}>
                                  {extra.pages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                                </select>
                                <div className="sh-bf-pages-select-actions">
                                  <button onClick={() => setBfAdditionalSelections(prev => prev.map((en, i) => i === idx ? { ...en, selectedPageIds: extra.pages.map(p => p.id) } : en))}>All</button>
                                  <button onClick={() => setBfAdditionalSelections(prev => prev.map((en, i) => i === idx ? { ...en, selectedPageIds: [] } : en))}>Clear</button>
                                  <span>{extra.selectedPageIds.length ? `${extra.selectedPageIds.length} selected` : 'None — space root used'}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                        {/* ── Add more button ── */}
                        {bfConfluenceSpaces.length > 0 && (
                          <button onClick={addSpaceSelection}
                            style={{ width: '100%', padding: '8px', borderRadius: 8, background: 'transparent', border: '1px dashed rgba(59,127,255,0.3)', color: '#3b7fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginBottom: 12 }}>
                            ＋ Add pages from another space
                          </button>
                        )}

                        <button
                          disabled={!bfSpaceKey}
                          onClick={handleConfluenceSubmit}
                          style={{ width: '100%', padding: '10px', borderRadius: 8, background: !bfSpaceKey ? '#141c27' : '#00c98d', color: !bfSpaceKey ? '#3d5273' : '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: !bfSpaceKey ? 'default' : 'pointer' }}>
                          Use this Confluence space →
                        </button>
                      </>
                    ) : (
                      /* ── MANUAL PATH — no connected app ── */
                      <>
                        <div style={{ fontSize: 11, color: '#f5a623', marginBottom: 14, lineHeight: 1.6 }}>
                          No Confluence connection found.{' '}
                          <a href="/settings" style={{ color: '#3b7fff' }}>Configure in Connected Apps →</a>
                          {' '}then{' '}
                          <button onClick={loadBfAccounts}
                            style={{ background: 'none', border: 'none', color: '#3b7fff', fontSize: 11, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                            Refresh
                          </button>
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>PAGE URL(S)</div>
                          <textarea
                            className="sh-bf-sidebar-input"
                            rows={2}
                            placeholder={'https://acme.atlassian.net/wiki/pages/123\nOne URL per line'}
                            value={bfConfUrl}
                            onChange={e => { const v = e.target.value; setBfConfUrl(v); }}
                            style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 11, width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>ATLASSIAN EMAIL</div>
                          <input className="sh-bf-sidebar-input" type="email" placeholder="me@acme.com"
                            value={bfConfEmail} onChange={e => { const v = e.target.value; setBfConfEmail(v); }} />
                        </div>

                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>API TOKEN</div>
                          <input className="sh-bf-sidebar-input" type="password" placeholder="atl_..."
                            value={bfConfToken} onChange={e => { const v = e.target.value; setBfConfToken(v); }} />
                        </div>

                        <button
                          disabled={!bfConfUrl.trim() || !bfConfEmail.trim() || !bfConfToken.trim()}
                          onClick={handleConfluenceManualSubmit}
                          style={{ width: '100%', padding: '10px', borderRadius: 8, background: (!bfConfUrl.trim() || !bfConfEmail.trim() || !bfConfToken.trim()) ? '#141c27' : '#00c98d', color: (!bfConfUrl.trim() || !bfConfEmail.trim() || !bfConfToken.trim()) ? '#3d5273' : '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: (!bfConfUrl.trim() || !bfConfEmail.trim() || !bfConfToken.trim()) ? 'default' : 'pointer' }}>
                          Fetch standard →
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── GitHub repo picker card ── */}
            {elicitStep === 'repo_details' && !setupDone && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#dce8ff', marginBottom: 10 }}>📦 Connect your codebase</div>

                    {/* Source tabs: GitHub URL vs Upload ZIP */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                      {[['github', '🔗 GitHub'], ['upload', '⬆ Upload ZIP']].map(([key, label]) => (
                        <button key={key} onClick={() => { setBfRepoTab(key); setBfZipError(''); }}
                          style={{ flex: 1, padding: '7px 10px', borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                            border: bfRepoTab === key ? '1.5px solid #3b7fff' : '1.5px solid #24344a',
                            background: bfRepoTab === key ? 'rgba(59,127,255,0.14)' : 'transparent',
                            color: bfRepoTab === key ? '#8fb6ff' : '#7a93bc' }}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {bfRepoTab === 'upload' ? (
                      /* ── UPLOAD ZIP PATH ── */
                      <>
                        <label style={{ display: 'block', border: '1.5px dashed #24344a', borderRadius: 8, padding: '18px 12px', textAlign: 'center', cursor: bfZipUploading ? 'default' : 'pointer', background: '#0c141e' }}>
                          <input type="file" accept=".zip" style={{ display: 'none' }} disabled={bfZipUploading}
                            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleRepoZipUpload(f); }} />
                          <div style={{ fontSize: 12, color: '#8fb6ff', fontWeight: 700 }}>
                            {bfZipUploading ? 'Uploading & extracting…' : (bfZipName ? `📦 ${bfZipName}` : '⬆ Choose a .zip of your project')}
                          </div>
                          <div style={{ fontSize: 10.5, color: '#7a93bc', marginTop: 5 }}>We extract it on the server and run the same {bfMode === 'addfeature' ? 'feature generation' : 'compliance scan'}.</div>
                        </label>
                        {bfZipError && <div style={{ fontSize: 11, color: '#ff6b6b', marginTop: 8 }}>{bfZipError}</div>}
                      </>
                    ) : bfRepoAccounts.length > 0 ? (
                      /* ── PICKER PATH — connected app ── */
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>GITHUB ACCOUNT</div>
                          <select className="sh-bf-sidebar-select"
                            value={bfSelectedRepo?._id || ''}
                            onChange={e => setBfSelectedRepo(bfRepoAccounts.find(a => a._id === e.target.value))}>
                            {bfRepoAccounts.map(a => <option key={a._id} value={a._id}>{a.gitUsername}</option>)}
                          </select>
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>REPOSITORY</div>
                          {bfLoadingRepos ? (
                            <div style={{ fontSize: 11, color: '#7a93bc' }}>Loading repositories…</div>
                          ) : bfRepoList.length > 0 ? (
                            <select className="sh-bf-sidebar-select" value={bfRepoUrl} onChange={e => setBfRepoUrl(e.target.value)}>
                              <option value="">— select repository —</option>
                              {bfRepoList.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
                            </select>
                          ) : (
                            <input className="sh-bf-sidebar-input" type="text" placeholder="repository-name"
                              value={bfRepoUrl} onChange={e => setBfRepoUrl(e.target.value)} />
                          )}
                        </div>

                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>BRANCH</div>
                          {bfLoadingBranches ? (
                            <div style={{ fontSize: 11, color: '#7a93bc' }}>Loading branches…</div>
                          ) : bfBranchList.length > 0 ? (
                            <select className="sh-bf-sidebar-select" value={bfBranch} onChange={e => setBfBranch(e.target.value)}>
                              {bfBranchList.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                          ) : (
                            <input className="sh-bf-sidebar-input" type="text" placeholder="main"
                              value={bfBranch} onChange={e => setBfBranch(e.target.value)} />
                          )}
                        </div>

                        <button
                          disabled={!bfRepoUrl.trim()}
                          onClick={handleRepoSubmit}
                          style={{ width: '100%', padding: '10px', borderRadius: 8, background: !bfRepoUrl.trim() ? '#141c27' : '#3b7fff', color: !bfRepoUrl.trim() ? '#3d5273' : '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: !bfRepoUrl.trim() ? 'default' : 'pointer' }}>
                          Use this repository →
                        </button>
                      </>
                    ) : (
                      /* ── MANUAL PATH — no connected app ── */
                      <>
                        <div style={{ fontSize: 11, color: '#f5a623', marginBottom: 14, lineHeight: 1.6 }}>
                          No GitHub connection found.{' '}
                          <a href="/settings" style={{ color: '#3b7fff' }}>Configure in Connected Apps →</a>
                          {' '}then{' '}
                          <button onClick={loadBfAccounts}
                            style={{ background: 'none', border: 'none', color: '#3b7fff', fontSize: 11, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                            Refresh
                          </button>
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>REPOSITORY (owner/repo)</div>
                          <input className="sh-bf-sidebar-input" type="text" placeholder="acme/my-api"
                            value={bfRepoUrl} onChange={e => { const v = e.target.value; setBfRepoUrl(v); }} />
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>PERSONAL ACCESS TOKEN</div>
                          <input className="sh-bf-sidebar-input" type="password" placeholder="ghp_..."
                            value={bfRepoToken} onChange={e => { const v = e.target.value; setBfRepoToken(v); }} />
                        </div>

                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 4 }}>BRANCH (optional)</div>
                          <input className="sh-bf-sidebar-input" type="text" placeholder="main"
                            value={bfBranch} onChange={e => { const v = e.target.value; setBfBranch(v); }} />
                        </div>

                        <button
                          disabled={!bfRepoUrl.trim() || !bfRepoToken.trim()}
                          onClick={handleRepoManualSubmit}
                          style={{ width: '100%', padding: '10px', borderRadius: 8, background: (!bfRepoUrl.trim() || !bfRepoToken.trim()) ? '#141c27' : '#3b7fff', color: (!bfRepoUrl.trim() || !bfRepoToken.trim()) ? '#3d5273' : '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: (!bfRepoUrl.trim() || !bfRepoToken.trim()) ? 'default' : 'pointer' }}>
                          {bfMode === 'addfeature' ? 'Use this repository →' : 'Scan this repository →'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Add-feature: requirement prompt card ── */}
            {elicitStep === 'feature_prompt' && bfMode === 'addfeature' && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#dce8ff', marginBottom: 4 }}>🛠 Describe the feature</div>
                    <div style={{ fontSize: 11, color: '#7a93bc', marginBottom: 10 }}>
                      {featureRepo?.source === 'upload'
                        ? <>Uploaded project: <code style={{ color: '#dce8ff' }}>{featureRepo?.repo || 'archive.zip'}</code></>
                        : <>Repo: <code style={{ color: '#dce8ff' }}>{featureRepo?.github_repo}</code> · branch <code style={{ color: '#dce8ff' }}>{featureRepo?.github_branch}</code></>}
                    </div>
                    <textarea
                      className="sh-bf-sidebar-input"
                      rows={4}
                      placeholder="e.g. Add all relevant MUnit test cases for the existing flows"
                      value={featureRequirement}
                      onChange={e => setFeatureRequirement(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', marginBottom: 12, fontFamily: 'inherit' }}
                    />
                    <button
                      disabled={!featureRequirement.trim() || featureGenerating}
                      onClick={generateFeatureRepoWide}
                      style={{ width: '100%', padding: '10px', borderRadius: 8, background: (!featureRequirement.trim() || featureGenerating) ? '#141c27' : '#00c98d', color: (!featureRequirement.trim() || featureGenerating) ? '#3d5273' : '#000', border: 'none', fontSize: 12, fontWeight: 700, cursor: (!featureRequirement.trim() || featureGenerating) ? 'default' : 'pointer' }}>
                      {featureGenerating ? 'Generating…' : 'Generate feature →'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Add-feature: generating spinner ── */}
            {featureGenerating && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ fontSize: 12, color: '#7a93bc' }}>
                    ⏳ Generating the feature across the repository and committing changes… this may take a minute.
                  </div>
                </div>
              </div>
            )}

            {/* ── Add-feature: results ── */}
            {featureFiles && !featureGenerating && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  {featureFiles.error ? (
                    <div className="sh-msg-bubble" style={{ fontSize: 12, color: '#ff4f4f' }}>
                      ⛔ {featureFiles.error}
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={() => { setFeatureFiles(null); setElicitStep('feature_prompt'); }}
                          style={{ padding: '6px 14px', borderRadius: 8, background: '#3b7fff', color: '#fff', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          Try again
                        </button>
                      </div>
                    </div>
                  ) : featureFiles.length === 0 ? (
                    <div className="sh-msg-bubble" style={{ fontSize: 12, color: '#7a93bc' }}>
                      No changes were generated for this request. Try rephrasing the feature.
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={() => { setFeatureFiles(null); setFeatureRequirement(''); setElicitStep('feature_prompt'); }}
                          style={{ padding: '6px 14px', borderRadius: 8, background: '#3b7fff', color: '#fff', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          Edit request
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="sh-msg-bubble" style={{ width: '100%', boxSizing: 'border-box' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#00c98d', marginBottom: 12 }}>
                        {featureRepo?.source === 'upload'
                          ? (featureCommitted
                              ? <>✓ Applied {featureFiles.length} file{featureFiles.length !== 1 ? 's' : ''} — download the updated project below</>
                              : <>✓ Generated {featureFiles.length} file{featureFiles.length !== 1 ? 's' : ''} — review below, then <strong>Apply</strong> to get an updated ZIP</>)
                          : (featureCommitted
                              ? <>✓ Committed {featureFiles.length} file{featureFiles.length !== 1 ? 's' : ''} to <code style={{ color: '#dce8ff' }}>{featureRepo?.github_branch}</code></>
                              : <>✓ Generated {featureFiles.length} file{featureFiles.length !== 1 ? 's' : ''} — review below, then <strong>Commit</strong> to <code style={{ color: '#dce8ff' }}>{featureRepo?.github_branch}</code></>)}
                      </div>
                      {featureFiles.map((f, fi) => (
                        <div key={fi} style={{ marginBottom: 14, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.03)' }}>
                            <code style={{ fontSize: 11, color: '#dce8ff', wordBreak: 'break-all' }}>{f.file_path}</code>
                            {featureCommitted && f.commit_url && (
                              <a href={f.commit_url} target="_blank" rel="noreferrer"
                                style={{ fontSize: 11, color: '#3b7fff', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                                View commit →
                              </a>
                            )}
                          </div>
                          {f.reasoning && (
                            <div style={{ fontSize: 11, color: '#7a93bc', padding: '8px 12px', lineHeight: 1.6 }}>{f.reasoning}</div>
                          )}
                          {Array.isArray(f.diff) && f.diff.length > 0 && (
                            <pre className="sh-bf-detail-scroll" style={{ margin: 0, padding: '10px 12px', background: '#06080f', overflowX: 'auto', fontSize: 11, lineHeight: 1.5, fontFamily: 'monospace' }}>
                              {f.diff.map((line, li) => {
                                const c = line.startsWith('+') ? '#00c98d' : line.startsWith('-') ? '#ff6b6b' : '#7a93bc';
                                return <div key={li} style={{ color: c, whiteSpace: 'pre-wrap' }}>{line || ' '}</div>;
                              })}
                            </pre>
                          )}
                        </div>
                      ))}
                      {featureCommitError && (
                        <div style={{ fontSize: 11, color: '#ff6b6b', marginBottom: 8 }}>⛔ {featureCommitError}</div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => { setFeaturePreviewIdx(0); setFeaturePreviewTab('code'); setFeaturePreviewOpen(true); }}
                          style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(59,127,255,0.15)', border: '1px solid rgba(59,127,255,0.4)', color: '#6aa3ff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          ⤢ Preview
                        </button>
                        {!featureCommitted && (
                          <button
                            onClick={commitFeatureRepoWide}
                            disabled={featureCommitting}
                            style={{ padding: '8px 16px', borderRadius: 8, background: featureCommitting ? '#141c27' : '#00c98d', color: featureCommitting ? '#3d5273' : '#000', border: 'none', fontSize: 11, fontWeight: 700, cursor: featureCommitting ? 'default' : 'pointer' }}>
                            {featureCommitting
                              ? (featureRepo?.source === 'upload' ? 'Preparing download…' : 'Committing…')
                              : (featureRepo?.source === 'upload' ? '⬇ Download updated ZIP' : `✅ Commit to ${featureRepo?.github_branch || 'branch'}`)}
                          </button>
                        )}
                        {featureCommitted && featureRepo?.source === 'upload' && featureDownloadUrl && (
                          <a href={featureDownloadUrl} style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid rgba(0,201,141,0.5)', color: '#00c98d', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>⬇ Download again</a>
                        )}
                        {featureCommitted && featureRepo?.source !== 'upload' && (
                          <span style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, color: '#00c98d' }}>✓ Committed</span>
                        )}
                        <button
                          onClick={() => { setFeatureFiles(null); setFeatureRequirement(''); setFeatureCommitted(false); setFeatureCommitError(''); setFeatureDownloadUrl(''); setElicitStep('feature_prompt'); }}
                          style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#dce8ff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          ＋ Add another feature
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Document upload card ── */}
            {elicitStep === 'upload_instruction' && !setupDone && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#dce8ff' }}>📄 Upload your coding standard</div>
                      <button
                        onClick={() => { setElicitStep(null); setUploadState({ loading: false, error: null, validating: false, validationFailed: false, reason: null }); }}
                        style={{ background: 'none', border: 'none', color: '#7a93bc', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                        title="Cancel upload">✕</button>
                    </div>
                    {bfResults?.detected_language && (
                      <div style={{ fontSize: 10, color: '#7a93bc', marginBottom: 10, padding: '4px 8px', background: 'rgba(59,127,255,0.07)', borderRadius: 5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ color: '#3b7fff' }}>●</span>
                        Will validate against <strong style={{ color: '#adc4e6' }}>{bfResults.detected_language}</strong> repo
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#7a93bc', marginBottom: 14 }}>Supported formats: PDF, DOC, DOCX, TXT, MD</div>

                    {uploadState.validationFailed && (
                      <div style={{ marginBottom: 14, padding: '12px 14px', background: 'rgba(255,79,79,0.07)', border: '1px solid rgba(255,79,79,0.25)', borderRadius: 9 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#ff4f4f', marginBottom: 6 }}>⛔ Document doesn't match this repository</div>
                        <div style={{ fontSize: 11, color: '#c97a7a', lineHeight: 1.6 }}>{uploadState.reason}</div>
                        {uploadState.documentType && (
                          <div style={{ fontSize: 10, color: '#7a93bc', fontFamily: 'monospace', marginTop: 6 }}>
                            Detected as: <span style={{ color: '#adc4e6' }}>{uploadState.documentType}</span>
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: '#7a93bc', marginTop: 10 }}>
                          Please upload a <strong style={{ color: '#dce8ff' }}>{bfResults?.detected_language || 'matching'}</strong> best-practices or coding-standards document.
                        </div>
                      </div>
                    )}

                    <label style={{ display: 'block', border: `2px dashed ${uploadState.validationFailed ? 'rgba(255,79,79,0.35)' : 'rgba(59,127,255,0.3)'}`, borderRadius: 10, padding: '20px', textAlign: 'center', cursor: 'pointer', background: uploadState.validationFailed ? 'rgba(255,79,79,0.03)' : 'rgba(59,127,255,0.04)', transition: 'all 0.2s' }}>
                      <input type="file" accept=".pdf,.doc,.docx,.txt,.md" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ''; }} />
                      {uploadState.loading ? (
                        <div style={{ fontSize: 12, color: '#7a93bc' }}>
                          <span className="sh-thinking-dots"><span /><span /><span /></span>
                          <span style={{ marginLeft: 8 }}>Parsing document…</span>
                        </div>
                      ) : uploadState.validating ? (
                        <div style={{ fontSize: 12, color: '#7a93bc' }}>
                          <span className="sh-thinking-dots"><span /><span /><span /></span>
                          <span style={{ marginLeft: 8 }}>Validating against {bfResults?.detected_language} standard…</span>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 22, marginBottom: 6 }}>{uploadState.validationFailed ? '🔄' : '📂'}</div>
                          <div style={{ fontSize: 12, color: '#dce8ff', fontWeight: 600 }}>{uploadState.validationFailed ? 'Upload a different file' : 'Click to choose file'}</div>
                          <div style={{ fontSize: 11, color: '#3d5273', marginTop: 4 }}>or drag and drop</div>
                        </div>
                      )}
                    </label>

                    {uploadState.error && (
                      <div style={{ marginTop: 10, fontSize: 11, color: '#ff4f4f' }}>Error: {uploadState.error}</div>
                    )}
                  </div>
                </div>
              </div>
            )}


            {/* Thinking bubble — shown while waiting for agent response */}
            {bfLoading && !bfResults && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 16px' }}>
                    <span className="sh-thinking-dots"><span /><span /><span /></span>
                    <span style={{ fontSize: 12, color: '#7a93bc' }}>{thinkingMsg}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Doc review running / done / error */}
            {docReviewState === 'running' && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="sh-thinking-dots"><span /><span /><span /></span>
                    <span style={{ fontSize: 12, color: '#7a93bc' }}>Reviewing your Confluence documentation against the codebase…</span>
                  </div>
                </div>
              </div>
            )}
            {docReviewState?.done && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ padding: '12px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#00c98d', marginBottom: 6 }}>📋 Documentation Review Complete</div>
                    <div style={{ fontSize: 11, color: '#7a93bc' }}>Gaps and updates have been appended to <strong style={{ color: '#dce8ff' }}>{docReviewState.title}</strong>. Original content is preserved.</div>
                  </div>
                </div>
              </div>
            )}
            {docReviewState?.error && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ fontSize: 11, color: '#ff4f4f' }}>⚠️ Documentation review could not complete: {docReviewState.error}</div>
                </div>
              </div>
            )}
            {/* Auto-create docs prompt card */}
            {autoDocPrompt && !autoDocResult && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ minWidth: 300 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#dce8ff', marginBottom: 8 }}>📁 No documentation found</div>
                    <div style={{ fontSize: 11, color: '#7a93bc', marginBottom: 14, lineHeight: 1.6 }}>
                      Would you like me to automatically create <strong style={{ color: '#dce8ff' }}>Project Documentation</strong>,{' '}
                      <strong style={{ color: '#dce8ff' }}>Best Practices</strong>, and a{' '}
                      <strong style={{ color: '#dce8ff' }}>Compliance Report</strong> in Confluence based on the scan results?
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 4 }}>CONFLUENCE SPACE URL</div>
                      <input value={autoDocSpaceUrl} onChange={e => setAutoDocSpaceUrl(e.target.value)}
                        placeholder="https://yourorg.atlassian.net"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: '#3d5273', fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 4 }}>SPACE KEY</div>
                      <input value={autoDocSpaceKey} onChange={e => setAutoDocSpaceKey(e.target.value)}
                        placeholder="e.g. DEV"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#dce8ff', fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <button onClick={() => setAutoDocPrompt(null)}
                        style={{ padding: '9px', borderRadius: 8, background: 'transparent', color: '#7a93bc', border: '1px solid rgba(255,255,255,0.08)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        No thanks
                      </button>
                      <button onClick={handleAutoCreateDocs} disabled={autoDocCreating || !autoDocSpaceUrl.trim() || !autoDocSpaceKey.trim()}
                        style={{ padding: '9px', borderRadius: 8, background: autoDocCreating ? '#141c27' : '#3b7fff', color: autoDocCreating ? '#3d5273' : '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: autoDocCreating ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                        {autoDocCreating ? '⏳ Creating…' : '✨ Create Docs'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Auto-create docs result */}
            {autoDocResult?.pages && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ padding: '14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#00c98d', marginBottom: 10 }}>✅ Documentation Created in Confluence</div>
                    {Object.entries({ 'Project Documentation': autoDocResult.pages.projectDocs, 'Best Practices': autoDocResult.pages.bestPractices, 'Compliance Report': autoDocResult.pages.complianceReport }).map(([label, page]) => page && (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <span style={{ fontSize: 11, color: '#dce8ff' }}>{label}</span>
                        {page.url ? <a href={page.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b7fff' }}>View →</a> : <span style={{ fontSize: 11, color: '#3d5273' }}>Created</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {autoDocResult?.error && (
              <div className="sh-msg bot">
                <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                <div className="sh-msg-body">
                  <div className="sh-msg-bubble" style={{ fontSize: 11, color: '#ff4f4f' }}>⛔ Could not create docs: {autoDocResult.error}</div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
          {/* end messages scroll area */}

          {/* ── Bottom input bar — always in DOM, hidden only for card-driven steps ── */}
          <div className="sh-input-area" style={{ display: (bfMode === 'addfeature' || ['standard_selection', 'confluence_details', 'upload_instruction', 'repo_details', 'org_standard_selection', 'feature_prompt'].includes(elicitStep)) ? 'none' : undefined }}>
              <div className="sh-input-row">
                <div className="sh-input-wrap" ref={bfPromptRef}>
                  {bfLoading ? (
                    <div className="sh-thinking-bar">
                      <span className="sh-thinking-dots"><span /><span /><span /></span>
                    </div>
                  ) : (
                    <>
                      <input
                        className="sh-input"
                        placeholder={!promptGiven && !setupDone ? "Describe what you'd like to analyze…" : bfResults ? "Ask a follow-up or type to re-analyze…" : "Ask intelog AI anything about your codebase…"}
                        value={bfPrompt}
                        onChange={e => setBfPrompt(e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                      <button
                        className="sh-send-btn"
                        disabled={!bfPrompt.trim()}
                        onClick={handleSend}
                      >↑</button>
                    </>
                  )}
                </div>
              </div>
          </div>
        </div>
        {/* end left chat col */}

        {/* ── Right: live logs + token usage ── */}
        <div className="sh-dashboard-col" style={{ width: 320, flexShrink: 0 }}>
          <SharedLogsPanel
            logs={bfLogs}
            usageData={usageData}
            liveTokens={liveTokens}
            extensionStatus={extensionStatus}
            onExtend={handleExtendUsage}
          />
        </div>

      </div>
      )}
      {/* end chat view */}

      {/* ── STANDARD REVIEW FULL VIEW ── */}
      {stdModalIdx !== null && (() => {
        const m = bfMessages[stdModalIdx];
        if (!m) return null;
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: '#06080f' }}>

            {/* Top bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 20px', height: 52, background: '#090c14', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
              {/* Back button */}
              <button onClick={() => setStdModalIdx(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, background: 'rgba(0,188,212,0.07)', border: '1px solid rgba(0,188,212,0.2)', color: '#00bcd4', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,188,212,0.14)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,188,212,0.07)'}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                Back to chat
              </button>

              {/* Divider */}
              <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

              {/* Badge + title */}
              <div style={{ padding: '3px 10px', borderRadius: 6, background: 'rgba(59,127,255,0.1)', border: '1px solid rgba(59,127,255,0.22)', fontSize: 9, fontWeight: 800, color: '#3b7fff', fontFamily: 'monospace', letterSpacing: '0.1em', flexShrink: 0 }}>
                STANDARD REVIEW
              </div>
              <div style={{ fontSize: 13, color: '#dce8ff', fontWeight: 600, letterSpacing: '-0.01em' }}>
                Summary
                {m.title && <span style={{ fontSize: 11, color: '#7a93bc', fontWeight: 400, marginLeft: 8 }}>{m.title}</span>}
              </div>

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* Source link */}
              {m.link
                ? <a href={m.link} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#3b7fff', textDecoration: 'none', padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(59,127,255,0.2)', background: 'rgba(59,127,255,0.06)' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    Source
                  </a>
                : null
              }

              {/* Accepted badge */}
              {m.accepted && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 20, background: 'rgba(0,201,141,0.1)', border: '1px solid rgba(0,201,141,0.25)', fontSize: 11, fontWeight: 700, color: '#00c98d' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  Accepted
                </div>
              )}
            </div>

            {/* Scrollable content */}
            <div className="sh-bf-std-review-scroll" style={{ flex: 1, overflowY: 'auto', padding: '36px 56px 48px', maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
              {renderPreview(m.preview)}
            </div>

            {/* Footer */}
            {!m.accepted && (
              <div style={{ padding: '18px 56px', borderTop: '1px solid rgba(255,255,255,0.06)', background: '#090c14', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 20 }}>
                <button
                  onClick={() => {
                    setBfMessages(prev => prev.map((msg, mi) => mi === stdModalIdx ? { ...msg, accepted: true } : msg));
                    setStdModalIdx(null);
                    callAnswer('accept');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 28px', borderRadius: 9, background: 'linear-gradient(135deg, #00c98d 0%, #00a87a 100%)', color: '#000', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: '-0.01em', boxShadow: '0 4px 16px rgba(0,201,141,0.25)', transition: 'opacity 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  Accept this standard
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#4a637a' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  <span>Not quite right? Go back and type requested changes in the chat.</span>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Document It Modal ── */}
      {showDocModal && (
        <DocModal
          onClose={() => setShowDocModal(false)}
          fixPayload={docFixPayload}
          bfSelectedConfluence={bfSelectedConfluence}
          bfConfluenceAccounts={bfConfluenceAccounts}
          bfRepoUrl={bfRepoUrl}
          selected={selected}
          onDone={(result) => { setDocResult(result); }}
          backendUrl={backendUrl}
          sessionId={sessionId}
          bfSelectedRepo={bfSelectedRepo}
          bfBranch={bfBranch}
          bfResults={bfResults}
        />
      )}

    </div>
  );
}
